"use strict";

const ORDER_LOOKUP_ENDPOINT = "/apps/qfconnector/order-lookup";
const FORM_SUBMIT_ENDPOINT = "/apps/qfconnector/saveForm";
const DHL_TRACKING_ENDPOINT = "https://api-eu.dhl.com/track/shipments";
const DHL_API_KEY = "PvqFqP3uwuLfJYmGDTNA2WEBmLv7WHpr";

// Upper bound for a wet food line item whose pack size we cannot read from the order
// lookup data. The largest tray quantity we sell is 48 (see the feeding advice modal),
// so this never blocks a genuine report but still stops nonsense numbers.
const UNKNOWN_WET_PACK_SIZE = 48;

const FINAL_STEP_CONFIG = {
  "order-amend": { step: 3, backStep: 2, hideFileUpload: true },
  "order-timing": { step: 3, backStep: 2, hideFileUpload: true },
  "subscription-support": { step: 3, backStep: 2 },
  "dog-unwell": {
    step: 4,
    backStep: 3,
    hideFinalTitle: true,
    hideFinalSubtitle: true,
    hideFileUpload: true,
    finalStepInfoLabelKey: "contact_form.dog_unwell.final_info_label",
    finalStepInfoPlaceholderKey: "contact_form.dog_unwell.final_info_placeholder",
  },
  "discount-code-error": {
    step: 3,
    backStep: 2,
    fileUploadLabelKey: "contact_form.steps.step_99.groups.image.discount_code_label_html",
  },
  "food-issue": {
    step: 3,
    backStep: 2,
    fileUploadLabelKey: "contact_form.steps.step_99.groups.image.food_issue_label_html",
    finalStepInfoLabelKey: "contact_form.food_issue.final_info_label",
  },
  "wrong-missing-item": { step: 4, backStep: 3, hideFileUpload: true },
  "need-advice": {
    step: 2,
    backStep: 1,
    hideFileUpload: true,
    finalStepSubtitleKey: "contact_form.steps.step_99.subtitle_alt_html",
  },
  "pr-marketing": {
    step: 2,
    backStep: 1,
    hideFileUpload: true,
    finalStepSubtitleKey: "contact_form.steps.step_99.pr_marketing_notice_html",
  },
  other: { step: 2, backStep: 1, route: "final-other" },
};

const CONTACT_FORM_SCHEMA = {
  stepOne: {
    titleKey: "contact_form.steps.step_1.title",
    options: [
      {
        id: "order-timing",
        value: "order-timing",
        labelKey: "contact_form.steps.step_1.order_timing.label",
        infoKey: "contact_form.steps.step_1.order_timing.info",
      },
      {
        id: "order-amend",
        value: "order-amend",
        labelKey: "contact_form.steps.step_1.order_amend.label",
        infoKey: "contact_form.steps.step_1.order_amend.info",
      },
      {
        id: "subscription-support",
        value: "subscription-support",
        labelKey: "contact_form.steps.step_1.subscription_support.label",
        infoKey: "contact_form.steps.step_1.subscription_support.info",
      },
      {
        id: "need-advice",
        value: "need-advice",
        labelKey: "contact_form.steps.step_1.need_advice.label",
        infoKey: "contact_form.steps.step_1.need_advice.info",
        goToStep: FINAL_STEP_CONFIG["need-advice"].step,
      },
      {
        id: "item-damaged",
        value: "food-issue",
        labelKey: "contact_form.steps.step_1.item_damaged.label",
        infoKey: "contact_form.steps.step_1.item_damaged.info",
      },
      {
        id: "food-looks-different",
        value: "food-issue",
        labelKey: "contact_form.steps.step_1.food_looks_different.label",
        infoKey: "contact_form.steps.step_1.food_looks_different.info",
        stepTitleKey: "contact_form.steps.step_2.food_issue.food_looks_different.title",
        stepSubtitleKey: "contact_form.steps.step_2.food_issue.food_looks_different.subtitle_html",
      },
      {
        id: "foreign-item",
        value: "food-issue",
        labelKey: "contact_form.steps.step_1.foreign_item.label",
        infoKey: "contact_form.steps.step_1.foreign_item.info",
        stepTitleKey: "contact_form.steps.step_2.food_issue.foreign_item.title",
        stepSubtitleKey: "contact_form.steps.step_2.food_issue.foreign_item.subtitle_html",
      },
      {
        id: "dog-unwell",
        value: "dog-unwell",
        labelKey: "contact_form.steps.step_1.dog_unwell.label",
        infoKey: "contact_form.steps.step_1.dog_unwell.info",
      },
      {
        id: "wrong-missing-item",
        value: "wrong-missing-item",
        labelKey: "contact_form.steps.step_1.wrong_item.label",
        infoKey: "contact_form.steps.step_1.wrong_item.info",
      },
      {
        id: "discount-code-error",
        value: "discount-code-error",
        labelKey: "contact_form.steps.step_1.discount_code_error.label",
        infoKey: "contact_form.steps.step_1.discount_code_error.info",
      },
      {
        id: "pr-marketing",
        value: "pr-marketing",
        labelKey: "contact_form.steps.step_1.pr_marketing.label",
        infoKey: "contact_form.steps.step_1.pr_marketing.info",
        goToStep: FINAL_STEP_CONFIG["pr-marketing"].step,
      },
      // {
      //   id: 'other',
      //   value: 'other',
      //   labelKey: 'contact_form.steps.step_1.other.label',
      //   infoKey: 'contact_form.steps.step_1.other.info',
      //   goToStep: FINAL_STEP_CONFIG.other.step,
      // },
    ],
  },
  steps: [
    {
      step: 2,
      route: "order-amend",
      titleKey: "contact_form.steps.step_2.order_amend.title",
      subtitleKey: "contact_form.steps.step_2.order_amend.subtitle_html",
      omitFinalFieldIds: ["customer-name", "customer-email"],
      fields: [
        {
          type: "orderLookup",
          inputType: "text",
          id: "order-amend-order-lookup",
          name: "order-number",
          labelKey: "contact_form.order_lookup.label",
          // infoKey: 'contact_form.order_lookup.info',
          placeholderKey: "contact_form.order_lookup.placeholder",
          minLength: 6,
          maxLength: 10,
          queryParam: "orderNumber",
          resultsName: "order-products",
          productsLabelKey: "contact_form.order_lookup.products_label",
        },
        {
          type: "select",
          id: "amended-order-subject",
          name: "amended-order-subject",
          labelKey: "contact_form.order_amend.route.order_amend.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          options: [
            {
              value: "change-address",
              labelKey: "contact_form.order_amend.route.order_amend.options.change_address",
            },
            {
              value: "add-remove-products",
              labelKey: "contact_form.order_amend.route.order_amend.options.add_remove_products",
            },
            {
              value: "return-products",
              labelKey: "contact_form.order_amend.route.order_amend.options.returns",
            },
            {
              value: "cancel-order",
              labelKey: "contact_form.order_amend.route.order_amend.options.cancel_order",
            },
          ],
        },
      ],
      innerSteps: [
        {
          id: "change-address",
          fields: [
            {
              type: "textarea",
              id: "change-address",
              name: "change-address",
              labelKey: "contact_form.order_amend.route.address_change.label",
              placeholderKey: "contact_form.order_amend.route.address_change.placeholder",
              rows: 5,
            },
          ],
        },
      ],
      disclaimerKey: "contact_form.order_amend.route.order_amend.order_number_disclaimer",
      buttons: {
        next: { step: FINAL_STEP_CONFIG["order-amend"].step, label: "Next Step" },
      },
    },
    {
      step: 2,
      route: "order-timing",
      titleKey: "contact_form.steps.step_2.order_timing.title",
      omitFinalFieldIds: ["customer-name", "customer-email"],
      fields: [
        {
          type: "orderLookup",
          inputType: "text",
          id: "order-timing-order-lookup",
          name: "order-number",
          labelKey: "contact_form.order_lookup.label",
          // infoKey: 'contact_form.order_lookup.info',
          placeholderKey: "contact_form.order_lookup.placeholder",
          minLength: 6,
          maxLength: 10,
          queryParam: "orderNumber",
          resultsName: "order-products",
          productsLabelText: "Your order contains the following products",
          disableProductSelection: true,
        },
      ],
      buttons: {
        next: { step: FINAL_STEP_CONFIG["order-timing"].step, label: "Next Step" },
      },
    },
    {
      step: 2,
      route: "subscription-support",
      titleKey: "contact_form.steps.step_2.subscription_support.title",
      subtitleKey: "contact_form.steps.step_2.subscription_support.subtitle_html",
      fields: [
        {
          type: "select",
          id: "amended-subscription-subject",
          name: "amended-subscription-subject",
          labelKey: "contact_form.subscription_support.route.subscription-support.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          options: [
            {
              value: "reschedule-delivery",
              labelKey:
                "contact_form.subscription_support.route.subscription-support.options.reschedule_delivery",
            },
            {
              value: "add-remove-products",
              labelKey:
                "contact_form.subscription_support.route.subscription-support.options.add_remove_products",
            },
            {
              value: "cancel",
              labelKey:
                "contact_form.subscription_support.route.subscription-support.options.cancel",
            },
            {
              value: "other",
              labelKey:
                "contact_form.subscription_support.route.subscription-support.options.other",
            },
          ],
        },
      ],
      innerSteps: [
        {
          id: "cancellation",
          fields: [
            {
              type: "select",
              id: "cancellation-reason",
              name: "cancellation-reason",
              labelKey:
                "contact_form.subscription_support.route.subscription-support.cancellation.label",
              required: true,
              placeholderKey: "contact_form.select_placeholder",
              options: [
                {
                  value: "too-much-food",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.too_much_food",
                },
                {
                  value: "dog-not-enjoying-food",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.dog_not_enjoying",
                },
                {
                  value: "subscribed-accident",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.subscribed_accident",
                },
                {
                  value: "no-longer-feed-forthglade",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.no_longer_feed_forthglade",
                },
                {
                  value: "different-pack-size",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.different_pack_size",
                },
                {
                  value: "no-longer-have-dog",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.no_longer_have_dog",
                },
                {
                  value: "dog-needs-specialist-diet",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.dog_needs_specialist_diet",
                },
                {
                  value: "dog-lifestage-change",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.dog_lifestage",
                },
                {
                  value: "other",
                  labelKey:
                    "contact_form.subscription_support.route.subscription-support.cancellation.options.other",
                },
              ],
            },
          ],
        },
      ],
      buttons: {
        next: { step: FINAL_STEP_CONFIG["subscription-support"].step, label: "Next Step" },
      },
    },
    {
      step: 2,
      route: "dog-unwell",
      titleKey: "contact_form.steps.step_2.dog_unwell.title",
      subtitleKey: "contact_form.steps.step_2.dog_unwell.subtitle_html",
      omitFinalFieldIds: ["customer-name", "customer-email"],
      fields: [
        {
          type: "select",
          id: "dog-unwell-retailer",
          name: "dog-unwell-retailer",
          labelKey: "contact_form.food_issue.retailer.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          isRetailerSelect: true,
          retailerSubtitleKey: "contact_form.food_issue.retailer.tool_tip",
          options: [
            {
              valueKey: "contact_form.food_issue.retailer.options.forthglade",
              labelKey: "contact_form.food_issue.retailer.options.forthglade",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.amazon",
              labelKey: "contact_form.food_issue.retailer.options.amazon",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.pets_at_home",
              labelKey: "contact_form.food_issue.retailer.options.pets_at_home",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.supermarket",
              labelKey: "contact_form.food_issue.retailer.options.supermarket",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.other",
              labelKey: "contact_form.food_issue.retailer.options.other",
            },
          ],
        },
        {
          type: "textarea",
          id: "dog-symptoms",
          name: "dog-symptoms",
          labelKey: "contact_form.dog_unwell.route.dog_symptoms.label",
          placeholderKey: "contact_form.dog_unwell.route.dog_symptoms.placeholder",
          required: true,
          rows: 5,
        },
        {
          type: "textarea",
          id: "dog-symptoms-start",
          name: "dog-symptoms-start",
          labelKey: "contact_form.dog_unwell.route.dog_symptoms_start.label",
          required: true,
          rows: 2,
        },
        {
          type: "select",
          id: "had-before",
          name: "had-before",
          labelKey: "contact_form.dog_unwell.route.had_before.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          options: [
            {
              value: "yes",
              labelKey: "contact_form.dog_unwell.route.had_before.options.yes",
            },
            {
              value: "no-eats-other",
              labelKey: "contact_form.dog_unwell.route.had_before.options.no_eats_other",
            },
            {
              value: "no",
              labelKey: "contact_form.dog_unwell.route.had_before.options.no",
            },
          ],
        },
        {
          type: "select",
          id: "dog-eat-other",
          name: "dog-eat-other",
          labelKey: "contact_form.dog_unwell.route.dog_eat_other.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          options: [
            {
              value: "yes",
              labelKey: "contact_form.dog_unwell.route.dog_eat_other.options.yes",
            },
            {
              value: "no",
              labelKey: "contact_form.dog_unwell.route.dog_eat_other.options.no",
            },
          ],
        },
        {
          type: "select",
          id: "sought-vet",
          name: "sought-vet",
          labelKey: "contact_form.dog_unwell.route.sought_vet.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          options: [
            {
              value: "yes",
              labelKey: "contact_form.dog_unwell.route.sought_vet.options.yes",
            },
            {
              value: "no",
              labelKey: "contact_form.dog_unwell.route.sought_vet.options.no",
            },
          ],
        },
        {
          type: "select",
          id: "dog-unwell-type",
          name: "dog-unwell-type",
          labelKey: "contact_form.food_issue.type.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          options: [
            { value: "wet-food", labelText: "Wet Food" },
            { value: "dry-food", labelText: "Dry Food" },
            { value: "treats", labelText: "Treats/Supplements" },
          ],
        },
      ],
      innerSteps: [
        {
          id: "order-lookup",
          insertAfterFieldId: "dog-unwell-retailer",
          alwaysVisible: true,
          fields: [
            {
              type: "orderLookup",
              inputType: "text",
              id: "dog-unwell-order-lookup",
              name: "order-number",
              labelKey: "contact_form.order_lookup.label",
              infoKey: "contact_form.order_lookup.info",
              placeholderKey: "contact_form.order_lookup.placeholder",
              minLength: 6,
              maxLength: 10,
              queryParam: "orderNumber",
              resultsName: "order-products",
              productsLabelKey: "contact_form.order_lookup.products_label_dog_unwell",
            },
          ],
        },
        {
          id: "wet-food",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          fields: [
            {
              type: "input",
              inputType: "text",
              id: "dog-unwell-wet-food-batch",
              name: "dog-unwell-wet-food-batch",
              labelKey: "contact_form.food_issue.type.wet_food.label",
              infoKey: "contact_form.food_issue.type.wet_food.info",
              required: true,
            },
          ],
        },
        {
          id: "dry-food",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          fields: [
            {
              type: "input",
              inputType: "text",
              id: "dog-unwell-dry-food-batch",
              name: "dog-unwell-dry-food-batch",
              labelKey: "contact_form.food_issue.type.dry_food.label",
              infoKey: "contact_form.food_issue.type.dry_food.info",
              required: true,
            },
          ],
        },
        {
          id: "treats",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          fields: [
            {
              type: "input",
              inputType: "text",
              id: "dog-unwell-treats-batch",
              name: "dog-unwell-treats-batch",
              labelKey: "contact_form.food_issue.type.treats.label",
              infoKey: "contact_form.food_issue.type.treats.info",
              required: true,
            },
          ],
        },
      ],
    },
    {
      step: 2,
      route: "discount-code-error",
      titleKey: "contact_form.steps.step_2.discount_code_error.title",
      subtitleKey: "contact_form.steps.step_2.discount_code_error.subtitle_html",
      fields: [
        {
          type: "input",
          inputType: "text",
          id: "discount-code",
          name: "discount-code",
          labelKey: "contact_form.discount_code_error.label",
          infoKey: "contact_form.discount_code_error.info",
          required: true,
        },
      ],
      buttons: {
        next: { step: FINAL_STEP_CONFIG["discount-code-error"].step, label: "Next Step" },
      },
    },
    {
      step: 2,
      route: "food-issue",
      titleKey: "contact_form.steps.step_2.food_issue.title",
      subtitleKey: "contact_form.steps.step_2.food_issue.subtitle_html",
      fields: [
        {
          type: "select",
          id: "food-issue-retailer",
          name: "food-issue-retailer",
          labelKey: "contact_form.food_issue.retailer.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          isRetailerSelect: true,
          retailerSubtitleKey: "contact_form.food_issue.retailer.tool_tip",
          options: [
            {
              valueKey: "contact_form.food_issue.retailer.options.forthglade",
              labelKey: "contact_form.food_issue.retailer.options.forthglade",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.amazon",
              labelKey: "contact_form.food_issue.retailer.options.amazon",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.pets_at_home",
              labelKey: "contact_form.food_issue.retailer.options.pets_at_home",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.supermarket",
              labelKey: "contact_form.food_issue.retailer.options.supermarket",
            },
            {
              valueKey: "contact_form.food_issue.retailer.options.other",
              labelKey: "contact_form.food_issue.retailer.options.other",
            },
          ],
        },
        {
          type: "textarea",
          id: "food-issue-name",
          name: "food-issue-name",
          labelKey: "contact_form.food_issue.name.label",
          infoKey: "contact_form.food_issue.name.info",
          placeholderKey: "contact_form.food_issue.name.placeholder",
          required: true,
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
        },
        {
          type: "textarea",
          id: "food-issue-description",
          name: "food-issue-description",
          labelKey: "contact_form.food_issue.food_ordered.label",
          placeholderKey: "contact_form.food_issue.food_ordered.placeholder",
          required: true,
          rows: 5,
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
        },
        {
          type: "select",
          id: "food-issue-type",
          name: "food-issue-type",
          labelKey: "contact_form.food_issue.type.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          options: [
            { value: "wet-food", labelText: "Wet Food" },
            { value: "dry-food", labelText: "Dry Food" },
            { value: "treats", labelText: "Treats/Supplements" },
          ],
        },
      ],
      innerSteps: [
        {
          id: "order-lookup",
          insertAfterFieldId: "food-issue-retailer",
          preserveActive: true,
          fields: [
            {
              type: "orderLookup",
              inputType: "text",
              id: "food-issue-order-lookup",
              name: "order-number",
              labelKey: "contact_form.order_lookup.label",
              infoKey: "contact_form.order_lookup.info",
              placeholderKey: "contact_form.order_lookup.placeholder",
              minLength: 6,
              maxLength: 10,
              queryParam: "orderNumber",
              resultsName: "order-products",
              productsLabelKey: "contact_form.order_lookup.products_label",
              showDamageField: true,
            },
          ],
        },
        {
          id: "wet-food",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          fields: [
            {
              type: "input",
              inputType: "text",
              id: "food-issue-wet-food-batch",
              name: "food-issue-wet-food-batch",
              labelKey: "contact_form.food_issue.type.wet_food.label",
              infoKey: "contact_form.food_issue.type.wet_food.info",
              required: true,
            },
          ],
        },
        {
          id: "dry-food",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          fields: [
            {
              type: "input",
              inputType: "text",
              id: "food-issue-dry-food-batch",
              name: "food-issue-dry-food-batch",
              labelKey: "contact_form.food_issue.type.dry_food.label",
              infoKey: "contact_form.food_issue.type.dry_food.info",
              required: true,
            },
          ],
        },
        {
          id: "treats",
          hideForRetailerValueKey: "contact_form.food_issue.retailer.options.forthglade",
          fields: [
            {
              type: "input",
              inputType: "text",
              id: "food-issue-treats-batch",
              name: "food-issue-treats-batch",
              labelKey: "contact_form.food_issue.type.treats.label",
              infoKey: "contact_form.food_issue.type.treats.info",
              required: true,
            },
          ],
        },
        {
          id: "food-issue-type-desc",
          alwaysVisible: true,
          fields: [
            {
              type: "textarea",
              id: "food-issue-additional-description",
              name: "food-issue-description",
              labelKey: "contact_form.food_issue.description.label",
              placeholderKey: "contact_form.food_issue.description.placeholder",
              rows: 5,
            },
          ],
        },
      ],
      buttons: {
        next: { step: FINAL_STEP_CONFIG["food-issue"].step, label: "Next Step" },
      },
    },
    {
      step: 2,
      route: "wrong-missing-item",
      titleKey: "contact_form.steps.step_2.wrong_missing_item.title",
    },
    {
      step: 3,
      route: "wrong-missing-item",
      titleKey: "contact_form.steps.step_3.wrong_missing_item.title",
      omitFinalFieldIds: ["customer-name", "customer-email", "additional-information"],
      fields: [
        {
          type: "orderLookup",
          inputType: "text",
          id: "wrong-missing-item-order-lookup",
          name: "order-number",
          labelKey: "contact_form.order_lookup.label",
          infoKey: "contact_form.order_lookup.info",
          placeholderKey: "contact_form.order_lookup.placeholder",
          minLength: 6,
          maxLength: 10,
          queryParam: "orderNumber",
          resultsName: "order-products",
          productsLabelKey: "contact_form.order_lookup.products_label",
        },
        {
          type: "textarea",
          id: "wrong-missing-item-name",
          name: "wrong-missing-item-name",
          labelKey: "contact_form.steps.step_3.wrong_missing_item.name.label",
          infoKey: "contact_form.steps.step_2.wrong_missing_item.name.info",
          placeholderKey: "contact_form.steps.step_2.wrong_missing_item.name.placeholder",
          rows: 4,
          required: true,
        },
        {
          type: "select",
          id: "wrong-missing-item-resolution",
          name: "wrong-missing-item-resolution",
          labelKey: "contact_form.steps.step_3.wrong_missing_item.resolution.label",
          required: true,
          placeholderKey: "contact_form.select_placeholder",
          options: [
            {
              value: "replacement",
              labelKey:
                "contact_form.steps.step_3.wrong_missing_item.resolution.options.replacement",
            },
            {
              value: "credit-note",
              labelKey:
                "contact_form.steps.step_3.wrong_missing_item.resolution.options.credit_note",
            },
          ],
        },
      ],
      buttons: {
        next: { step: FINAL_STEP_CONFIG["wrong-missing-item"].step, label: "Next Step" },
      },
    },
    {
      step: 4,
      route: "subscription-support",
      titleText: "Do you want to amend your subscription?",
      descriptionKey: "contact_form.steps.step_4.subscription_support.info",
      actions: [
        {
          type: "external",
          id: "amend-subscription-button",
          href: "https://forthglade.com/tools/recurring/get-subscription-access",
          labelKey: "contact_form.steps.step_4.subscription_support.amend-subscription-button",
          ariaLabel: "Opens Subscription Portal in a new tab",
          target: "_blank",
        },
        {
          type: "step",
          step: FINAL_STEP_CONFIG["subscription-support"].step,
          labelKey: "contact_form.steps.step_4.subscription_support.cancel-subscription-button",
        },
      ],
    },
    {
      step: 3,
      route: "dog-unwell",
      titleKey: "contact_form.steps.step_2.food_issue.title",
      fields: [
        {
          type: "textarea",
          id: "recipe-name",
          name: "recipe-name",
          labelKey: "contact_form.food_issue.name.label",
          infoKey: "contact_form.food_issue.name.info",
          placeholderKey: "contact_form.food_issue.name.placeholder",
          required: true,
        },
        {
          type: "textarea",
          id: "dog-unwell-description",
          name: "dog-unwell-description",
          labelKey: "contact_form.food_issue.food_ordered.label",
          placeholderKey: "contact_form.food_issue.food_ordered.placeholder",
          required: true,
          rows: 5,
        },
      ],
      innerSteps: [
        {
          id: "dog-unwell-type-desc",
          alwaysVisible: true,
          fields: [
            {
              type: "textarea",
              id: "dog-unwell-additional-description",
              name: "dog-unwell-description",
              labelKey: "contact_form.food_issue.description.label",
              placeholderKey: "contact_form.food_issue.description.placeholder",
              rows: 5,
            },
          ],
        },
      ],
      buttons: {
        next: { step: FINAL_STEP_CONFIG["dog-unwell"].step, label: "Next Step" },
      },
    },
    {
      step: 99,
      titleKey: "contact_form.steps.step_99.title",
      subtitleKey: "contact_form.steps.step_99.subtitle_html",
      isFinalStep: true,
      fields: [
        {
          type: "input",
          inputType: "text",
          id: "customer-name",
          name: "customer-name",
          labelKey: "contact_form.steps.step_99.groups.name.label",
          required: true,
        },
        {
          type: "input",
          inputType: "email",
          id: "customer-email",
          name: "customer-email",
          labelKey: "contact_form.steps.step_99.groups.email.label",
          required: true,
        },
        {
          type: "input",
          inputType: "text",
          id: "customer-phone",
          name: "customer-phone",
          labelKey: "contact_form.steps.step_99.groups.phone.label",
          required: true,
          pattern: "(?:0\\d{10}|\\+44\\d{9,10})",
          attributes: {
            maxlength: 13,
          },
        },
        {
          type: "textarea",
          id: "additional-information",
          name: "additional-information",
          labelKey: "contact_form.steps.step_99.groups.info.label",
          placeholderKey: "contact_form.steps.step_99.groups.image.placeholder",
          required: true,
          rows: 5,
        },
        {
          type: "fileUpload",
          labelKey: "contact_form.steps.step_99.groups.image.label_html",
          labelIsHtml: true,
          buttonLabel: "Add Image",
          inputId: "product-image-input",
          containerId: "product-images-container",
          errorId: "product-images-error",
        },
        {
          type: "checkbox",
          id: "marketing-opt-in",
          name: "marketing-opt-in",
          labelKey: "contact_form.steps.step_99.marketing_opt_in.label",
          value: "yes",
        },
      ],
      buttons: {
        submit: { label: "Submit" },
      },
    },
  ],
  successMessage: {
    headingKey: "contact_form.success_message.heading",
    contentKey: "contact_form.success_message.content",
  },
};

class ContactForm extends HTMLElement {
  constructor() {
    super();
    this.schema = CONTACT_FORM_SCHEMA;
    this.translations = {};
    this.initialized = false;
    this.productImages = [];
    this.MAX_IMAGES = 4;
    this.TOTAL_MAX_IMAGE_BYTES = 20 * 1024 * 1024;
    this.orderLookupFieldConfigs = {};
    this.orderLookupTimers = new Map();
    this.orderLookupControllers = new Map();
    this.orderLookupEndpoint = ORDER_LOOKUP_ENDPOINT;
    this.formSubmitEndpoint = FORM_SUBMIT_ENDPOINT;
    this.dhlTrackingEndpoint = DHL_TRACKING_ENDPOINT;
    this.dhlApiKey = DHL_API_KEY;
    this.dhlTrackingCache = new Map();
    this.subscriptionSupportResources = {};
    this.subscriptionSupportHelper = null;
    this.subscriptionSupportFinalContainer = null;
    this.subscriptionSupportSelfButton = null;
    this.subscriptionSupportAssistButton = null;
    this.subscriptionSupportSelfColumn = null;
    this.subscriptionSupportAssistColumn = null;
    this.subscriptionSupportOtherFinalDefaults = null;
    this.subscriptionSupportSelectedResources = null;
    this.subscriptionSupportOriginalInfoGroup = null;
    this.wrongMissingItemHelper = null;
    this.wrongMissingItemSelection = "";
    this.cancellationInnerStepOriginalParent = null;
    this.cancellationInnerStepOriginalNextSibling = null;
    this.subscriptionSupportOriginalInfoLabel = "";
    this.subscriptionSupportOriginalInfoPlaceholder = undefined;
    this.orderAmendOriginalInfoLabel = null;
    this.orderAmendOriginalInfoPlaceholder = null;
    this.finalStepOriginalTitleDisplay = null;
    this.finalStepOriginalSubtitleDisplay = null;
    this.finalStepSourceRoute = "";
    this.finalStepEl = null;
    this.activeRoute = "";
    this.selectedStepOneOptionId = "";
    this.hasFormInteraction = false;
    this.hasTrackedFormInteraction = false;
    this.klaviyoInteractionTrackingInitialized = false;
    if (this.form) {
      delete this.form.dataset.selectedIssue;
    }
    this.orderTimingOptionalVisible = true;
    this.orderTimingFallbackActive = false;
    this.orderTimingHelpButtonReady = false;
    this.orderAmendHelpButtonReady = false;
  }

  getFinalStepConfig(route = "") {
    if (!route) {
      return FINAL_STEP_CONFIG.other;
    }
    return FINAL_STEP_CONFIG[route] || FINAL_STEP_CONFIG.other;
  }

  getStepConfigForRoute(route = "") {
    if (!route || !this.schema || !Array.isArray(this.schema.steps)) {
      return null;
    }
    return this.schema.steps.find((step) => step.route === route) || null;
  }

  getStepOneOptionById(optionId = "") {
    if (
      !optionId ||
      !this.schema ||
      !this.schema.stepOne ||
      !Array.isArray(this.schema.stepOne.options)
    ) {
      return null;
    }
    return this.schema.stepOne.options.find((option) => option.id === optionId) || null;
  }

  getStepOneOptionByValue(value = "") {
    if (
      !value ||
      !this.schema ||
      !this.schema.stepOne ||
      !Array.isArray(this.schema.stepOne.options)
    ) {
      return null;
    }
    return (
      this.schema.stepOne.options.find((option) => option.value === value || option.id === value) ||
      null
    );
  }

  getCurrentIssueLabel(radio = null) {
    const selectedRadio =
      radio || (this.form ? this.form.querySelector('input[name="current_issue"]:checked') : null);
    const getLabelText = (input) =>
      input ? input.closest("label")?.querySelector(".label-title")?.textContent.trim() || "" : "";

    const selectedIssueId =
      (selectedRadio && selectedRadio.id) ||
      this.selectedStepOneOptionId ||
      (this.form ? this.form.dataset.selectedIssue : "") ||
      "";
    const selectedIssueRoute =
      (selectedRadio && selectedRadio.value) || this.activeRoute || this.finalStepSourceRoute || "";

    let labelText = getLabelText(selectedRadio);
    if (labelText) {
      return labelText;
    }

    if (selectedIssueId && this.form) {
      const issueByIdField = this.form.querySelector(
        `input[name="current_issue"][id="${selectedIssueId}"]`,
      );
      labelText = getLabelText(issueByIdField);
    }
    if (labelText) {
      return labelText;
    }

    let selectedOption = this.getStepOneOptionById(selectedIssueId);
    if (!selectedOption) {
      selectedOption = this.getStepOneOptionByValue(selectedIssueRoute);
    }

    if (selectedOption && selectedOption.id && this.form) {
      const issueByOptionField = this.form.querySelector(
        `input[name="current_issue"][id="${selectedOption.id}"]`,
      );
      labelText = getLabelText(issueByOptionField);
    }
    if (labelText) {
      return labelText;
    }

    if (selectedOption && selectedOption.labelKey) {
      const translatedLabel = this.tPlain(selectedOption.labelKey, "");
      if (translatedLabel && translatedLabel !== selectedOption.labelKey) {
        return translatedLabel;
      }
    }

    return "";
  }

  getFinalStepNumber(route = "") {
    return this.getFinalStepConfig(route).step;
  }

  getFinalStepBackStep(route = "") {
    return this.getFinalStepConfig(route).backStep;
  }

  getFinalStepElement() {
    if (this.finalStepEl) {
      return this.finalStepEl;
    }
    const el = this.querySelector('.step[data-final-step="true"]');
    if (el) {
      this.finalStepEl = el;
    }
    return this.finalStepEl;
  }

  isFinalStepElement(step) {
    return step && step.dataset.finalStep === "true";
  }

  isFinalStepNumber(stepNumber, route = "") {
    return parseInt(stepNumber, 10) === this.getFinalStepNumber(route);
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.translations = this.loadTranslations();
    this.render();
    this.subscriptionSupportResources = this.getSubscriptionSupportResources();
    this.selectors();
    this.prefillCustomerDetails();
    this.setupLogic();
    this.hideFallbackShopifyForm();
  }

  loadTranslations() {
    const translationsId = this.dataset.translationsId;
    if (!translationsId) {
      return {};
    }
    const scriptEl = document.getElementById(translationsId);
    if (!scriptEl) {
      return {};
    }
    try {
      return JSON.parse(scriptEl.textContent || "{}");
    } catch (error) {
      console.warn("FG contact form translations parse error", error);
      return {};
    }
  }

  t(key, fallback = "") {
    if (!key) {
      return fallback;
    }
    return this.translations[key] !== undefined ? this.translations[key] : fallback || key;
  }

  tPlain(key, fallback = "") {
    return this.decodeHtmlEntities(this.t(key, fallback));
  }

  tWithFallback(key, fallback = "") {
    if (!key) {
      return fallback;
    }
    const value = this.t(key, fallback);
    return value === key ? fallback : value;
  }

  decodeHtmlEntities(value) {
    if (typeof value !== "string" || value.indexOf("&") === -1) {
      return value;
    }
    if (!this.htmlEntityDecoder) {
      this.htmlEntityDecoder = document.createElement("textarea");
    }
    this.htmlEntityDecoder.innerHTML = value;
    return this.htmlEntityDecoder.value;
  }

  render() {
    this.orderLookupFieldConfigs = {};
    this.innerHTML = this.buildTemplate();
  }

  buildTemplate() {
    const innerStepsMarkup = this.schema.steps.map((step) => this.renderStep(step)).join("");
    return `
      <form class="form-new" id="customer-services-form" novalidate>
        <div class="inner">
          ${this.renderStartOverButton()}
          ${this.renderStepOne()}
          ${innerStepsMarkup}
        </div>
        <div class="form-success-message" style="display: none;"></div>
        <div class="form-error-message" style="display: none;"></div>
      </form>
    `;
  }

  renderStartOverButton() {
    const labelKey = "contact_form.start_over.back_label";
    return `
      <div class="contact-form-start-over" data-start-over-container hidden>
        <button type="button" class="go-back-button" data-start-over="true">
          <svg width="15" height="15" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15.846 10.681">
            <path data-name="Path 3344" d="M11.217 3.502c.076.013.146.031.114.136-.452.41-1.442.59-.683.574.08.465-.514.131-.777.186-.092.214.088.752-.192.744-.057-.632-.487.44-.421.47-.16-.021-.16-.177-.367-.159-.456.42-.691 1.22-1.643 1.376.028-.198.33-.072.285-.355C6.726 5.568 4.59 3.988 3.42 2.82c-.166-.166-.168-.552-.323-.661A1.98 1.98 0 0 0 .95 2.098c-.531.682-.7 2.041.442 2.192.023.886 1.645 1.405 1.94 2.45a21.695 21.695 0 0 1 3.772 3.243l1.006.088c2.659-2.34 5.067-4.977 7.619-7.444-.5-3.625-3.832.893-4.511.875Z" fill="currentColor" fill-rule="evenodd"></path>
          </svg>
          ${this.t(labelKey, "Back")}
        </button>
      </div>
    `;
  }

  renderStepOne() {
    const options = this.schema.stepOne.options
      .map((option) => {
        const infoMarkup = option.infoKey
          ? `<div class="small">${this.t(option.infoKey)}</div>`
          : "";
        const goToStepAttr = option.goToStep ? ` data-go-to-step="${option.goToStep}"` : "";
        return `
          <label class="radio-wrapper" for="${option.id}">
            <div class="label-title">${this.t(option.labelKey)}</div>
            ${infoMarkup}
            <input type="radio" id="${option.id}" name="current_issue" value="${option.value}"${goToStepAttr}>
          </label>
        `;
      })
      .join("");

    return `
      <div class="step active is-visible" data-step="1">
        <h2 class="step-title">${this.t(this.schema.stepOne.titleKey)}</h2>
        ${options}
      </div>
    `;
  }

  renderStep(step) {
    const titleMarkup = step.titleKey
      ? `<h2 class="step-title">${this.t(step.titleKey)}</h2>`
      : step.titleText
        ? `<h2 class="step-title">${step.titleText}</h2>`
        : "";
    const subtitleMarkup = step.subtitleKey
      ? `<div class="step-subtitle small">${this.t(step.subtitleKey)}</div>`
      : "";
    const descriptionMarkup = step.descriptionKey
      ? `<div class="small">${this.t(step.descriptionKey)}</div>`
      : "";
    const innerSteps = step.innerSteps || [];
    const inlineInnerStepsMap = new Map();
    const remainingInnerSteps = [];

    innerSteps.forEach((innerStep) => {
      if (innerStep.insertAfterFieldId) {
        const fieldId = innerStep.insertAfterFieldId;
        if (!inlineInnerStepsMap.has(fieldId)) {
          inlineInnerStepsMap.set(fieldId, []);
        }
        inlineInnerStepsMap.get(fieldId).push(innerStep);
      } else {
        remainingInnerSteps.push(innerStep);
      }
    });

    const fieldIds = new Set((step.fields || []).map((field) => field.id));
    const fieldsMarkup = step.fields
      ? step.fields
          .map((field) => {
            const fieldMarkup = this.renderField(field);
            const inlineMarkup = inlineInnerStepsMap.has(field.id)
              ? inlineInnerStepsMap
                  .get(field.id)
                  .map((innerStep) => this.renderInnerStep(innerStep))
                  .join("")
              : "";
            return `${fieldMarkup}${inlineMarkup}`;
          })
          .join("")
      : "";

    const unusedInlineInnerSteps = [];
    inlineInnerStepsMap.forEach((stepsArray, fieldId) => {
      if (!fieldIds.has(fieldId)) {
        unusedInlineInnerSteps.push(...stepsArray);
      }
    });

    const innerStepsMarkup = [...remainingInnerSteps, ...unusedInlineInnerSteps]
      .map((innerStep) => this.renderInnerStep(innerStep))
      .join("");
    const disclaimerMarkup = step.disclaimerKey
      ? `<div class="disclaimer small">${this.t(step.disclaimerKey)}</div>`
      : "";
    const actionsMarkup = step.actions ? this.renderActions(step.actions) : "";
    const buttonsMarkup = step.buttons ? this.renderButtons(step.buttons, step) : "";
    const routeAttr = step.route ? ` data-question-route="${step.route}"` : "";
    const finalAttr = step.isFinalStep ? ' data-final-step="true"' : "";
    const omitFinalAttr =
      step.omitFinalFieldIds && step.omitFinalFieldIds.length
        ? ` data-omit-final-fields="${step.omitFinalFieldIds.join(",")}"`
        : "";

    return `
      <div class="step" data-step="${step.step}"${routeAttr}${finalAttr}${omitFinalAttr}>
        ${titleMarkup}
        ${subtitleMarkup}
        ${descriptionMarkup}
        ${fieldsMarkup}
        ${innerStepsMarkup}
        ${disclaimerMarkup}
        ${actionsMarkup}
        ${buttonsMarkup}
      </div>
    `;
  }

  renderField(field) {
    switch (field.type) {
      case "input":
        return this.renderInputField(field);
      case "textarea":
        return this.renderTextareaField(field);
      case "select":
        return this.renderSelectField(field);
      case "fileUpload":
        return this.renderFileUploadField(field);
      case "orderLookup":
        return this.renderOrderLookupField(field);
      case "checkbox":
        return this.renderCheckboxField(field);
      default:
        return "";
    }
  }

  renderInputField(field) {
    const infoMarkup = field.infoKey ? `<div class="small">${this.t(field.infoKey)}</div>` : "";
    const requiredAttr = field.required ? ' required data-original-required="true"' : "";
    const patternAttr = field.pattern ? ` pattern="${field.pattern}"` : "";
    const attributes = this.renderFieldAttributes(field.attributes);
    const labelClass = field.required ? "label-title required" : "label-title";
    const wrapperAttributes = [];
    if (field.hideForRetailerValueKey) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(this.t(field.hideForRetailerValueKey))}"`,
      );
    }
    if (field.hideForRetailerValue) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(field.hideForRetailerValue)}"`,
      );
    }
    const wrapperAttrString = wrapperAttributes.length ? ` ${wrapperAttributes.join(" ")}` : "";
    return `
      <div class="form-group"${wrapperAttrString}>
        <label class="${labelClass}" for="${field.id}">${this.t(field.labelKey)}</label>
        ${infoMarkup}
        <input type="${field.inputType || "text"}" class="form-control" id="${field.id}" name="${field.name}"${requiredAttr}${patternAttr}${attributes}>
      </div>
    `;
  }

  renderTextareaField(field) {
    const infoMarkup = field.infoKey ? `<div class="small">${this.t(field.infoKey)}</div>` : "";
    const requiredAttr = field.required ? ' required data-original-required="true"' : "";
    const rowsAttr = field.rows ? ` rows="${field.rows}"` : "";
    const placeholder = field.placeholderKey
      ? ` placeholder="${this.t(field.placeholderKey)}"`
      : "";
    const noticeMarkup = field.prMarketingNotice
      ? '<div class="small" data-pr-marketing-notice style="display: none;"></div>'
      : "";
    const labelClass = field.required ? "label-title required" : "label-title";
    const wrapperAttributes = [];
    if (field.hideForRetailerValueKey) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(this.t(field.hideForRetailerValueKey))}"`,
      );
    }
    if (field.hideForRetailerValue) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(field.hideForRetailerValue)}"`,
      );
    }
    const wrapperAttrString = wrapperAttributes.length ? ` ${wrapperAttributes.join(" ")}` : "";
    return `
      <div class="form-group"${wrapperAttrString}>
        <label class="${labelClass}" for="${field.id}">${this.t(field.labelKey)}</label>
        ${noticeMarkup}
        ${infoMarkup}
        <textarea class="form-control" id="${field.id}" name="${field.name}"${rowsAttr}${placeholder}${requiredAttr}></textarea>
      </div>
    `;
  }

  renderSelectField(field) {
    const requiredAttr = field.required ? ' required data-original-required="true"' : "";
    const labelClass = field.required ? "label-title required" : "label-title";
    const customAttributes = this.renderFieldAttributes(field.attributes);
    const retailerSelectAttr = field.isRetailerSelect ? ' data-retailer-select="true"' : "";
    const selectAttributes = `${requiredAttr}${retailerSelectAttr}${customAttributes}`;
    const placeholder = field.placeholderKey
      ? `<option value="" disabled selected>${this.t(field.placeholderKey)}</option>`
      : "";
    const optionsMarkup = field.options
      .map((option) => {
        const value = option.valueKey ? this.t(option.valueKey) : option.value || "";
        const label = option.labelKey ? this.t(option.labelKey) : option.labelText || "";
        return `<option value="${value}">${label}</option>`;
      })
      .join("");
    const wrapperAttributes = [];
    if (field.hideForRetailerValueKey) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(this.t(field.hideForRetailerValueKey))}"`,
      );
    }
    if (field.hideForRetailerValue) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(field.hideForRetailerValue)}"`,
      );
    }
    const wrapperAttrString = wrapperAttributes.length ? ` ${wrapperAttributes.join(" ")}` : "";
    const retailerSubtitleMarkup = field.retailerSubtitleKey
      ? `<p class="retailer-subtitle" data-retailer-subtitle hidden>${this.t(field.retailerSubtitleKey)}</p>`
      : "";
    return `
      <div class="form-group"${wrapperAttrString}>
        <label class="${labelClass}" for="${field.id}">${this.t(field.labelKey)}</label>
        <select class="form-control" id="${field.id}" name="${field.name}"${selectAttributes}>
          ${placeholder}
          ${optionsMarkup}
        </select>
        ${retailerSubtitleMarkup}
      </div>
    `;
  }

  renderCheckboxField(field) {
    const infoMarkup = field.infoKey ? `<div class="small">${this.t(field.infoKey)}</div>` : "";
    const requiredAttr = field.required ? ' required data-original-required="true"' : "";
    const attributes = this.renderFieldAttributes(field.attributes);
    const valueAttr = field.value ? ` value="${this.escapeAttribute(field.value)}"` : "";
    const wrapperAttributes = [];
    if (field.hideForRetailerValueKey) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(this.t(field.hideForRetailerValueKey))}"`,
      );
    }
    if (field.hideForRetailerValue) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(field.hideForRetailerValue)}"`,
      );
    }
    const wrapperAttrString = wrapperAttributes.length ? ` ${wrapperAttributes.join(" ")}` : "";
    const labelContent = this.t(field.labelKey);
    return `
      <div class="form-group">  
        <div class="checkbox"${wrapperAttrString}>
          <div class="checkbox__input">
            <input class="input-checkbox" type="checkbox" id="${field.id}" name="${field.name}"${valueAttr}${requiredAttr}${attributes}>
          </div>
          <label class="checkbox__label" for="${field.id}">${labelContent}</label>
          ${infoMarkup}
        </div>
      </div>
    `;
  }

  renderOrderLookupField(field) {
    this.orderLookupFieldConfigs[field.id] = { ...field };
    const infoMarkup = field.infoKey ? `<div class="small">${this.t(field.infoKey)}</div>` : "";
    const requiredAttr = field.required ? ' required data-original-required="true"' : "";
    const placeholderAttr = field.placeholderKey
      ? ` placeholder="${this.escapeAttribute(this.t(field.placeholderKey))}"`
      : "";
    const labelClass = field.required ? "label-title required" : "label-title";
    const maxLengthAttr = field.maxLength ? ` maxlength="${field.maxLength}"` : "";
    const dataAttributes = [
      'data-order-lookup-input="true"',
      `data-results-name="${this.escapeAttribute(field.resultsName || `${field.name || field.id}-product`)}"`,
    ];
    if (field.minLength) {
      dataAttributes.push(`data-min-length="${field.minLength}"`);
    }
    if (field.maxLength) {
      dataAttributes.push(`data-max-length="${field.maxLength}"`);
    }
    if (field.queryParam) {
      dataAttributes.push(`data-query-param="${this.escapeAttribute(field.queryParam)}"`);
    }
    const dataAttrString = dataAttributes.join(" ");
    const dataAttributesAttr = dataAttrString ? ` ${dataAttrString}` : "";
    const emailLabel = this.t(
      field.emailLabelKey || "contact_form.order_lookup.email_label",
      "Email",
    );
    const postcodeLabel = this.t(
      field.postcodeLabelKey || "contact_form.order_lookup.postcode_label",
      "Postcode",
    );
    const emailName = field.emailFieldName || `${field.name || field.id}-email`;
    const postcodeName = field.postcodeFieldName || `${field.name || field.id}-postcode`;
    const emailPlaceholder = this.t(
      field.emailPlaceholderKey || "contact_form.order_lookup.email_placeholder",
      "Enter your email address",
    );
    const postcodePlaceholder = this.t(
      field.postcodePlaceholderKey || "contact_form.order_lookup.postcode_placeholder",
      "Enter your postcode",
    );
    const ordersLabel = this.t(
      field.ordersLabelKey || "contact_form.order_lookup.orders_label",
      "Select your order",
    );
    const productsLabel =
      field.productsLabelText ||
      this.t(
        field.productsLabelKey || "contact_form.order_lookup.products_label",
        "Select the products you need help with",
      );
    const productsLabelClass =
      field.disableProductSelection === true
        ? "order-lookup-results__label label-title"
        : "order-lookup-results__label label-title required";
    const submitLabel = field.submitLabelKey
      ? this.t(field.submitLabelKey)
      : field.submitLabelText || this.t("contact_form.order_lookup.submit_label", "Find order");
    const damageFieldId = `${field.id}-number-items-damaged`;
    const damageFieldMarkup = field.showDamageField
      ? `
        <div class="form-group order-lookup-damage" data-order-lookup-damage hidden>
          <label class="label-title" for="${damageFieldId}"></label>
          <input type="number" class="form-control" id="${damageFieldId}" name="number-items-damaged" min="1" step="1" inputmode="numeric" data-original-required="true" disabled>
        </div>
      `
      : "";

    return `
      <div class="order-lookup" data-order-lookup-wrapper>
        <div class="form-group" data-order-lookup-number-group="true">
          <label class="${labelClass}" for="${field.id}">${this.t(field.labelKey)}</label>
          ${infoMarkup}
          <input type="text" class="form-control" id="${field.id}" name="${field.name || field.id}" inputmode="numeric" pattern="[0-9]*"${placeholderAttr}${requiredAttr}${maxLengthAttr}${dataAttributesAttr}>
        </div>
        <div class="order-lookup-identity" data-order-lookup-identity-group>
          <div class="form-group">
            <div class="order-lookup-identity__field">
              <label class="label-title required" for="${field.id}-email">${emailLabel}</label>
              <input type="email" class="form-control" id="${field.id}-email" name="${emailName}" placeholder="${emailPlaceholder}" required data-original-required="true" data-order-lookup-email="true">
            </div>
          </div>
          <div class="form-group">
            <div class="order-lookup-identity__field">
              <label class="label-title required" for="${field.id}-postcode">${postcodeLabel}</label>
              <div class="order-lookup-identity__row">
                <input type="text" class="form-control" id="${field.id}-postcode" name="${postcodeName}" placeholder="${postcodePlaceholder}" required data-original-required="true" data-order-lookup-postcode="true">
                <button type="button" class="button-2 order-lookup-submit" data-order-lookup-submit disabled>${submitLabel}</button>
              </div>
            </div>
          </div>
        </div>
        <div class="form-group">  
          <div class="order-lookup-orders" data-order-lookup-orders style="display:none;">
            <div class="order-lookup-orders__label label-title required">${ordersLabel}</div>
            <div class="order-lookup-orders__list" data-order-lookup-orders-list></div>
          </div>
          <div class="order-lookup-status" data-order-lookup-status style="display:none;"></div>
          <div class="order-lookup-results" data-order-lookup-results style="display:none;">
            <div class="${productsLabelClass}">${productsLabel}</div>
            <div class="order-lookup-products" data-order-lookup-products></div>
          </div>
          <div class="order-lookup-tracking" data-order-lookup-tracking style="display:none;">
            <div class="order-lookup-tracking__label label-title">${this.t("contact_form.order_lookup.tracking_label", "Great, we've found your order.")}</div>
            <div class="order-lookup-tracking__content" data-order-lookup-tracking-content></div>
          </div>  
          <span class="validation-error" data-order-lookup-error></span>
        </div>
        ${damageFieldMarkup}
      </div>
    `;
  }

  renderFileUploadField(field) {
    const labelMarkup = field.labelIsHtml
      ? this.t(field.labelKey)
      : `<span class="label-title">${this.t(field.labelKey)}</span>`;
    return `
      <div class="form-group">
        <div class="image-upload">
          <label class="label-title">${labelMarkup}</label>
          <button type="button" id="add-image-btn" class="button-2 button--add-image" style="margin-top:10px;">${field.buttonLabel || "Add Image"}</button>
        </div>
        <span id="${field.errorId}" class="validation-error" style="display:none;"></span>
        <div id="${field.containerId}" class="product-images-container"></div>
        <input type="file" id="${field.inputId}" accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif" style="display:none;" multiple>
      </div>
    `;
  }

  renderInnerStep(innerStep) {
    const innerStepClass = innerStep.alwaysVisible ? "step inner-step active" : "step inner-step";
    const alwaysVisibleAttr = innerStep.alwaysVisible ? ' data-always-visible="true"' : "";
    const preserveActiveAttr = innerStep.preserveActive ? ' data-preserve-active="true"' : "";
    const wrapperAttributes = [];
    if (innerStep.hideForRetailerValueKey) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(this.t(innerStep.hideForRetailerValueKey))}"`,
      );
    }
    if (innerStep.hideForRetailerValue) {
      wrapperAttributes.push(
        `data-hide-for-retailer-value="${this.escapeAttribute(innerStep.hideForRetailerValue)}"`,
      );
    }
    const wrapperAttrString = wrapperAttributes.length ? ` ${wrapperAttributes.join(" ")}` : "";
    const fieldsMarkup = innerStep.fields.map((field) => this.renderField(field)).join("");
    return `
      <div class="${innerStepClass}" data-inner-step="${innerStep.id}"${alwaysVisibleAttr}${preserveActiveAttr}${wrapperAttrString}>
        ${fieldsMarkup}
      </div>
    `;
  }

  renderButtons(buttons, stepConfig = null) {
    const route = stepConfig && stepConfig.route ? stepConfig.route : "";
    const finalStepNumber =
      route && this.getFinalStepConfig(route) ? this.getFinalStepNumber(route) : null;
    const shouldRenderNext =
      buttons.next &&
      !(route && finalStepNumber !== null && parseInt(buttons.next.step, 10) === finalStepNumber);

    const next = shouldRenderNext
      ? `<a data-go-to-step="${buttons.next.step}"${buttons.next.route ? ` data-step-route="${buttons.next.route}"` : ""} class="button button-2${
          buttons.next.classes ? ` ${buttons.next.classes}` : ""
        }">
            ${buttons.next.label || "Next Step"}
          </a>`
      : "";

    const submit = buttons.submit
      ? `<button class="button-2" type="submit">${buttons.submit.label || "Submit"}</button>`
      : "";

    if (!next && !submit) {
      return "";
    }

    return `
      <div class="button-container">
        ${next}
        ${submit}
      </div>
    `;
  }

  renderActions(actions) {
    if (!actions || !actions.length) {
      return "";
    }
    const markup = actions
      .map((action) => {
        if (action.type === "external") {
          const ariaLabel = action.ariaLabel ? ` aria-label="${action.ariaLabel}"` : "";
          const targetAttr = action.target ? ` target="${action.target}"` : "";
          const idAttr = action.id ? ` id="${action.id}"` : "";
          return `<a href="${action.href}" class="button button-2"${idAttr}${targetAttr}${ariaLabel}>${this.t(action.labelKey)}</a>`;
        }
        if (action.type === "step") {
          return `<a data-go-to-step="${action.step}" class="button button-2">${this.t(action.labelKey)}</a>`;
        }
        return "";
      })
      .join("");

    return `<div class="subscription-support-buttons">${markup}</div>`;
  }

  renderFieldAttributes(attributes = {}) {
    return Object.entries(attributes)
      .map(([key, value]) => ` ${key}="${value}"`)
      .join("");
  }

  escapeAttribute(value) {
    if (typeof value !== "string") {
      return value;
    }
    return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
  }

  selectors() {
    this.form = this.querySelector("form");
    this.stepOne = this.querySelector('.step[data-step="1"]');
    this.mainSteps = Array.from(this.querySelectorAll(".step")).filter(
      (step) => !step.classList.contains("inner-step"),
    );
    this.finalStepEl = this.getFinalStepElement();
    if (this.finalStepEl) {
      this.mainSteps = this.mainSteps.filter((step) => step !== this.finalStepEl);
    }
    this.routeSelectors = Array.from(this.querySelectorAll('input[name="current_issue"]'));
    this.stepButtons = Array.from(this.querySelectorAll("[data-go-to-step]"));
    this.subscriptionReasonSelector = this.querySelector("#amended-subscription-subject");
    this.cancellationReasonSelector = this.querySelector("#cancellation-reason");
    this.wrongMissingItemTypeField = this.querySelector("#wrong-missing-item-type");
    this.amendReasonSelector = this.querySelector("#amended-order-subject");
    this.foodIssueTypeSelector = this.querySelector("#food-issue-type");
    this.dogUnwellTypeSelector = this.querySelector("#dog-unwell-type");
    this.dogUnwellRetailerSelector = this.querySelector("#dog-unwell-retailer");
    this.retailerTypeSelector = this.querySelector("#food-issue-retailer");
    this.addImageBtn = this.querySelector("#add-image-btn");
    this.imageInput = this.querySelector("#product-image-input");
    this.imagesError = this.querySelector("#product-images-error");
    this.productImagesContainer = this.querySelector("#product-images-container");
    this.successMessage = this.querySelector(".form-success-message");
    this.errorMessage = this.querySelector(".form-error-message");
    this.amendSubscriptionButton = this.querySelector("#amend-subscription-button");
    this.startOverButton = this.querySelector("[data-start-over]");
    this.startOverContainer = this.querySelector("[data-start-over-container]");
    this.fallbackShopifyForm = null;
  }

  prefillCustomerDetails() {
    if (!this.form) {
      return;
    }
    const customerName = (this.dataset.customerName || "").trim();
    const customerEmail = (this.dataset.customerEmail || "").trim();
    const customerPhone = (this.dataset.customerPhone || "").trim();
    const customerPostcode = (this.dataset.customerPostcode || "").trim();

    const prefillFieldValue = (field, value) => {
      if (!field || !value) {
        return;
      }
      if ((field.value || "").trim()) {
        return;
      }
      field.value = value;
    };

    const emailFields = this.form.querySelectorAll(
      'input[type="email"], input[name*="customer-email"], input[id*="customer-email"]',
    );
    emailFields.forEach((field) => {
      if (field.type === "hidden") {
        return;
      }
      prefillFieldValue(field, customerEmail);
    });

    const phoneFields = this.form.querySelectorAll(
      'input[type="tel"], input[name*="customer-phone"], input[id*="customer-phone"]',
    );
    phoneFields.forEach((field) => {
      if (field.type === "hidden") {
        return;
      }
      prefillFieldValue(field, customerPhone);
    });

    const postcodeFields = this.form.querySelectorAll(
      'input[name*="postcode"], input[id*="postcode"], input[data-order-lookup-postcode="true"]',
    );
    postcodeFields.forEach((field) => {
      if (field.type === "hidden") {
        return;
      }
      prefillFieldValue(field, customerPostcode);
    });

    const nameFields = this.form.querySelectorAll(
      'input[name*="name"], input[id*="customer-name"]',
    );
    nameFields.forEach((field) => {
      const type = (field.type || "text").toLowerCase();
      if (field.type === "hidden" || !["text", "search"].includes(type)) {
        return;
      }
      prefillFieldValue(field, customerName);
    });
  }

  setupLogic() {
    if (!this.form) {
      return;
    }

    this.setupOrderNumberLimit();
    this.setupPhoneInputRestrictions();
    this.setupFieldListeners();
    this.setupRouteSelectors();
    this.setupStepButtons();
    this.setupTypeSelectors();
    this.setupRetailerTypeWatcher();
    this.setupRetailerSubtitleNotices();
    this.setupOrderLookupFields();
    this.setupDogUnwellOrderLookupVisibility();
    this.setupAmendReasonWatcher();
    this.setupSubscriptionReasonWatcher();
    this.setupWrongMissingItemWatcher();
    this.setupCustomerDetailsMirroring();
    this.setupFileUpload();
    this.setupFormSubmission();
    this.setupKlaviyoInteractionTracking();
    this.setupAmendSubscriptionShortcut();
    this.setupStartOverButton();

    if (this.stepOne) {
      this.stepOne.classList.add("is-visible");
    }

    this.refreshConditionalSelectors();
    this.renderProductImages();
    this.updateAddImageBtnState();
    this.prepareFinalStep();
    this.applyFinalStepCustomizations("");
    this.updateStartOverVisibility();
  }

  setupOrderNumberLimit() {
    const numberFields = this.form.querySelectorAll(
      '[name="order-number"], [name="wrong-missing-item-order-number"]',
    );
    numberFields.forEach((field) => {
      this.addNumericInputRestrictions(field, 10);
    });
  }

  setupPhoneInputRestrictions() {
    const phoneField = this.form.querySelector("#customer-phone");
    if (!phoneField || phoneField.dataset.phoneRestrictionAttached === "true") {
      return;
    }
    phoneField.dataset.phoneRestrictionAttached = "true";

    phoneField.addEventListener("input", () => {
      const rawValue = phoneField.value || "";
      if (!rawValue) {
        return;
      }
      const hasLeadingPlus = rawValue.startsWith("+");
      const digitsOnly = rawValue.replace(/\D/g, "");
      const nextValueRaw = hasLeadingPlus ? `+${digitsOnly}` : digitsOnly;
      const nextValue = nextValueRaw.slice(0, 13);
      if (phoneField.value !== nextValue) {
        phoneField.value = nextValue;
      }
    });
  }

  addNumericInputRestrictions(field, maxLength = 0) {
    if (!field || field.dataset.numericHandlerAttached === "true") {
      return;
    }
    field.dataset.numericHandlerAttached = "true";

    field.addEventListener("input", () => {
      let value = field.value.replace(/[^0-9]/g, "");
      if (maxLength) {
        value = value.slice(0, maxLength);
      }
      if (field.value !== value) {
        field.value = value;
      }
    });

    field.addEventListener("keydown", (event) => {
      const allowedKeys = [46, 8, 9, 27, 13, 37, 38, 39, 40];
      const hasModifier = event.ctrlKey || event.metaKey;
      if (
        allowedKeys.includes(event.keyCode) ||
        (event.keyCode === 65 && hasModifier) ||
        (event.keyCode === 67 && hasModifier) ||
        (event.keyCode === 86 && hasModifier) ||
        (event.keyCode === 88 && hasModifier)
      ) {
        return;
      }

      if (event.key && !/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        return;
      }

      if (maxLength && field.value.length >= maxLength) {
        event.preventDefault();
      }
    });
  }

  // Mirrors each product checkbox onto its card so the selected styling does not rely
  // solely on :has(), and so programmatic resets cannot leave a card looking selected.
  syncOrderLookupProductOptionStates(scope) {
    const root = scope || this.form;
    if (!root) {
      return;
    }
    root.querySelectorAll('input[data-order-product-checkbox="true"]').forEach((checkbox) => {
      const option = checkbox.closest(".order-lookup-product-option");
      if (option) {
        option.classList.toggle("is-selected", checkbox.checked);
      }
    });
  }

  getOrderLookupElements(wrapper) {
    if (!wrapper) {
      return null;
    }
    const damageContainer = wrapper.querySelector("[data-order-lookup-damage]");
    return {
      numberInput: wrapper.querySelector('[data-order-lookup-input="true"]'),
      emailInput: wrapper.querySelector('[data-order-lookup-email="true"]'),
      postcodeInput: wrapper.querySelector('[data-order-lookup-postcode="true"]'),
      statusContainer: wrapper.querySelector("[data-order-lookup-status]"),
      identityGroup: wrapper.querySelector("[data-order-lookup-identity-group]"),
      ordersContainer: wrapper.querySelector("[data-order-lookup-orders]"),
      ordersList: wrapper.querySelector("[data-order-lookup-orders-list]"),
      productsContainer: wrapper.querySelector("[data-order-lookup-products]"),
      resultsContainer: wrapper.querySelector("[data-order-lookup-results]"),
      trackingContainer: wrapper.querySelector("[data-order-lookup-tracking]"),
      trackingContent: wrapper.querySelector("[data-order-lookup-tracking-content]"),
      damageContainer,
      damageLabel: damageContainer ? damageContainer.querySelector("label") : null,
      damageInput: damageContainer ? damageContainer.querySelector("input") : null,
    };
  }

  toggleOrderNumberFieldVisibility(input, visible) {
    if (!input) {
      return;
    }
    const group = input.closest(".form-group");
    if (group) {
      if (visible) {
        group.style.display = "";
        group.removeAttribute("hidden");
      } else {
        group.style.display = "none";
        group.setAttribute("hidden", "hidden");
      }
    }

    if (!visible) {
      if (input.required) {
        input.dataset.orderLookupOriginalRequired = "true";
        input.required = false;
      }
    } else if (input.dataset.orderLookupOriginalRequired === "true") {
      input.required = true;
    }
  }

  getSubscriptionSupportResources() {
    const rescheduleUrl =
      this.dataset.subscriptionSupportRescheduleUrl ||
      "https://forthglade.com/pages/reschedule-subscription-delivery";
    const amendUrl =
      this.dataset.subscriptionSupportAmendProductsUrl ||
      "https://forthglade.com/pages/edit-products-in-subscription";
    const cancelUrl =
      this.dataset.subscriptionSupportCancelUrl ||
      "https://forthglade.com/pages/how-to-cancel-subscription";
    const faqUrl = this.dataset.subscriptionSupportFaqUrl || "https://forthglade.com/pages/faqs";
    return {
      "reschedule-delivery": {
        selfLinkUrl: rescheduleUrl,
        infoLabel: this.t(
          "contact_form.subscription_support.labels.reschedule",
          "When would you like your order",
        ),
        placeholder: this.t(
          "contact_form.subscription_support.placeholders.reschedule",
          "When would you like your order",
        ),
      },
      "add-remove-products": {
        selfLinkUrl: amendUrl,
        infoLabel: this.t(
          "contact_form.subscription_support.labels.amend",
          "What products would you like amending",
        ),
        placeholder: this.t(
          "contact_form.subscription_support.placeholders.amend",
          "What products would you like amending",
        ),
      },
      cancel: {
        selfLinkUrl: cancelUrl,
        infoLabel: this.t(
          "contact_form.subscription_support.labels.cancel",
          "Please select a cancellation reason",
        ),
        placeholder: this.t(
          "contact_form.subscription_support.placeholders.cancel",
          "Please select a cancellation reason",
        ),
      },
      other: {
        selfLinkUrl: faqUrl,
        infoLabel: this.t("contact_form.subscription_support.labels.other", "How can we help?"),
        placeholder: this.t(
          "contact_form.subscription_support.placeholders.other",
          "Tell us how we can help",
        ),
      },
    };
  }

  ensureSubscriptionSupportHelper(step) {
    if (!step) {
      return null;
    }
    let helper = step.querySelector("[data-subscription-support-helper]");
    if (helper) {
      this.subscriptionSupportHelper = helper;
      const downloadLink = helper.querySelector("[data-subscription-support-download]");
      if (downloadLink) {
        downloadLink.remove();
      }
      this.subscriptionSupportFinalContainer = step.querySelector(
        "[data-subscription-support-final]",
      );
      if (!this.subscriptionSupportFinalContainer) {
        const finalWrapper = document.createElement("div");
        finalWrapper.className = "subscription-support-helper__final";
        finalWrapper.dataset.subscriptionSupportFinal = "true";
        finalWrapper.style.display = "none";
        helper.insertAdjacentElement("afterend", finalWrapper);
        this.subscriptionSupportFinalContainer = finalWrapper;
      }
      let selfButton = helper.querySelector(".subscription-support-helper__button--self");
      if (selfButton && selfButton.tagName === "BUTTON") {
        const linkButton = document.createElement("a");
        linkButton.className = selfButton.className;
        linkButton.innerHTML = selfButton.innerHTML;
        if (selfButton.id) {
          linkButton.id = selfButton.id;
        }
        selfButton.replaceWith(linkButton);
        selfButton = linkButton;
      }
      this.subscriptionSupportSelfButton = selfButton;
      this.subscriptionSupportAssistButton = helper.querySelector(
        ".subscription-support-helper__button--assist",
      );
      this.subscriptionSupportSelfColumn = helper.querySelector(
        ".subscription-support-helper__column--self",
      );
      this.subscriptionSupportAssistColumn = helper.querySelector(
        ".subscription-support-helper__column--assist",
      );
      this.subscriptionSupportSelfSubtitle = helper.querySelector(
        ".subscription-support-helper__column--self .subscription-support-helper__subtitle",
      );
      this.subscriptionSupportAssistSubtitle = helper.querySelector(
        ".subscription-support-helper__column--assist .subscription-support-helper__subtitle",
      );
      return helper;
    }
    const selectField = step.querySelector("#amended-subscription-subject");
    if (!selectField) {
      return null;
    }
    const selectGroup = selectField.closest(".form-group") || selectField.parentElement;
    if (!selectGroup) {
      return null;
    }

    helper = document.createElement("div");
    helper.className = "subscription-support-helper";
    helper.dataset.subscriptionSupportHelper = "true";

    const selfColumn = document.createElement("div");
    selfColumn.className =
      "subscription-support-helper__column subscription-support-helper__column--self";
    selfColumn.setAttribute("role", "button");
    selfColumn.tabIndex = 0;

    const selfButton = document.createElement("a");
    selfButton.className =
      "subscription-support-helper__button subscription-support-helper__button--self";
    selfButton.textContent = this.tPlain(
      "contact_form.subscription_support.self_service_title",
      "Here's how you can do this yourself",
    );
    const selfSubtitleText = this.tPlain(
      "contact_form.subscription_support.self_service_subtitle",
      "",
    );
    const selfSubtitle = document.createElement("div");
    selfSubtitle.className = "small subscription-support-helper__subtitle";
    selfSubtitle.textContent = selfSubtitleText;

    selfColumn.appendChild(selfButton);
    if (selfSubtitleText) {
      selfColumn.appendChild(selfSubtitle);
    }

    const assistColumn = document.createElement("div");
    assistColumn.className =
      "subscription-support-helper__column subscription-support-helper__column--assist";
    assistColumn.setAttribute("role", "button");
    assistColumn.tabIndex = 0;

    const assistButton = document.createElement("button");
    assistButton.type = "button";
    assistButton.className =
      "subscription-support-helper__button subscription-support-helper__button--assist";
    assistButton.textContent = this.tPlain(
      "contact_form.subscription_support.assisted_title",
      "I'd prefer some help",
    );
    const assistSubtitleText = this.tPlain(
      "contact_form.subscription_support.assisted_subtitle",
      "",
    );
    const assistSubtitle = document.createElement("div");
    assistSubtitle.className = "small subscription-support-helper__subtitle";
    assistSubtitle.textContent = assistSubtitleText;

    assistColumn.appendChild(assistButton);
    if (assistSubtitleText) {
      assistColumn.appendChild(assistSubtitle);
    }

    helper.appendChild(selfColumn);
    helper.appendChild(assistColumn);

    selectGroup.insertAdjacentElement("afterend", helper);

    const finalWrapper = document.createElement("div");
    finalWrapper.className = "subscription-support-helper__final";
    finalWrapper.dataset.subscriptionSupportFinal = "true";
    finalWrapper.style.display = "none";
    helper.insertAdjacentElement("afterend", finalWrapper);

    this.subscriptionSupportHelper = helper;
    this.subscriptionSupportFinalContainer = finalWrapper;
    this.subscriptionSupportSelfButton = selfButton;
    this.subscriptionSupportAssistButton = assistButton;
    this.subscriptionSupportSelfColumn = selfColumn;
    this.subscriptionSupportAssistColumn = assistColumn;
    this.subscriptionSupportSelfSubtitle = selfSubtitleText ? selfSubtitle : null;
    this.subscriptionSupportAssistSubtitle = assistSubtitleText ? assistSubtitle : null;

    return helper;
  }

  updateSubscriptionSupportHelper(step) {
    if (!this.subscriptionReasonSelector) {
      return;
    }
    const selectedValue = this.subscriptionReasonSelector.value;
    const selectionChanged = selectedValue !== this.subscriptionSupportSelection;
    this.subscriptionSupportSelection = selectedValue;
    this.applySubscriptionSupportOtherFinalState(false);

    const finalStep = this.getFinalStepElement();
    const helperContainer = this.subscriptionSupportFinalContainer;
    const isFinalVisible = finalStep && finalStep.classList.contains("is-visible");
    const finalParent = finalStep ? finalStep.parentNode : null;
    if (
      selectionChanged &&
      finalStep &&
      isFinalVisible &&
      ((step && finalParent === step) || (helperContainer && finalParent === helperContainer))
    ) {
      this.showFinalStep(null);
      if (helperContainer) {
        helperContainer.style.display = "none";
      }
    }

    const resources = this.subscriptionSupportResources[selectedValue] || null;
    this.subscriptionSupportSelectedResources = resources;

    if (!selectedValue || !resources) {
      this.removeSubscriptionSupportHelper(step);
      if (step) {
        this.setSubscriptionSupportNextButtonVisibility(step, true);
      }
      return;
    }

    const helper = this.ensureSubscriptionSupportHelper(step);
    if (!helper) {
      return;
    }

    this.setSubscriptionSupportNextButtonVisibility(step, false);
    this.updateSubscriptionSupportOtherSubtitle(selectedValue);
    const setActiveColumn = (activeColumn) => {
      if (!this.subscriptionSupportSelfColumn || !this.subscriptionSupportAssistColumn) {
        return;
      }
      this.subscriptionSupportSelfColumn.classList.toggle(
        "subscription-support-helper__column--active",
        activeColumn === this.subscriptionSupportSelfColumn,
      );
      this.subscriptionSupportAssistColumn.classList.toggle(
        "subscription-support-helper__column--active",
        activeColumn === this.subscriptionSupportAssistColumn,
      );
    };

    if (this.subscriptionSupportSelfButton) {
      const selfLinkUrl = resources.selfLinkUrl || "";
      const hasSelfLink = Boolean(selfLinkUrl);
      this.subscriptionSupportSelfButton.classList.toggle(
        "subscription-support-helper__button--disabled",
        !hasSelfLink,
      );
      if (hasSelfLink) {
        this.subscriptionSupportSelfButton.setAttribute("href", selfLinkUrl);
        this.subscriptionSupportSelfButton.setAttribute("target", "_blank");
        this.subscriptionSupportSelfButton.setAttribute("rel", "noopener");
        this.subscriptionSupportSelfButton.removeAttribute("aria-disabled");
        this.subscriptionSupportSelfButton.removeAttribute("tabindex");
      } else {
        this.subscriptionSupportSelfButton.removeAttribute("href");
        this.subscriptionSupportSelfButton.removeAttribute("target");
        this.subscriptionSupportSelfButton.removeAttribute("rel");
        this.subscriptionSupportSelfButton.setAttribute("aria-disabled", "true");
        this.subscriptionSupportSelfButton.tabIndex = -1;
      }
      this.subscriptionSupportSelfButton.onclick = (event) => {
        if (!hasSelfLink) {
          event.preventDefault();
          return;
        }
        setActiveColumn(this.subscriptionSupportSelfColumn);
        if (this.subscriptionSupportFinalContainer) {
          const finalStep = this.getFinalStepElement();
          if (finalStep && finalStep.parentNode === this.subscriptionSupportFinalContainer) {
            this.showFinalStep(null);
            this.resetSubscriptionSupportFinalLabel();
          }
          this.subscriptionSupportFinalContainer.style.display = "none";
        }
      };
    }

    if (this.subscriptionSupportSelfColumn && this.subscriptionSupportSelfButton) {
      const selfDisabled = this.subscriptionSupportSelfButton.classList.contains(
        "subscription-support-helper__button--disabled",
      );
      this.subscriptionSupportSelfColumn.classList.toggle(
        "subscription-support-helper__column--disabled",
        selfDisabled,
      );
      this.subscriptionSupportSelfColumn.onclick = (event) => {
        if (selfDisabled || event.target.closest("a,button")) {
          return;
        }
        this.subscriptionSupportSelfButton.click();
      };
      this.subscriptionSupportSelfColumn.onkeydown = (event) => {
        if (selfDisabled) {
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.subscriptionSupportSelfButton.click();
        }
      };
    }

    if (this.subscriptionSupportAssistButton) {
      this.subscriptionSupportAssistButton.disabled = false;
      this.subscriptionSupportAssistButton.onclick = () => {
        setActiveColumn(this.subscriptionSupportAssistColumn);
        this.showSubscriptionSupportFinal(step);
      };
    }

    if (this.subscriptionSupportAssistColumn && this.subscriptionSupportAssistButton) {
      this.subscriptionSupportAssistColumn.classList.remove(
        "subscription-support-helper__column--disabled",
      );
      this.subscriptionSupportAssistColumn.onclick = (event) => {
        if (event.target.closest("a,button")) {
          return;
        }
        this.subscriptionSupportAssistButton.click();
      };
      this.subscriptionSupportAssistColumn.onkeydown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.subscriptionSupportAssistButton.click();
        }
      };
    }

    if (selectedValue === "cancel") {
      this.moveCancellationInnerStepToFinal(step);
      this.setAdditionalInformationVisibility(false);
      this.toggleAdditionalInformationVisibility(false);
    } else {
      this.restoreCancellationInnerStep();
      this.toggleAdditionalInformationVisibility(true);
    }

    if (this.subscriptionSupportFinalContainer) {
      const finalStep = this.getFinalStepElement();
      const finalStepInsideHelper =
        finalStep && finalStep.parentNode === this.subscriptionSupportFinalContainer;
      if (finalStepInsideHelper) {
        if (resources) {
          this.setSubscriptionSupportFinalLabel(resources.infoLabel, resources.placeholder);
        }
      } else {
        this.subscriptionSupportFinalContainer.style.display = "none";
        this.resetSubscriptionSupportFinalLabel();
      }
    }
  }

  ensureWrongMissingItemHelper(step) {
    if (!step) {
      return null;
    }
    let helper = step.querySelector("[data-wrong-missing-item-helper]");
    if (helper) {
      this.wrongMissingItemHelper = helper;
      return helper;
    }

    helper = document.createElement("div");
    helper.className = "subscription-support-helper wrong-missing-item-helper";
    helper.dataset.wrongMissingItemHelper = "true";

    const createOptionColumn = (value, labelKey) => {
      const column = document.createElement("div");
      column.className = `subscription-support-helper__column wrong-missing-item-helper__column`;
      column.dataset.wrongMissingItemValue = value;
      column.setAttribute("role", "button");
      column.tabIndex = 0;

      const button = document.createElement("button");
      button.type = "button";
      button.className = "subscription-support-helper__button";
      button.textContent = this.tPlain(labelKey);
      column.appendChild(button);
      return column;
    };

    const incorrectColumn = createOptionColumn(
      "incorrect-item",
      "contact_form.steps.step_2.wrong_missing_item.option_incorrect",
    );
    const missingColumn = createOptionColumn(
      "missing-item",
      "contact_form.steps.step_2.wrong_missing_item.option_missing",
    );

    helper.appendChild(incorrectColumn);
    helper.appendChild(missingColumn);
    step.appendChild(helper);

    let hiddenField = step.querySelector("#wrong-missing-item-type");
    if (!hiddenField) {
      hiddenField = document.createElement("input");
      hiddenField.type = "hidden";
      hiddenField.id = "wrong-missing-item-type";
      hiddenField.name = "wrong-missing-item-type";
      step.appendChild(hiddenField);
    }
    if (hiddenField.dataset.changeHandlerAttached !== "true") {
      hiddenField.dataset.changeHandlerAttached = "true";
      hiddenField.addEventListener("change", () => {
        this.updateWrongMissingItemState();
      });
    }
    this.wrongMissingItemTypeField = hiddenField;
    this.wrongMissingItemHelper = helper;

    const handleSelection = (value) => {
      if (!this.wrongMissingItemTypeField) {
        return;
      }
      this.wrongMissingItemTypeField.value = value;
      this.updateWrongMissingItemState();
      this.loadStep(3, "wrong-missing-item");
    };

    const addInteractionHandlers = (column) => {
      column.onclick = (event) => {
        if (event.target.closest("button")) {
          return;
        }
        handleSelection(column.dataset.wrongMissingItemValue || "");
      };
      column.onkeydown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleSelection(column.dataset.wrongMissingItemValue || "");
        }
      };
      const button = column.querySelector("button");
      if (button) {
        button.onclick = () => {
          handleSelection(column.dataset.wrongMissingItemValue || "");
        };
      }
    };

    addInteractionHandlers(incorrectColumn);
    addInteractionHandlers(missingColumn);

    return helper;
  }

  updateWrongMissingItemStepCopy(step, selection) {
    if (!step) {
      return;
    }
    const titleEl = step.querySelector(".step-title");
    if (!titleEl) {
      return;
    }
    let titleKey = "contact_form.steps.step_3.wrong_missing_item.title";
    if (selection === "incorrect-item") {
      titleKey = "contact_form.steps.step_3.wrong_missing_item.title_incorrect";
    } else if (selection === "missing-item") {
      titleKey = "contact_form.steps.step_3.wrong_missing_item.title_missing";
    }
    titleEl.textContent = this.tPlain(titleKey);
  }

  updateWrongMissingItemFields(step, selection, options = {}) {
    if (!step) {
      return;
    }
    const { selectionChanged = false } = options;
    const nameField = step.querySelector("#wrong-missing-item-name");
    if (nameField) {
      const group = nameField.closest(".form-group");
      if (group) {
        if (selection === "missing-item") {
          group.style.display = "none";
          group.setAttribute("hidden", "hidden");
          if (nameField.required) {
            nameField.dataset.originalRequired = "true";
            nameField.required = false;
          }
          nameField.value = "";
        } else {
          group.style.display = "";
          group.removeAttribute("hidden");
          if (nameField.dataset.originalRequired === "true") {
            nameField.required = true;
          }
        }
      }
    }

    const wrapper = step.querySelector("[data-order-lookup-wrapper]");
    const resultsLabel = wrapper ? wrapper.querySelector(".order-lookup-results__label") : null;
    const missingLabel = this.tPlain(
      "contact_form.steps.step_3.wrong_missing_item.products_label_missing",
      "Select the missing item(s)",
    );
    const incorrectLabel = this.tPlain(
      "contact_form.steps.step_3.wrong_missing_item.products_label_incorrect",
      "Great we have found your order",
    );
    const defaultLabel = this.tPlain(
      "contact_form.order_lookup.products_label",
      "Select the products you need help with",
    );
    if (resultsLabel) {
      if (selection === "missing-item") {
        resultsLabel.textContent = missingLabel;
      } else if (selection === "incorrect-item") {
        resultsLabel.textContent = incorrectLabel;
      } else {
        resultsLabel.textContent = defaultLabel;
      }
    }

    if (wrapper) {
      const input = wrapper.querySelector('[data-order-lookup-input="true"]');
      if (input && this.orderLookupFieldConfigs[input.id]) {
        this.orderLookupFieldConfigs[input.id].disableProductSelection =
          selection === "incorrect-item";
      }
      if (selectionChanged) {
        this.clearOrderLookupResults(wrapper);
        wrapper.dataset.lastLookupKey = "";
        wrapper.dataset.selectedOrderNumber = "";
        wrapper.dataset.selectedOrderId = "";
      }
    }
  }

  updateWrongMissingItemState(step = null) {
    const selection = this.wrongMissingItemTypeField ? this.wrongMissingItemTypeField.value : "";
    const selectionChanged = selection !== this.wrongMissingItemSelection;
    this.wrongMissingItemSelection = selection;

    const stepTwo = this.findStepElement(2, "wrong-missing-item");
    if (stepTwo && (!step || stepTwo === step)) {
      this.ensureWrongMissingItemHelper(stepTwo);
    }

    const stepThree = this.findStepElement(3, "wrong-missing-item");
    if (stepThree && (!step || stepThree === step)) {
      this.updateWrongMissingItemStepCopy(stepThree, selection);
      this.updateWrongMissingItemFields(stepThree, selection, { selectionChanged });
    }
  }

  removeSubscriptionSupportHelper(step = null) {
    const helper =
      (step && step.querySelector("[data-subscription-support-helper]")) ||
      this.subscriptionSupportHelper;
    const finalWrapper =
      (step && step.querySelector("[data-subscription-support-final]")) ||
      this.subscriptionSupportFinalContainer;

    const finalStep = this.getFinalStepElement();
    const shouldResetFinalStep =
      !!finalStep &&
      ((helper && helper.contains(finalStep)) ||
        (finalWrapper && finalStep.parentNode === finalWrapper) ||
        (step && finalStep.parentNode === step));

    if (shouldResetFinalStep) {
      this.showFinalStep(null);
    }

    this.removeSubscriptionSupportOrderLookup();

    if (helper) {
      helper.remove();
    }
    if (finalWrapper) {
      this.restoreCancellationInnerStep();
      finalWrapper.style.display = "none";
      finalWrapper.remove();
    } else {
      this.restoreCancellationInnerStep();
    }

    this.subscriptionSupportHelper = null;
    this.subscriptionSupportFinalContainer = null;
    this.subscriptionSupportSelfButton = null;
    this.subscriptionSupportAssistButton = null;
    this.subscriptionSupportSelfColumn = null;
    this.subscriptionSupportAssistColumn = null;
    this.subscriptionSupportSelfSubtitle = null;
    this.subscriptionSupportAssistSubtitle = null;
    this.subscriptionSupportSelectedResources = null;
    this.subscriptionSupportSelection = "";
    this.setAdditionalInformationVisibility(true);
    this.resetSubscriptionSupportFinalLabel();
    this.applySubscriptionSupportOtherFinalState(false);

    const targetStep =
      step ||
      (this.subscriptionReasonSelector ? this.subscriptionReasonSelector.closest(".step") : null);
    if (targetStep) {
      this.setSubscriptionSupportNextButtonVisibility(targetStep, true);
    }
  }

  setSubscriptionSupportNextButtonVisibility(step, visible) {
    if (!step) {
      return;
    }
    const nextButton = step.querySelector(".button-container [data-go-to-step]");
    if (!nextButton) {
      return;
    }
    if (visible) {
      if (nextButton.dataset.subscriptionSupportOriginalDisplay !== undefined) {
        nextButton.style.display = nextButton.dataset.subscriptionSupportOriginalDisplay;
        delete nextButton.dataset.subscriptionSupportOriginalDisplay;
      } else {
        nextButton.style.display = "";
      }
      return;
    }
    if (nextButton.dataset.subscriptionSupportOriginalDisplay === undefined) {
      nextButton.dataset.subscriptionSupportOriginalDisplay = nextButton.style.display || "";
    }
    nextButton.style.display = "none";
  }

  showSubscriptionSupportFinal(step) {
    const resources = this.subscriptionSupportSelectedResources;
    if (!this.subscriptionSupportFinalContainer) {
      return;
    }
    this.subscriptionSupportFinalContainer.style.display = "flex";
    this.showFinalStep(this.subscriptionSupportFinalContainer, "subscription-support");
    this.prefillCustomerDetails();
    if (resources) {
      this.setSubscriptionSupportFinalLabel(resources.infoLabel, resources.placeholder);
    } else {
      this.resetSubscriptionSupportFinalLabel();
    }

    const selection = this.subscriptionReasonSelector ? this.subscriptionReasonSelector.value : "";
    if (selection === "cancel") {
      this.toggleAdditionalInformationVisibility(false);
      this.moveCancellationInnerStepToFinal(step);
    } else {
      this.toggleAdditionalInformationVisibility(true);
    }

    if (step) {
      this.setSubscriptionSupportNextButtonVisibility(step, false);
    }

    this.ensureSubscriptionSupportOrderLookup();
    this.updateSubscriptionSupportFinalLookupVisibility();

    requestAnimationFrame(() => {
      this.scrollIntoViewWithOffset(this.subscriptionSupportFinalContainer);
    });
  }

  ensureSubscriptionSupportOrderLookup() {
    const finalStep = this.getFinalStepElement();
    if (
      !finalStep ||
      !this.subscriptionSupportFinalContainer ||
      finalStep.parentElement !== this.subscriptionSupportFinalContainer
    ) {
      return null;
    }

    let wrapper = finalStep.querySelector('[data-subscription-support-order-lookup="true"]');
    if (wrapper) {
      if (!wrapper.dataset.orderLookupDisabled) {
        wrapper.dataset.orderLookupDisabled = "false";
      }
      return wrapper;
    }

    const field = {
      type: "orderLookup",
      inputType: "text",
      id: "subscription-support-order-lookup",
      name: "order-number",
      labelKey: "contact_form.order_lookup.label",
      placeholderKey: "contact_form.order_lookup.placeholder",
      minLength: 6,
      maxLength: 10,
      queryParam: "orderNumber",
      resultsName: "order-products",
      productsLabelText: "Your order contains the following products",
      disableProductSelection: true,
    };

    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = this.renderOrderLookupField(field).trim();
    wrapper = tempContainer.firstElementChild;
    if (!wrapper) {
      return null;
    }
    wrapper.dataset.subscriptionSupportOrderLookup = "true";
    wrapper.dataset.orderLookupDisabled = "false";

    const firstGroup = Array.from(finalStep.children).find(
      (child) => child.classList && child.classList.contains("form-group"),
    );
    if (firstGroup) {
      finalStep.insertBefore(wrapper, firstGroup);
    } else {
      finalStep.appendChild(wrapper);
    }

    this.setupOrderLookupFields();
    this.prefillCustomerDetails();
    this.resetOrderLookupWrapper(wrapper);
    return wrapper;
  }

  removeSubscriptionSupportOrderLookup() {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    const wrapper = finalStep.querySelector('[data-subscription-support-order-lookup="true"]');
    if (!wrapper) {
      return;
    }
    const numberInput = wrapper.querySelector('[data-order-lookup-input="true"]');
    if (numberInput) {
      this.abortOrderLookupRequest(numberInput);
    }
    wrapper.remove();
  }

  updateSubscriptionSupportFinalLookupVisibility() {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    const isSubscriptionSupportHelperFinal =
      this.finalStepSourceRoute === "subscription-support" &&
      this.subscriptionSupportFinalContainer &&
      finalStep.parentElement === this.subscriptionSupportFinalContainer;

    if (!isSubscriptionSupportHelperFinal) {
      this.removeSubscriptionSupportOrderLookup();
      return;
    }

    const lookupWrapper = this.ensureSubscriptionSupportOrderLookup();
    if (!lookupWrapper) {
      return;
    }

    const productsContainer = lookupWrapper.querySelector("[data-order-lookup-products]");
    const hasProducts = !!(productsContainer && productsContainer.children.length);
    const hasLookupSuccess = lookupWrapper.dataset.orderLookupSuccessful === "true" && hasProducts;

    if (!hasLookupSuccess) {
      Array.from(finalStep.children).forEach((child) => {
        if (child === lookupWrapper) {
          child.style.display = "";
          child.removeAttribute("hidden");
          return;
        }
        if (!child.classList) {
          return;
        }
        if (
          child.classList.contains("form-group") ||
          child.classList.contains("button-container")
        ) {
          child.style.display = "none";
          child.setAttribute("hidden", "hidden");
          this.toggleFieldRequiredState(child, false);
        }
      });
      const cancellationInnerStep = this.getCancellationInnerStep(
        this.subscriptionSupportFinalContainer,
      );
      if (cancellationInnerStep) {
        this.toggleFieldRequiredState(cancellationInnerStep, false);
        cancellationInnerStep.classList.remove("active");
        cancellationInnerStep.style.display = "none";
        cancellationInnerStep.setAttribute("hidden", "hidden");
      }
      return;
    }

    this.applyFinalStepCustomizations("subscription-support");
    const subscriptionStep = this.findFirstStepForRoute("subscription-support");
    if (subscriptionStep) {
      this.updateSubscriptionSupportHelper(subscriptionStep);
    }

    ["customer-name", "customer-email"].forEach((id) => {
      const field = finalStep.querySelector(`#${id}`);
      const group = field ? field.closest(".form-group") : null;
      if (!group) {
        return;
      }
      group.style.display = "none";
      group.setAttribute("hidden", "hidden");
      this.toggleFieldRequiredState(group, false);
    });
  }

  updateSubscriptionSupportOtherSubtitle(selectedValue) {
    if (!this.subscriptionSupportSelfColumn || !this.subscriptionSupportSelfButton) {
      return;
    }

    if (!this.subscriptionSupportSelfSubtitle) {
      const subtitle = document.createElement("div");
      subtitle.className = "small subscription-support-helper__subtitle";
      this.subscriptionSupportSelfButton.insertAdjacentElement("afterend", subtitle);
      this.subscriptionSupportSelfSubtitle = subtitle;
    }

    if (this.subscriptionSupportSelfSubtitleDefault === undefined) {
      this.subscriptionSupportSelfSubtitleDefault =
        this.subscriptionSupportSelfSubtitle.textContent || "";
    }

    const otherSubtitle = this.tPlain(
      "contact_form.subscription_support.other_self_service_subtitle",
      "We've created a detailed list of helpful advice if you'd like to try here first.",
    );
    const nextText =
      selectedValue === "other" ? otherSubtitle : this.subscriptionSupportSelfSubtitleDefault || "";
    this.subscriptionSupportSelfSubtitle.textContent = nextText;
    if (nextText) {
      this.subscriptionSupportSelfSubtitle.style.display = "";
    } else {
      this.subscriptionSupportSelfSubtitle.style.display = "none";
    }
  }

  applySubscriptionSupportOtherFinalState(enable) {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }

    const titleEl = finalStep.querySelector(".step-title");
    const subtitleEl = finalStep.querySelector(".step-subtitle");
    const backButton = finalStep.querySelector(".back-step");

    if (!this.subscriptionSupportOtherFinalDefaults) {
      this.subscriptionSupportOtherFinalDefaults = {
        backDisplay: backButton ? backButton.style.display || "" : "",
      };
    }

    const defaults = this.subscriptionSupportOtherFinalDefaults || {};

    if (enable) {
      finalStep.classList.add("subscription-support-final--other");
      if (titleEl) {
        titleEl.style.display = "none";
      }
      if (subtitleEl) {
        subtitleEl.style.display = "none";
      }
      if (backButton) {
        backButton.style.display = "none";
      }
      return;
    }

    finalStep.classList.remove("subscription-support-final--other");
    if (backButton) {
      backButton.style.display = defaults.backDisplay || "";
    }
    this.applyFinalStepCustomizations(this.finalStepSourceRoute || "");
  }

  setSubscriptionSupportFinalLabel(text, placeholder) {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }

    const label = finalStep.querySelector('label[for="additional-information"]');
    const textarea = finalStep.querySelector("#additional-information");
    if (label) {
      if (!this.subscriptionSupportOriginalInfoLabel) {
        this.subscriptionSupportOriginalInfoLabel = label.textContent.trim();
      }
      label.textContent = text || this.subscriptionSupportOriginalInfoLabel;
    }
    if (textarea) {
      if (this.subscriptionSupportOriginalInfoPlaceholder === undefined) {
        this.subscriptionSupportOriginalInfoPlaceholder =
          textarea.getAttribute("placeholder") || "";
      }
      if (placeholder) {
        textarea.setAttribute("placeholder", placeholder);
      }
    }
  }

  resetSubscriptionSupportFinalLabel() {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    const label = finalStep.querySelector('label[for="additional-information"]');
    const textarea = finalStep.querySelector("#additional-information");
    if (label && this.subscriptionSupportOriginalInfoLabel) {
      label.textContent = this.subscriptionSupportOriginalInfoLabel;
    }
    if (textarea && this.subscriptionSupportOriginalInfoPlaceholder !== undefined) {
      textarea.setAttribute("placeholder", this.subscriptionSupportOriginalInfoPlaceholder || "");
    }
  }

  getCancellationInnerStep(step = null) {
    const container = step || this.form;
    if (!container) {
      return null;
    }
    return container.querySelector('.inner-step[data-inner-step="cancellation"]');
  }

  moveCancellationInnerStepToFinal(step) {
    if (!this.subscriptionSupportFinalContainer) {
      return;
    }
    const finalStep = this.getFinalStepElement();
    const cancellationInnerStep =
      this.getCancellationInnerStep(step) ||
      this.getCancellationInnerStep(finalStep) ||
      this.getCancellationInnerStep(this.subscriptionSupportFinalContainer);
    if (!cancellationInnerStep) {
      return;
    }
    if (!this.cancellationInnerStepOriginalParent) {
      this.cancellationInnerStepOriginalParent = cancellationInnerStep.parentNode;
      this.cancellationInnerStepOriginalNextSibling = cancellationInnerStep.nextSibling;
    }

    const shouldInsertIntoFinalStep =
      !!finalStep && finalStep.parentNode === this.subscriptionSupportFinalContainer;
    const targetParent = shouldInsertIntoFinalStep
      ? finalStep
      : this.subscriptionSupportFinalContainer;
    let insertBeforeNode = null;

    if (shouldInsertIntoFinalStep) {
      const phoneField = finalStep.querySelector("#customer-phone");
      const phoneGroup = phoneField ? phoneField.closest(".form-group") : null;
      if (phoneGroup && phoneGroup.parentNode === finalStep) {
        insertBeforeNode = phoneGroup.nextSibling;
      } else {
        insertBeforeNode = finalStep.querySelector(".button-container");
      }
    }

    const isAlreadyInPosition =
      cancellationInnerStep.parentNode === targetParent &&
      cancellationInnerStep.nextSibling === (insertBeforeNode || null);

    if (!isAlreadyInPosition && insertBeforeNode !== cancellationInnerStep) {
      targetParent.insertBefore(cancellationInnerStep, insertBeforeNode || null);
    }

    cancellationInnerStep.classList.add("active");
    cancellationInnerStep.style.display = "";
    cancellationInnerStep.removeAttribute("hidden");
    this.toggleFieldRequiredState(cancellationInnerStep, true);
  }

  restoreCancellationInnerStep() {
    const cancellationInnerStep = this.getCancellationInnerStep();
    if (!cancellationInnerStep) {
      return;
    }
    if (
      this.cancellationInnerStepOriginalParent &&
      cancellationInnerStep.parentNode !== this.cancellationInnerStepOriginalParent
    ) {
      if (
        this.cancellationInnerStepOriginalNextSibling &&
        this.cancellationInnerStepOriginalNextSibling.parentNode ===
          this.cancellationInnerStepOriginalParent
      ) {
        this.cancellationInnerStepOriginalParent.insertBefore(
          cancellationInnerStep,
          this.cancellationInnerStepOriginalNextSibling,
        );
      } else {
        this.cancellationInnerStepOriginalParent.appendChild(cancellationInnerStep);
      }
    }
    cancellationInnerStep.classList.remove("active");
    cancellationInnerStep.style.display = "";
    cancellationInnerStep.removeAttribute("hidden");
    this.cancellationInnerStepOriginalParent = null;
    this.cancellationInnerStepOriginalNextSibling = null;
  }

  toggleAdditionalInformationVisibility(shouldShow) {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    const infoField = finalStep.querySelector("#additional-information");
    if (!infoField) {
      return;
    }
    const group = infoField.closest(".form-group");
    if (!group) {
      return;
    }
    if (!this.subscriptionSupportOriginalInfoGroup) {
      this.subscriptionSupportOriginalInfoGroup = group;
    }
    if (shouldShow) {
      group.style.display = "";
      group.removeAttribute("hidden");
      this.toggleFieldRequiredState(group, true);
    } else {
      group.style.display = "none";
      group.setAttribute("hidden", "hidden");
      this.toggleFieldRequiredState(group, false);
    }
  }

  setAdditionalInformationVisibility(shouldShow) {
    this.toggleAdditionalInformationVisibility(shouldShow);
  }

  handleOrderLookupIdentityChange(wrapper) {
    this.handleOrderLookupFieldChange(wrapper);
  }

  handleOrderLookupFieldChange(wrapper, options = {}) {
    if (!wrapper) {
      return;
    }
    const { keepOrders = false } = options;
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements) {
      return;
    }
    const numberInput = elements.numberInput;
    if (numberInput) {
      const config = this.orderLookupFieldConfigs[numberInput.id] || {};
      const maxLength = parseInt(config.maxLength || numberInput.dataset.maxLength || "0", 10) || 0;
      const normalisedValue = this.normaliseOrderNumber(numberInput.value);
      numberInput.value = maxLength ? normalisedValue.slice(0, maxLength) : normalisedValue;
      this.abortOrderLookupRequest(numberInput);
    }
    wrapper.dataset.lastLookupKey = "";
    wrapper.dataset.identityKey = "";
    this.setOrderLookupError(wrapper, "");
    this.hideOrderLookupStatus(wrapper);
    this.clearOrderLookupResults(wrapper, { keepOrders });
    this.orderTimingHelpButtonReady = false;
    this.orderAmendHelpButtonReady = false;
    this.updateOrderTimingHelpButtonVisibility();
    this.updateOrderLookupSubmitState(wrapper);
  }

  handleOrderLookupSubmit(wrapper) {
    if (!wrapper || wrapper.dataset.orderLookupDisabled === "true") {
      return;
    }
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.numberInput) {
      return;
    }
    const config = this.orderLookupFieldConfigs[elements.numberInput.id] || {};
    const minLength =
      parseInt(config.minLength || elements.numberInput.dataset.minLength || "0", 10) || 0;
    const maxLength =
      parseInt(config.maxLength || elements.numberInput.dataset.maxLength || "0", 10) || 0;
    const email = elements.emailInput ? elements.emailInput.value.trim() : "";
    const postcode = elements.postcodeInput ? elements.postcodeInput.value.trim() : "";
    const orderNumber = this.normaliseOrderNumber(elements.numberInput.value);
    if (!email || !postcode) {
      this.updateOrderLookupSubmitState(wrapper);
      return;
    }
    if (orderNumber && minLength && orderNumber.length < minLength) {
      this.updateOrderLookupSubmitState(wrapper);
      return;
    }
    if (orderNumber && maxLength && orderNumber.length > maxLength) {
      this.updateOrderLookupSubmitState(wrapper);
      return;
    }
    const submitCount = parseInt(wrapper.dataset.orderLookupSubmitCount || "0", 10) || 0;
    wrapper.dataset.orderLookupSubmitCount = String(submitCount + 1);
    this.setOrderLookupError(wrapper, "");
    this.hideOrderLookupStatus(wrapper);
    this.clearOrderLookupResults(wrapper, { keepOrders: true });
    this.orderTimingHelpButtonReady = false;
    this.orderAmendHelpButtonReady = false;
    this.updateOrderTimingHelpButtonVisibility();
    this.performOrderLookup(wrapper, {
      orderNumber,
      sourceInput: elements.numberInput,
      force: true,
    });
  }

  updateOrderLookupSubmitState(wrapper) {
    if (!wrapper) {
      return;
    }
    const button = wrapper.querySelector("[data-order-lookup-submit]");
    if (!button) {
      return;
    }
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.numberInput) {
      return;
    }
    const config = this.orderLookupFieldConfigs[elements.numberInput.id] || {};
    const minLength =
      parseInt(config.minLength || elements.numberInput.dataset.minLength || "0", 10) || 0;
    const maxLength =
      parseInt(config.maxLength || elements.numberInput.dataset.maxLength || "0", 10) || 0;
    const orderNumber = this.normaliseOrderNumber(elements.numberInput.value);
    const email = elements.emailInput ? elements.emailInput.value.trim() : "";
    const postcode = elements.postcodeInput ? elements.postcodeInput.value.trim() : "";
    let isOrderNumberValid = true;
    if (orderNumber) {
      if (minLength && orderNumber.length < minLength) {
        isOrderNumberValid = false;
      }
      if (maxLength && orderNumber.length > maxLength) {
        isOrderNumberValid = false;
      }
    }
    const shouldEnable = Boolean(email && postcode && isOrderNumberValid);
    const isLookupDisabled = wrapper.dataset.orderLookupDisabled === "true";
    const shouldDisable = !shouldEnable || isLookupDisabled;
    button.disabled = shouldDisable;
    button.setAttribute("aria-disabled", shouldDisable ? "true" : "false");

    const promptMessage = this.getOrderLookupPromptMessage();
    const errorEl = wrapper.querySelector("[data-order-lookup-error]");
    const currentMessage = errorEl ? (errorEl.textContent || "").trim() : "";
    const submitCount = parseInt(wrapper.dataset.orderLookupSubmitCount || "0", 10) || 0;
    const hasIdentity = Boolean(email && postcode);
    const shouldShowPrompt =
      !isLookupDisabled &&
      submitCount === 0 &&
      hasIdentity &&
      isOrderNumberValid &&
      wrapper.dataset.orderLookupState !== "loading" &&
      !this.isOrderLookupResolved(wrapper);

    if (shouldShowPrompt && (!currentMessage || currentMessage === promptMessage)) {
      this.setOrderLookupError(wrapper, promptMessage);
    } else if (!shouldShowPrompt && currentMessage === promptMessage) {
      this.setOrderLookupError(wrapper, "");
    }
  }

  setOrderLookupError(wrapper, message) {
    if (!wrapper) {
      return;
    }
    const errorEl = wrapper.querySelector("[data-order-lookup-error]");
    if (!errorEl) {
      return;
    }
    errorEl.textContent = message || "";
    errorEl.style.display = message ? "block" : "none";
  }

  getOrderAmendReturnProductsSelectionState(step = null) {
    let container = step;
    if (container && container.getAttribute("data-step") === "99" && container.parentElement) {
      container = container.parentElement;
    }
    if (container && !container.classList.contains("step")) {
      container = container.closest(".step");
    }
    const fallbackContainer = this.findFirstStepForRoute("order-amend");
    const findWrapper = (scope) =>
      scope ? scope.querySelector("[data-order-lookup-wrapper]") : null;
    let wrapper = findWrapper(container);
    if (!wrapper && fallbackContainer && fallbackContainer !== container) {
      wrapper = findWrapper(fallbackContainer);
    }
    if (!wrapper) {
      wrapper = findWrapper(this.form);
    }
    if (!wrapper) {
      return { wrapper: null, hasResults: false, hasSelection: false };
    }
    const checkboxes = Array.from(
      wrapper.querySelectorAll('input[data-order-product-checkbox="true"]'),
    );
    const hasResults = checkboxes.length > 0;
    const hasSelection = checkboxes.some((checkbox) => checkbox.checked);
    return { wrapper, hasResults, hasSelection };
  }

  validateOrderLookupProductSelections(step) {
    if (!step) {
      return true;
    }

    let isValid = true;
    const wrappers = Array.from(step.querySelectorAll("[data-order-lookup-wrapper]")).filter(
      (wrapper) => this.isElementVisibleForValidation(wrapper),
    );

    wrappers.forEach((wrapper) => {
      const checkboxes = Array.from(
        wrapper.querySelectorAll('input[data-order-product-checkbox="true"]'),
      );
      const selectableCheckboxes = checkboxes.filter((checkbox) => !checkbox.disabled);
      const hasSelectableProducts = selectableCheckboxes.length > 0;
      const productsContainer = wrapper.querySelector("[data-order-lookup-products]");
      const hasRenderedProducts = Boolean(productsContainer && productsContainer.children.length);
      const hasLookupSuccess =
        wrapper.dataset.orderLookupSuccessful === "true" && hasRenderedProducts;

      if (!hasSelectableProducts || !hasLookupSuccess) {
        this.setOrderLookupError(wrapper, "");
        return;
      }

      const hasSelection = selectableCheckboxes.some((checkbox) => checkbox.checked);
      if (!hasSelection) {
        this.setOrderLookupError(
          wrapper,
          this.tPlain(
            "contact_form.errors.order_lookup_select_product",
            "Please select at least one product.",
          ),
        );
        isValid = false;
        return;
      }

      this.setOrderLookupError(wrapper, "");
    });

    return isValid;
  }

  getOrderLookupRequiredMessage() {
    return this.tPlain(
      "contact_form.errors.order_lookup_required",
      "Please click Find order and select your order before submitting.",
    );
  }

  getOrderLookupPromptMessage() {
    return this.tPlain(
      "contact_form.order_lookup.prompt_find_order",
      "Please click Find order to look up your order.",
    );
  }

  getOrderLookupLoadingMessage() {
    return this.tPlain(
      "contact_form.errors.order_lookup_loading",
      "Please wait while we finish finding your order.",
    );
  }

  isOrderLookupDisplayNone(element) {
    if (!element) {
      return true;
    }
    if (element.style && element.style.display === "none") {
      return true;
    }
    return Boolean(window.getComputedStyle && window.getComputedStyle(element).display === "none");
  }

  areOrderLookupStatusAndResultsHidden(wrapper) {
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements) {
      return false;
    }
    const isStatusHidden = this.isOrderLookupDisplayNone(elements.statusContainer);
    const isResultsHidden = this.isOrderLookupDisplayNone(elements.resultsContainer);
    return isStatusHidden && isResultsHidden;
  }

  isOrderLookupResolved(wrapper) {
    if (!wrapper) {
      return false;
    }
    if (wrapper.dataset.orderLookupState === "loading") {
      return false;
    }
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.productsContainer || !elements.resultsContainer) {
      return false;
    }
    const hasRenderedProducts = Boolean(
      elements.productsContainer.querySelector(".order-lookup-product-option"),
    );
    const resultsVisible = elements.resultsContainer.style.display !== "none";
    return hasRenderedProducts && resultsVisible;
  }

  getOrderLookupWrappersForSubmitValidation() {
    if (!this.form) {
      return [];
    }
    const activeRoute = this.finalStepSourceRoute || this.activeRoute || "";
    return Array.from(this.form.querySelectorAll("[data-order-lookup-wrapper]")).filter(
      (wrapper) => {
        if (!wrapper || wrapper.dataset.orderLookupDisabled === "true") {
          return false;
        }

        const step = wrapper.closest(".step");
        if (!step) {
          return false;
        }
        const stepRoute = step.getAttribute("data-question-route") || "";
        if (activeRoute && stepRoute && stepRoute !== activeRoute) {
          return false;
        }
        const isVisibleStep =
          step.classList.contains("active") ||
          step.classList.contains("is-visible") ||
          step.classList.contains("completed");
        if (!isVisibleStep) {
          return false;
        }

        return true;
      },
    );
  }

  validateOrderLookupForSubmit() {
    const wrappers = this.getOrderLookupWrappersForSubmitValidation();
    if (!wrappers.length) {
      return true;
    }

    let isValid = true;
    wrappers.forEach((wrapper) => {
      if (wrapper.dataset.orderLookupState === "loading") {
        this.setOrderLookupError(wrapper, this.getOrderLookupLoadingMessage());
        isValid = false;
        return;
      }

      if (this.areOrderLookupStatusAndResultsHidden(wrapper)) {
        this.setOrderLookupError(wrapper, this.getOrderLookupPromptMessage());
        isValid = false;
        return;
      }

      if (this.isOrderLookupResolved(wrapper)) {
        this.setOrderLookupError(wrapper, "");
        return;
      }

      const step = wrapper.closest(".step");
      const stepRoute = step ? step.getAttribute("data-question-route") || "" : "";
      if (
        stepRoute === "order-timing" &&
        this.finalStepSourceRoute === "order-timing" &&
        this.orderTimingFallbackActive
      ) {
        this.setOrderLookupError(wrapper, "");
        return;
      }

      this.setOrderLookupError(wrapper, this.getOrderLookupRequiredMessage());
      isValid = false;
    });

    return isValid;
  }

  setupFieldListeners() {
    this.form.querySelectorAll("input, select, textarea").forEach((field) => {
      field.addEventListener("input", () => {
        field.classList.remove("error");
        const errorMessage = field.nextElementSibling;
        if (errorMessage && errorMessage.classList.contains("validation-error")) {
          errorMessage.textContent = "";
        }
      });
    });

    this.routeSelectors.forEach((radio) => {
      radio.addEventListener("change", () => {
        const radioError = this.querySelector(".radio-group-error");
        if (radioError) {
          radioError.remove();
        }
        this.routeSelectors.forEach((selector) => {
          const wrapper = selector.closest(".radio-wrapper");
          if (wrapper) {
            wrapper.classList.remove("error");
          }
        });
      });
    });
  }

  getResolvedCustomerEmail() {
    if (!this.form) {
      return (this.dataset.customerEmail || "").trim();
    }

    const prioritizedFields = [
      this.form.querySelector("#customer-email"),
      this.form.querySelector('input[name="customer-email"]'),
    ].filter(Boolean);

    for (let i = 0; i < prioritizedFields.length; i += 1) {
      const value = (prioritizedFields[i].value || "").trim();
      if (value && this.isValidEmail(value)) {
        return value;
      }
    }

    const candidateFields = Array.from(
      this.form.querySelectorAll(
        'input[data-order-lookup-email="true"], input[name$="-email"], input[type="email"]',
      ),
    );
    for (let i = 0; i < candidateFields.length; i += 1) {
      const value = (candidateFields[i].value || "").trim();
      if (value && this.isValidEmail(value)) {
        return value;
      }
    }

    const fallbackEmail = (this.dataset.customerEmail || "").trim();
    if (fallbackEmail && this.isValidEmail(fallbackEmail)) {
      return fallbackEmail;
    }
    return "";
  }

  trackKlaviyoFormInteractionIfReady() {
    if (!this.hasFormInteraction || this.hasTrackedFormInteraction) {
      return;
    }
    const email = this.getResolvedCustomerEmail();
    if (!email) {
      return;
    }
    const klaviyoClient = window.klaviyo;
    if (
      !klaviyoClient ||
      typeof klaviyoClient.identify !== "function" ||
      typeof klaviyoClient.track !== "function"
    ) {
      return;
    }

    klaviyoClient.identify({
      email,
      custom_forms_interacted: true,
    });
    klaviyoClient.track("Customer Form Interacted");
    this.hasTrackedFormInteraction = true;
  }

  setupKlaviyoInteractionTracking() {
    if (!this.form || this.klaviyoInteractionTrackingInitialized) {
      return;
    }
    this.klaviyoInteractionTrackingInitialized = true;

    const markInteraction = () => {
      this.hasFormInteraction = true;
      this.trackKlaviyoFormInteractionIfReady();
    };

    this.form.addEventListener("input", markInteraction);
    this.form.addEventListener("change", markInteraction);
    this.form.addEventListener("click", markInteraction);
  }

  setupRouteSelectors() {
    this.routeSelectors.forEach((selector) => {
      selector.addEventListener("click", (event) => {
        const radio = event.currentTarget;
        const goToStep = radio.dataset.goToStep;
        const route = radio.value;
        this.selectedStepOneOptionId = radio.id || "";
        if (this.form) {
          this.form.dataset.selectedIssue = this.selectedStepOneOptionId;
        }
        if (goToStep) {
          this.loadStep(parseInt(goToStep, 10), route);
        } else {
          this.loadRoute(route);
        }
        this.refreshConditionalSelectors();
      });
    });
  }

  setupStepButtons() {
    this.stepButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const trigger = event.currentTarget;
        const targetStep = trigger.getAttribute("data-go-to-step") || "1";
        const activeStep = this.getActiveStep();
        const currentRoute = activeStep ? activeStep.getAttribute("data-question-route") : null;
        const stepRouteOverride = trigger.getAttribute("data-step-route");
        if (!stepRouteOverride) {
          this.loadStep(targetStep, currentRoute);
        } else {
          this.loadStep(targetStep, currentRoute, stepRouteOverride);
        }
      });
    });
  }

  setupTypeSelectors() {
    const selectors = [this.foodIssueTypeSelector, this.dogUnwellTypeSelector].filter(Boolean);
    selectors.forEach((selector) => {
      selector.addEventListener("change", (event) => {
        this.updateConditionalInnerSteps(event.currentTarget);
      });
    });
  }

  setupRetailerTypeWatcher() {
    if (!this.retailerTypeSelector) {
      return;
    }
    this.retailerTypeSelector.addEventListener("change", () => {
      this.updateFoodIssueOrderLookupVisibility();
    });
    this.updateFoodIssueOrderLookupVisibility();
  }

  setupRetailerSubtitleNotices() {
    const retailerSelectors = Array.from(this.querySelectorAll('[data-retailer-select="true"]'));
    if (!retailerSelectors.length) {
      return;
    }
    const forthgladeValue = this.t("contact_form.food_issue.retailer.options.forthglade");
    retailerSelectors.forEach((selector) => {
      const wrapper = selector.closest(".form-group");
      if (!wrapper) {
        return;
      }
      const subtitle = wrapper.querySelector("[data-retailer-subtitle]");
      if (!subtitle) {
        return;
      }
      const toggleSubtitle = () => {
        const selectedValue = selector.value || "";
        if (selectedValue && selectedValue !== forthgladeValue) {
          subtitle.removeAttribute("hidden");
          return;
        }
        subtitle.setAttribute("hidden", "hidden");
      };
      selector.addEventListener("change", toggleSubtitle);
      toggleSubtitle();
    });
  }

  setupOrderLookupFields() {
    const fields = Array.from(this.querySelectorAll('[data-order-lookup-input="true"]'));
    if (!fields.length) {
      return;
    }
    this.orderLookupFields = fields;
    fields.forEach((input) => {
      if (input.dataset.lookupHandlerAttached === "true") {
        return;
      }
      const wrapper = input.closest("[data-order-lookup-wrapper]");
      if (!wrapper) {
        return;
      }
      if (!wrapper.dataset.orderLookupDisabled) {
        wrapper.dataset.orderLookupDisabled = "false";
      }
      input.dataset.lookupHandlerAttached = "true";
      const config = this.orderLookupFieldConfigs[input.id] || {};
      const maxLength = parseInt(config.maxLength || input.dataset.maxLength || "0", 10) || 0;
      if (maxLength) {
        input.maxLength = maxLength;
      }
      this.addNumericInputRestrictions(input, maxLength);
      input.addEventListener("input", () =>
        this.handleOrderLookupFieldChange(wrapper, { keepOrders: true }),
      );

      const { emailInput, postcodeInput } = this.getOrderLookupElements(wrapper) || {};
      const identityInputHandler = () => this.handleOrderLookupFieldChange(wrapper);
      if (emailInput && emailInput.dataset.identityListenerAttached !== "true") {
        emailInput.dataset.identityListenerAttached = "true";
        emailInput.addEventListener("input", identityInputHandler);
      }
      if (postcodeInput && postcodeInput.dataset.identityListenerAttached !== "true") {
        postcodeInput.dataset.identityListenerAttached = "true";
        postcodeInput.addEventListener("input", identityInputHandler);
      }

      const submitButton = wrapper.querySelector("[data-order-lookup-submit]");
      const enterSubmitInputs = [input, emailInput, postcodeInput].filter(Boolean);
      enterSubmitInputs.forEach((field) => {
        if (!submitButton || field.dataset.orderLookupEnterAttached === "true") {
          return;
        }
        field.dataset.orderLookupEnterAttached = "true";
        field.addEventListener("keydown", (event) => {
          if (event.key !== "Enter") {
            return;
          }
          event.preventDefault();
          if (!submitButton.disabled) {
            submitButton.click();
          }
        });
      });
      if (submitButton && submitButton.dataset.orderLookupSubmitAttached !== "true") {
        submitButton.dataset.orderLookupSubmitAttached = "true";
        submitButton.addEventListener("click", (event) => {
          event.preventDefault();
          this.handleOrderLookupSubmit(wrapper);
        });
      }
      this.updateOrderLookupSubmitState(wrapper);
    });
  }

  setupAmendReasonWatcher() {
    if (!this.amendReasonSelector) {
      return;
    }
    this.amendReasonSelector.addEventListener("change", () => {
      this.handleAmendReasonState();
    });
  }

  setupSubscriptionReasonWatcher() {
    if (this.subscriptionReasonSelector) {
      this.subscriptionReasonSelector.addEventListener("change", () => {
        this.handleSubscriptionReasonState();
      });
    }
    if (this.cancellationReasonSelector) {
      this.cancellationReasonSelector.addEventListener("change", () => {
        this.handleSubscriptionReasonState({ skipHelperUpdate: true });
      });
    }
  }

  setupWrongMissingItemWatcher() {
    if (!this.wrongMissingItemTypeField) {
      return;
    }
    this.wrongMissingItemTypeField.addEventListener("change", () => {
      this.updateWrongMissingItemState();
    });
  }

  setupDogUnwellOrderLookupVisibility() {
    const retailerSelectors = this.getDogUnwellRetailerSelectors();
    if (!retailerSelectors.length) {
      return;
    }
    retailerSelectors.forEach((selector) => {
      selector.addEventListener("change", () => {
        this.syncDogUnwellRetailerSelectors(selector.value, selector);
        this.updateDogUnwellOrderLookupVisibility();
      });
    });
    this.updateDogUnwellOrderLookupVisibility();
  }

  updateDogUnwellOrderLookupVisibility(step = null) {
    const retailerSelectors = this.getDogUnwellRetailerSelectors();
    if (!retailerSelectors.length) {
      return;
    }
    const activeStep = step || this.getActiveStep();
    const selected = this.getDogUnwellRetailerValue(activeStep);
    const orderLookupStep = this.findStepElement(2, "dog-unwell");
    const forthglade = this.t("contact_form.food_issue.retailer.options.forthglade");
    const hasSelection = Boolean((selected || "").trim());
    const shouldShow = hasSelection && selected === forthglade;
    if (hasSelection) {
      this.syncDogUnwellRetailerSelectors(selected);
    }

    const shouldUpdateRetailerStep = orderLookupStep && (!step || orderLookupStep === step);
    if (shouldUpdateRetailerStep && orderLookupStep.classList.contains("active")) {
      const innerStep = orderLookupStep.querySelector(
        '.inner-step[data-inner-step="order-lookup"]',
      );
      if (innerStep) {
        const wrapper = innerStep.querySelector("[data-order-lookup-wrapper]");
        const numberGroup = wrapper
          ? wrapper.querySelector("[data-order-lookup-number-group]")
          : null;
        const numberInput = wrapper
          ? wrapper.querySelector('[data-order-lookup-input="true"]')
          : null;
        const identityGroup = wrapper
          ? wrapper.querySelector("[data-order-lookup-identity-group]")
          : null;

        innerStep.classList.add("active");
        innerStep.style.display = "";
        innerStep.removeAttribute("hidden");

        if (wrapper) {
          if (shouldShow) {
            wrapper.style.display = "";
            wrapper.removeAttribute("hidden");
          } else {
            wrapper.style.display = "none";
            wrapper.setAttribute("hidden", "hidden");
          }
          wrapper.dataset.orderLookupDisabled = shouldShow ? "false" : "true";
        }

        if (identityGroup) {
          if (shouldShow) {
            identityGroup.style.display = "";
            identityGroup.removeAttribute("hidden");
            this.toggleFieldRequiredState(identityGroup, true);
          } else {
            this.toggleFieldRequiredState(identityGroup, false);
            this.clearInputs(identityGroup);
            identityGroup.style.display = "none";
            identityGroup.setAttribute("hidden", "hidden");
          }
        }

        if (shouldShow) {
          if (numberGroup) {
            numberGroup.style.display = "";
            numberGroup.removeAttribute("hidden");
          }
          if (numberInput && numberInput.dataset.originalRequired === "true") {
            numberInput.setAttribute("required", "");
          }
        } else {
          if (numberGroup) {
            numberGroup.style.display = "none";
            numberGroup.setAttribute("hidden", "hidden");
          }
          if (numberInput) {
            numberInput.value = "";
            numberInput.removeAttribute("required");
            this.abortOrderLookupRequest(numberInput);
          }
          if (wrapper) {
            this.clearOrderLookupResults(wrapper);
            wrapper.dataset.lastLookupKey = "";
            wrapper.dataset.selectedOrderNumber = "";
            wrapper.dataset.selectedOrderId = "";
          }
        }

        if (!shouldShow) {
          if (numberGroup) {
            this.clearInputs(numberGroup);
          }
          if (wrapper) {
            this.clearOrderLookupResults(wrapper);
            wrapper.dataset.lastLookupKey = "";
            wrapper.dataset.selectedOrderNumber = "";
            wrapper.dataset.selectedOrderId = "";
          }
        }
      }
    }

    const detailStep = orderLookupStep;
    if (detailStep && (!step || detailStep === step)) {
      const conditionalGroups = detailStep.querySelectorAll("[data-hide-for-retailer-value]");
      conditionalGroups.forEach((group) => {
        const hideForValue = group.getAttribute("data-hide-for-retailer-value");
        const shouldShowGroup = hasSelection && hideForValue !== selected;
        this.setRetailerConditionalFieldVisibility(group, shouldShowGroup);
      });
    }

    if (hasSelection) {
      this.updateDogUnwellFinalStepEmailOmission(shouldShow);
    } else {
      this.updateDogUnwellFinalStepEmailOmission(false);
    }
  }

  getDogUnwellRetailerSelectors() {
    return Array.from(this.querySelectorAll("#dog-unwell-retailer"));
  }

  getDogUnwellRetailerValue(preferredStep = null) {
    const selectors = this.getDogUnwellRetailerSelectors();
    if (preferredStep) {
      const stepSelector = preferredStep.querySelector("#dog-unwell-retailer");
      if (stepSelector && stepSelector.value) {
        return stepSelector.value;
      }
    }
    const selected = selectors.find((selector) => (selector.value || "").trim());
    return selected ? selected.value : "";
  }

  syncDogUnwellRetailerSelectors(value, source = null) {
    if (!value) {
      return;
    }
    this.getDogUnwellRetailerSelectors().forEach((selector) => {
      if (selector !== source && selector.value !== value) {
        selector.value = value;
      }
    });
  }

  updateDogUnwellFinalStepEmailOmission(shouldHideEmail) {
    if (!this.dogUnwellRetailerSelector) {
      return;
    }
    const stepEl = this.dogUnwellRetailerSelector.closest(".step");
    if (!stepEl) {
      return;
    }

    const omitValues = stepEl.dataset.omitFinalFields
      ? stepEl.dataset.omitFinalFields
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
      : [];
    const omitSet = new Set(omitValues);

    const identityFieldIds = ["customer-email", "customer-name"];
    identityFieldIds.forEach((fieldId) => {
      if (shouldHideEmail) {
        omitSet.add(fieldId);
      } else {
        omitSet.delete(fieldId);
      }
    });

    const updatedOmitValues = Array.from(omitSet);
    if (updatedOmitValues.length) {
      stepEl.dataset.omitFinalFields = updatedOmitValues.join(",");
    } else {
      delete stepEl.dataset.omitFinalFields;
    }

    const finalStep = this.getFinalStepElement();
    if (finalStep) {
      const routeToApply = this.activeRoute || this.finalStepSourceRoute || "";
      this.applyFinalStepCustomizations(routeToApply);
    }
  }

  setupCustomerDetailsMirroring() {
    if (!this.form) {
      return;
    }
    const sourceFields = {
      name: this.form.querySelector("#food-issue-customer-name"),
      email: this.form.querySelector("#food-issue-customer-email"),
      phone: this.form.querySelector("#food-issue-customer-phone"),
    };
    const targetFields = {
      name: this.form.querySelector("#customer-name"),
      email: this.form.querySelector("#customer-email"),
      phone: this.form.querySelector("#customer-phone"),
    };

    Object.entries(sourceFields).forEach(([key, source]) => {
      const target = targetFields[key];
      if (!source || !target) {
        return;
      }
      source.addEventListener("input", () => {
        target.value = source.value;
      });
    });
  }

  prepareFinalStep() {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    finalStep.classList.remove("active", "completed");
    finalStep.classList.add("final-step-inline");
    if (finalStep.parentNode) {
      finalStep.parentNode.removeChild(finalStep);
    }
    finalStep.style.display = "none";
  }

  showFinalStep(step, routeName = "") {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    if (step && routeName === "wrong-missing-item" && step.getAttribute("data-step") === "2") {
      if (finalStep.parentNode) {
        finalStep.parentNode.removeChild(finalStep);
      }
      finalStep.style.display = "none";
      finalStep.classList.remove("active", "is-visible");
      this.updateDirectFinalStepState(null, "");
      this.applyFinalStepCustomizations("");
      this.updateSubscriptionSupportFinalLookupVisibility();
      this.updateFoodIssuePostLookupVisibility();
      this.updateDogUnwellPostRetailerVisibility();
      return;
    }
    if (!step) {
      if (finalStep.parentNode) {
        finalStep.parentNode.removeChild(finalStep);
      }
      finalStep.style.display = "none";
      finalStep.classList.remove("active", "is-visible");
      this.updateDirectFinalStepState(null, "");
      this.applyFinalStepCustomizations("");
      this.updateSubscriptionSupportFinalLookupVisibility();
      this.updateFoodIssuePostLookupVisibility();
      this.updateDogUnwellPostRetailerVisibility();
      return;
    }

    this.updateDirectFinalStepState(step, routeName);

    if (finalStep.parentNode !== step) {
      if (finalStep.parentNode) {
        finalStep.parentNode.removeChild(finalStep);
      }
      step.appendChild(finalStep);
    }
    finalStep.style.display = "";
    finalStep.classList.add("is-visible", "active");
    finalStep.classList.remove("completed");
    this.applyFinalStepCustomizations(routeName);
    this.updateOrderAmendPostLookupVisibility(step);
    this.updateSubscriptionSupportFinalLookupVisibility();
    this.updateFoodIssuePostLookupVisibility(step);
    this.updateDogUnwellPostRetailerVisibility(step);
  }

  updateFoodIssueStepCopy() {
    const stepEl = this.findFirstStepForRoute("food-issue");
    if (!stepEl) {
      return;
    }

    const stepConfig = this.getStepConfigForRoute("food-issue");
    const defaultTitle = stepConfig && stepConfig.titleKey ? this.t(stepConfig.titleKey) : "";
    const defaultSubtitle =
      stepConfig && stepConfig.subtitleKey ? this.t(stepConfig.subtitleKey) : "";

    const selectedOption = this.getStepOneOptionById(this.selectedStepOneOptionId);
    const hasOverride = selectedOption && selectedOption.value === "food-issue";
    const titleKey = hasOverride ? selectedOption.stepTitleKey : null;
    const subtitleKey = hasOverride ? selectedOption.stepSubtitleKey : null;
    const resolvedTitle = titleKey ? this.tWithFallback(titleKey, defaultTitle) : defaultTitle;
    const resolvedSubtitle = subtitleKey
      ? this.tWithFallback(subtitleKey, defaultSubtitle)
      : defaultSubtitle;

    const titleEl = stepEl.querySelector(".step-title");
    if (titleEl) {
      titleEl.innerHTML = resolvedTitle || "";
    }
    const subtitleEl = stepEl.querySelector(".step-subtitle");
    if (subtitleEl) {
      subtitleEl.innerHTML = resolvedSubtitle || "";
    }
  }

  updateFoodIssueOrderLookupVisibility(step = null) {
    if (!this.retailerTypeSelector) {
      return;
    }
    const stepEl = this.retailerTypeSelector.closest(".step");
    if (!stepEl || (step && stepEl !== step)) {
      return;
    }

    if (!step && !stepEl.classList.contains("active")) {
      return;
    }

    const orderLookupInnerStep = stepEl.querySelector(
      '.inner-step[data-inner-step="order-lookup"]',
    );
    const forthgladeValue = this.t("contact_form.food_issue.retailer.options.forthglade");
    const selectedValue = this.retailerTypeSelector.value;
    const hasSelection = Boolean((selectedValue || "").trim());
    const shouldShowOrderLookup = hasSelection && selectedValue === forthgladeValue;

    if (orderLookupInnerStep) {
      if (shouldShowOrderLookup) {
        orderLookupInnerStep.classList.add("active");
        orderLookupInnerStep.style.display = "";
        orderLookupInnerStep.removeAttribute("hidden");
      } else {
        orderLookupInnerStep.classList.remove("active");
        orderLookupInnerStep.style.display = "none";
        orderLookupInnerStep.setAttribute("hidden", "hidden");
      }
    }

    const wrapper = orderLookupInnerStep
      ? orderLookupInnerStep.querySelector("[data-order-lookup-wrapper]")
      : null;
    const numberGroup = wrapper ? wrapper.querySelector("[data-order-lookup-number-group]") : null;
    const identityGroup = wrapper
      ? wrapper.querySelector("[data-order-lookup-identity-group]")
      : null;
    const numberInput = wrapper ? wrapper.querySelector('[data-order-lookup-input="true"]') : null;

    if (wrapper) {
      if (shouldShowOrderLookup) {
        wrapper.style.display = "";
        wrapper.removeAttribute("hidden");
      } else {
        wrapper.style.display = "none";
        wrapper.setAttribute("hidden", "hidden");
      }
      wrapper.dataset.orderLookupDisabled = shouldShowOrderLookup ? "false" : "true";
    }

    if (identityGroup) {
      if (shouldShowOrderLookup) {
        identityGroup.style.display = "";
        identityGroup.removeAttribute("hidden");
        this.toggleFieldRequiredState(identityGroup, true);
      } else {
        this.toggleFieldRequiredState(identityGroup, false);
        this.clearInputs(identityGroup);
        identityGroup.style.display = "none";
        identityGroup.setAttribute("hidden", "hidden");
      }
    }

    if (shouldShowOrderLookup) {
      if (numberGroup) {
        numberGroup.style.display = "";
        numberGroup.removeAttribute("hidden");
      }
      if (numberInput && numberInput.dataset.originalRequired === "true") {
        numberInput.setAttribute("required", "");
      }
    } else {
      if (numberGroup) {
        numberGroup.style.display = "none";
        numberGroup.setAttribute("hidden", "hidden");
      }
      if (numberInput) {
        numberInput.value = "";
        numberInput.removeAttribute("required");
        this.abortOrderLookupRequest(numberInput);
      }
      if (wrapper) {
        this.clearOrderLookupResults(wrapper);
        wrapper.dataset.lastLookupKey = "";
        wrapper.dataset.selectedOrderNumber = "";
        wrapper.dataset.selectedOrderId = "";
      }
    }

    if (!shouldShowOrderLookup) {
      if (numberGroup) {
        this.clearInputs(numberGroup);
      }
      if (wrapper) {
        this.clearOrderLookupResults(wrapper);
        wrapper.dataset.lastLookupKey = "";
        wrapper.dataset.selectedOrderNumber = "";
        wrapper.dataset.selectedOrderId = "";
      }
    }

    const conditionalGroups = stepEl.querySelectorAll("[data-hide-for-retailer-value]");
    conditionalGroups.forEach((group) => {
      const hideForValue = group.getAttribute("data-hide-for-retailer-value");
      const shouldShowGroup = hasSelection && hideForValue !== selectedValue;
      this.setRetailerConditionalFieldVisibility(group, shouldShowGroup);
    });

    this.updateFoodIssueFinalStepIdentityOmission(shouldShowOrderLookup);
  }

  updateFoodIssueFinalStepIdentityOmission(shouldHideIdentityFields) {
    if (!this.retailerTypeSelector) {
      return;
    }
    const stepEl = this.retailerTypeSelector.closest(".step");
    if (!stepEl) {
      return;
    }

    const omitValues = stepEl.dataset.omitFinalFields
      ? stepEl.dataset.omitFinalFields
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
      : [];
    const omitSet = new Set(omitValues);

    const identityFieldIds = ["customer-email", "customer-name"];
    identityFieldIds.forEach((fieldId) => {
      if (shouldHideIdentityFields) {
        omitSet.add(fieldId);
      } else {
        omitSet.delete(fieldId);
      }
    });

    const updatedOmitValues = Array.from(omitSet);
    if (updatedOmitValues.length) {
      stepEl.dataset.omitFinalFields = updatedOmitValues.join(",");
    } else {
      delete stepEl.dataset.omitFinalFields;
    }

    const finalStep = this.getFinalStepElement();
    if (finalStep) {
      this.applyFinalStepCustomizations("food-issue");
    }
  }

  setRetailerConditionalFieldVisibility(container, shouldShow) {
    if (!container) {
      return;
    }
    const isInnerStep = container.classList.contains("inner-step");

    if (shouldShow) {
      container.classList.remove("is-hidden");
      container.removeAttribute("hidden");
      if (isInnerStep) {
        container.style.display = "";
      }
      this.toggleFieldRequiredState(container, true);
      return;
    }

    this.toggleFieldRequiredState(container, false);
    this.clearInputs(container);
    if (isInnerStep) {
      container.classList.remove("active");
      container.style.display = "none";
    }
    container.classList.add("is-hidden");
    container.setAttribute("hidden", "hidden");
  }

  toggleFieldRequiredState(container, enable) {
    container.querySelectorAll("input, select, textarea").forEach((field) => {
      if (field.dataset.originalRequired === "true") {
        if (enable) {
          field.setAttribute("required", "");
        } else if (field.dataset.originalRequired !== "always-visible") {
          field.removeAttribute("required");
        }
      }
    });
  }

  decodeHtmlEntities(value) {
    if (typeof value !== "string" || value.indexOf("&") === -1) {
      return value;
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }

  isDirectFinalStepRoute(route = "") {
    return route === "pr-marketing" || route === "other" || route === "need-advice";
  }

  shouldShowFinalStepTitles(route = "") {
    return this.isDirectFinalStepRoute(route);
  }

  getSelectedIssueRoute() {
    const checkedIssue = this.form
      ? this.form.querySelector('input[name="current_issue"]:checked')
      : null;
    return (
      (checkedIssue && checkedIssue.value) || this.activeRoute || this.finalStepSourceRoute || ""
    );
  }

  updateDirectFinalStepState(step = null, route = "") {
    if (!this.stepOne) {
      return;
    }
    const shouldEnable = step === this.stepOne && this.isDirectFinalStepRoute(route);
    if (shouldEnable) {
      this.stepOne.classList.add("step--direct-final");
    } else {
      this.stepOne.classList.remove("step--direct-final");
    }
  }

  applyFinalStepCustomizations(route = undefined) {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    if (route !== undefined) {
      this.finalStepSourceRoute = route || "";
    }
    const sourceRoute = this.finalStepSourceRoute || "";
    const hasRoute = Boolean(sourceRoute);
    if (sourceRoute !== "order-timing") {
      this.orderTimingHelpButtonReady = false;
    }
    const finalConfig = this.getFinalStepConfig(sourceRoute);
    const parentStep =
      finalStep.parentElement && finalStep.parentElement.classList.contains("step")
        ? finalStep.parentElement
        : null;
    const titleEl = finalStep.querySelector(".step-title");
    const subtitleEl = finalStep.querySelector(".step-subtitle");
    const shouldShowTitles = this.shouldShowFinalStepTitles(sourceRoute);
    if (titleEl) {
      if (this.finalStepOriginalTitleDisplay === null) {
        this.finalStepOriginalTitleDisplay = titleEl.style.display || "";
      }
      const showTitle = shouldShowTitles && !finalConfig.hideFinalTitle;
      titleEl.style.display = showTitle ? this.finalStepOriginalTitleDisplay : "none";
    }
    if (subtitleEl) {
      if (this.finalStepOriginalSubtitleDisplay === null) {
        this.finalStepOriginalSubtitleDisplay = subtitleEl.style.display || "";
      }
      const defaultSubtitleKey = "contact_form.steps.step_99.subtitle_html";
      const subtitleKey = finalConfig.finalStepSubtitleKey || defaultSubtitleKey;
      const defaultSubtitle = this.t(defaultSubtitleKey);
      const resolvedSubtitle = this.t(subtitleKey, defaultSubtitle);
      subtitleEl.innerHTML = resolvedSubtitle;
      const showSubtitle = shouldShowTitles && !finalConfig.hideFinalSubtitle;
      subtitleEl.style.display = showSubtitle ? this.finalStepOriginalSubtitleDisplay : "none";
    }

    if (sourceRoute === "order-timing") {
      this.orderTimingFallbackActive = false;
      this.ensureOrderTimingHelpButton(finalStep);
      this.applyOrderTimingOptionalFields(finalStep, false);
    } else {
      this.orderTimingFallbackActive = false;
      this.applyOrderTimingOptionalFields(finalStep, true);
    }
    this.updateOrderTimingHelpButtonVisibility();
    this.updateOrderTimingSubmitVisibility();
    const omitIds =
      parentStep && parentStep.dataset.omitFinalFields
        ? parentStep.dataset.omitFinalFields
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean)
        : [];
    const omitSet = new Set(omitIds);
    if (sourceRoute === "order-amend") {
      const selectedValue = this.amendReasonSelector ? this.amendReasonSelector.value : "";
      if (selectedValue === "return-products") {
        const returnState = this.getOrderAmendReturnProductsSelectionState(finalStep);
        if (returnState.hasResults && returnState.hasSelection) {
          omitSet.add("additional-information");
        }
      }
    }

    if (hasRoute) {
      finalStep.setAttribute("data-source-route", sourceRoute);
      finalStep.setAttribute("data-question-route", sourceRoute);
      finalStep.setAttribute("data-step", String(finalConfig.step));
    } else {
      finalStep.removeAttribute("data-source-route");
      finalStep.removeAttribute("data-question-route");
      finalStep.setAttribute("data-step", "99");
    }

    const backButton = finalStep.querySelector(".back-step");
    if (backButton) {
      const backStep = hasRoute ? finalConfig.backStep : 1;
      backButton.setAttribute("data-go-to-step", String(backStep));
      if (hasRoute && backStep !== 1) {
        backButton.setAttribute("data-step-route", sourceRoute);
      } else {
        backButton.removeAttribute("data-step-route");
      }
    }

    let shouldHideFileUpload = Boolean(finalConfig.hideFileUpload);
    if (sourceRoute === "subscription-support") {
      const selection = this.subscriptionReasonSelector
        ? this.subscriptionReasonSelector.value
        : "";
      shouldHideFileUpload = ["reschedule-delivery", "add-remove-products", "cancel"].includes(
        selection,
      );
    }
    const fileUploadLabelKey =
      finalConfig.fileUploadLabelKey || "contact_form.steps.step_99.groups.image.label_html";
    const defaultFileUploadLabel = this.t("contact_form.steps.step_99.groups.image.label_html");
    const resolvedFileUploadLabel = this.t(fileUploadLabelKey, defaultFileUploadLabel);

    const fileUploadInput = finalStep.querySelector("#product-image-input");
    if (fileUploadInput) {
      const fileUploadGroup = fileUploadInput.closest(".form-group");
      if (fileUploadGroup) {
        const fileUploadLabel = fileUploadGroup.querySelector(".label-title");
        if (fileUploadLabel) {
          fileUploadLabel.innerHTML = resolvedFileUploadLabel;
        }
        if (shouldHideFileUpload) {
          fileUploadGroup.style.display = "none";
          fileUploadGroup.setAttribute("hidden", "hidden");
          this.clearProductImagesState();
          this.toggleFieldRequiredState(fileUploadGroup, false);
        } else {
          fileUploadGroup.style.display = "";
          fileUploadGroup.removeAttribute("hidden");
          this.toggleFieldRequiredState(fileUploadGroup, true);
        }
      }
    }

    const infoField = finalStep.querySelector("#additional-information");
    if (infoField) {
      const infoGroup = infoField.closest(".form-group");
      if (infoGroup) {
        this.subscriptionSupportOriginalInfoGroup =
          this.subscriptionSupportOriginalInfoGroup || infoGroup;

        const labelEl = infoGroup.querySelector(".label-title");
        const originalLabel =
          labelEl && labelEl.textContent
            ? labelEl.textContent.trim()
            : this.subscriptionSupportOriginalInfoLabel || "";
        if (!this.subscriptionSupportOriginalInfoLabel && originalLabel) {
          this.subscriptionSupportOriginalInfoLabel = originalLabel;
        }
        if (
          this.subscriptionSupportOriginalInfoPlaceholder === undefined &&
          typeof this.subscriptionSupportOriginalInfoPlaceholder !== "string"
        ) {
          this.subscriptionSupportOriginalInfoPlaceholder =
            infoField.getAttribute("placeholder") || "";
        }
        const infoLabelKey = finalConfig.finalStepInfoLabelKey || null;
        const infoPlaceholderKey = finalConfig.finalStepInfoPlaceholderKey || null;
        const resolvedLabel = infoLabelKey
          ? this.t(infoLabelKey, this.subscriptionSupportOriginalInfoLabel || originalLabel)
          : this.subscriptionSupportOriginalInfoLabel || originalLabel;
        if (labelEl && resolvedLabel) {
          labelEl.textContent = this.decodeHtmlEntities(resolvedLabel);
        }
        const resolvedPlaceholder = infoPlaceholderKey
          ? this.t(
              infoPlaceholderKey,
              typeof this.subscriptionSupportOriginalInfoPlaceholder === "string"
                ? this.subscriptionSupportOriginalInfoPlaceholder
                : infoField.getAttribute("placeholder") || "",
            )
          : typeof this.subscriptionSupportOriginalInfoPlaceholder === "string"
            ? this.subscriptionSupportOriginalInfoPlaceholder
            : infoField.getAttribute("placeholder") || "";
        if (resolvedPlaceholder !== undefined && resolvedPlaceholder !== null) {
          infoField.setAttribute("placeholder", this.decodeHtmlEntities(resolvedPlaceholder));
        }
      }
    }

    this.applyOrderAmendAdditionalInfoOverrides();

    const finalFieldGroups = finalStep.querySelectorAll(".form-group");
    finalFieldGroups.forEach((group) => {
      const field = group.querySelector("input, select, textarea");
      if (!field) {
        return;
      }

      if (
        sourceRoute === "order-timing" &&
        group.dataset.orderTimingOptional === "true" &&
        this.orderTimingOptionalVisible === false
      ) {
        this.toggleFieldRequiredState(group, false);
        group.style.display = "none";
        group.setAttribute("hidden", "hidden");
        return;
      }

      if (field.id === "product-image-input" && shouldHideFileUpload) {
        group.style.display = "none";
        group.setAttribute("hidden", "hidden");
        this.toggleFieldRequiredState(group, false);
        return;
      }

      const shouldHideField = omitSet.has(field.id);
      if (shouldHideField) {
        this.toggleFieldRequiredState(group, false);
        group.style.display = "none";
        group.setAttribute("hidden", "hidden");
      } else {
        group.style.display = "";
        group.removeAttribute("hidden");
        this.toggleFieldRequiredState(group, true);
      }
    });
  }

  handleOrderLookupInput(input, wrapper, options = {}) {
    if (wrapper && wrapper.dataset.orderLookupDisabled === "true") {
      return;
    }
    const { debounce = false, immediate = false } = options;
    const config = this.orderLookupFieldConfigs[input.id] || {};
    const defaultLength =
      config.maxLength ||
      input.dataset.maxLength ||
      config.minLength ||
      input.dataset.minLength ||
      "0";
    const minLength =
      parseInt(config.minLength || input.dataset.minLength || defaultLength, 10) || 0;
    const maxLength = parseInt(config.maxLength || input.dataset.maxLength || "0", 10) || 0;

    let value = (input.value || "").replace(/[^0-9]/g, "");
    if (maxLength) {
      value = value.slice(0, maxLength);
    }
    if (input.value !== value) {
      input.value = value;
    }

    if (value && minLength && value.length < minLength) {
      // this.showOrderLookupStatus(wrapper, this.t('contact_form.order_lookup.status_invalid'), 'info');
      this.clearOrderLookupResults(wrapper, { keepOrders: true });
      this.abortOrderLookupRequest(input);
      this.orderTimingHelpButtonReady = false;
      this.updateOrderTimingHelpButtonVisibility();
      return;
    }

    const triggerFetch = () => {
      this.performOrderLookup(wrapper, { orderNumber: value, sourceInput: input, force: true });
    };

    if (!value) {
      this.abortOrderLookupRequest(input);
      this.performOrderLookup(wrapper, { orderNumber: "", sourceInput: input, force: immediate });
      this.orderTimingHelpButtonReady = false;
      this.updateOrderTimingHelpButtonVisibility();
      return;
    }

    this.hideOrderLookupStatus(wrapper);
    this.clearOrderLookupResults(wrapper, { keepOrders: true });

    if (immediate) {
      this.scheduleOrderLookupFetch(input, triggerFetch, 0);
    } else if (debounce) {
      this.scheduleOrderLookupFetch(input, triggerFetch, 400);
    } else {
      triggerFetch();
    }
  }

  scheduleOrderLookupFetch(input, callback, delay = 0) {
    if (!this.orderLookupTimers) {
      this.orderLookupTimers = new Map();
    }
    if (this.orderLookupTimers.has(input)) {
      clearTimeout(this.orderLookupTimers.get(input));
    }
    if (delay > 0) {
      const timer = setTimeout(() => {
        this.orderLookupTimers.delete(input);
        callback();
      }, delay);
      this.orderLookupTimers.set(input, timer);
    } else {
      this.orderLookupTimers.delete(input);
      callback();
    }
  }

  abortOrderLookupRequest(input) {
    if (this.orderLookupTimers && this.orderLookupTimers.has(input)) {
      clearTimeout(this.orderLookupTimers.get(input));
      this.orderLookupTimers.delete(input);
    }
    if (this.orderLookupControllers && this.orderLookupControllers.has(input)) {
      this.orderLookupControllers.get(input).abort();
      this.orderLookupControllers.delete(input);
    }
  }

  performOrderLookup(wrapper, options = {}) {
    const { orderNumber = undefined, sourceInput = null, force = false } = options;
    if (wrapper && wrapper.dataset.orderLookupDisabled === "true") {
      return;
    }
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements) {
      return;
    }
    const numberInput = sourceInput || elements.numberInput;
    if (!numberInput) {
      return;
    }
    const config = this.orderLookupFieldConfigs[numberInput.id] || {};
    const email = elements.emailInput ? elements.emailInput.value.trim() : "";
    const postcode = elements.postcodeInput ? elements.postcodeInput.value.trim() : "";
    const trimmedOrderNumber =
      orderNumber !== undefined
        ? orderNumber
        : elements.numberInput
          ? elements.numberInput.value.trim()
          : "";
    const normalisedOrderNumber = this.normaliseOrderNumber(trimmedOrderNumber);

    // if (!email || !postcode) {
    //   this.showOrderLookupStatus(wrapper, this.t('contact_form.order_lookup.status_identity_required', 'Please enter your email and postcode'), 'info');
    //   this.clearOrderLookupResults(wrapper);
    //   return;
    // }

    wrapper.dataset.identityKey = `${email.toLowerCase()}|${postcode.toLowerCase()}`;
    if (!normalisedOrderNumber) {
      wrapper.dataset.selectedOrderNumber = "";
      wrapper.dataset.selectedOrderId = "";
      wrapper.dataset.selectedCustomerName = "";
    }

    const requestKey = `${email.toLowerCase()}|${postcode.toLowerCase()}|${normalisedOrderNumber}`;
    if (!force && wrapper.dataset.lastLookupKey === requestKey) {
      return;
    }
    wrapper.dataset.lastLookupKey = requestKey;

    this.fetchOrderLookupData(numberInput, wrapper, config, {
      email,
      postcode,
      orderNumber: normalisedOrderNumber,
    });
  }

  fetchOrderLookupData(input, wrapper, config, values) {
    const { email, postcode, orderNumber } = values;
    const trimmedOrderNumber = (orderNumber || "").trim();
    const endpoint = config.endpoint || this.orderLookupEndpoint;

    wrapper.dataset.orderLookupSuccessful = "false";
    this.updateOrderAmendPostLookupVisibility(wrapper);
    this.updateSubscriptionSupportFinalLookupVisibility();
    this.updateFoodIssuePostLookupVisibility();
    this.updateDogUnwellPostRetailerVisibility();

    if (!endpoint) {
      this.showOrderLookupStatus(
        wrapper,
        this.t("contact_form.order_lookup.status_missing_endpoint"),
        "error",
      );
      return;
    }

    this.abortOrderLookupRequest(input);

    let url;
    try {
      url = new URL(endpoint, window.location.origin);
    } catch (error) {
      try {
        url = new URL(`${window.location.origin}${endpoint}`);
      } catch (err) {
        this.showOrderLookupStatus(
          wrapper,
          this.t("contact_form.order_lookup.status_error"),
          "error",
        );
        return;
      }
    }

    const controller = new AbortController();
    this.orderLookupControllers.set(input, controller);

    const payload = {
      email,
      postcode,
    };
    if (trimmedOrderNumber) {
      payload.order_name = trimmedOrderNumber.startsWith("#")
        ? trimmedOrderNumber
        : `#${trimmedOrderNumber}`;
    }

    const loadingKey = trimmedOrderNumber
      ? "contact_form.order_lookup.status_loading_products"
      : "contact_form.order_lookup.status_loading_orders";
    const loadingFallback = trimmedOrderNumber ? "Fetching products..." : "Fetching your orders...";
    this.showOrderLookupStatus(wrapper, this.t(loadingKey, loadingFallback), "loading");
    wrapper.dataset.orderLookupState = "loading";

    fetch(url.toString(), {
      method: "POST",
      headers: {
        // Accept: 'application/json',
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      redirect: "follow",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("lookup_failed");
        }
        return response.json();
      })
      .then((data) => {
        if (this.orderLookupControllers.get(input) === controller) {
          this.orderLookupControllers.delete(input);
        }
        this.handleDhlTrackingLookup(data, wrapper);
        const { products, orders } = this.parseOrderLookupResponse(data);
        const lookupCustomerName = this.getOrderLookupCustomerName(data, products);
        wrapper.dataset.selectedCustomerName = lookupCustomerName || "";
        const selectedOrderId = this.getOrderLookupSelectedOrderId(data, products);
        if (!trimmedOrderNumber && orders.length) {
          this.hideOrderLookupStatus(wrapper);
          this.renderOrderLookupOrders(wrapper, config, orders);
          wrapper.dataset.selectedOrderId = "";
          wrapper.dataset.orderLookupSuccessful = "false";
          this.orderAmendHelpButtonReady = false;
          this.updateOrderAmendPostLookupVisibility(wrapper);
          this.updateSubscriptionSupportFinalLookupVisibility();
          this.updateFoodIssuePostLookupVisibility();
          this.updateDogUnwellPostRetailerVisibility();
          return;
        }
        if (products.length) {
          this.hideOrderLookupStatus(wrapper);
          if (trimmedOrderNumber) {
            wrapper.dataset.selectedOrderNumber = trimmedOrderNumber;
          }
          wrapper.dataset.selectedOrderId = selectedOrderId;
          wrapper.dataset.orderLookupSuccessful = "true";
          this.renderOrderLookupProducts(wrapper, config, products);
          this.updateOrderLookupRecentNotice(wrapper, data);
          this.markOrderTimingLookupComplete(wrapper, true);
          this.markOrderAmendLookupComplete(wrapper, true);
          this.updateOrderAmendPostLookupVisibility(wrapper);
          this.updateSubscriptionSupportFinalLookupVisibility();
          this.updateFoodIssuePostLookupVisibility();
          this.updateDogUnwellPostRetailerVisibility();
          return;
        }
        wrapper.dataset.orderLookupSuccessful = "false";
        this.showOrderLookupStatus(
          wrapper,
          this.t("contact_form.order_lookup.status_empty"),
          "info",
        );
        this.clearOrderLookupResults(wrapper, { keepOrders: Boolean(trimmedOrderNumber) });
        this.markOrderTimingLookupComplete(wrapper);
        this.markOrderAmendLookupComplete(wrapper);
        this.updateOrderAmendPostLookupVisibility(wrapper);
        this.updateSubscriptionSupportFinalLookupVisibility();
        this.updateFoodIssuePostLookupVisibility();
        this.updateDogUnwellPostRetailerVisibility();
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }
        wrapper.dataset.orderLookupSuccessful = "false";
        this.clearOrderLookupTracking(wrapper);
        this.showOrderLookupStatus(
          wrapper,
          this.t("contact_form.order_lookup.status_error"),
          "error",
        );
        this.clearOrderLookupResults(wrapper, {
          keepOrders: Boolean(trimmedOrderNumber || wrapper.dataset.availableOrders),
        });
        this.markOrderTimingLookupComplete(wrapper);
        this.markOrderAmendLookupComplete(wrapper);
        this.updateOrderAmendPostLookupVisibility(wrapper);
        this.updateSubscriptionSupportFinalLookupVisibility();
        this.updateFoodIssuePostLookupVisibility();
        this.updateDogUnwellPostRetailerVisibility();
      })
      .finally(() => {
        if (this.orderLookupControllers.get(input) === controller) {
          this.orderLookupControllers.delete(input);
        }
        wrapper.dataset.orderLookupState = "ready";
      });
  }

  normaliseOrderNumber(value) {
    return (value || "").toString().replace(/[^0-9]/g, "");
  }

  parseOrderLookupResponse(data) {
    const products = this.parseOrderLookupProducts(data);
    const orders = this.parseOrderLookupOrders(data);
    return { products, orders };
  }

  parseOrderLookupProducts(data) {
    let products = [];
    let orderMeta = null;
    if (Array.isArray(data)) {
      products = data;
    } else if (data && Array.isArray(data.products)) {
      products = data.products;
    } else if (data && data.order && Array.isArray(data.order.line_items)) {
      const order = data.order || {};
      orderMeta = {
        id: order.id,
        name: order.name || order.order_number || order.number || "",
        reference: order.order_number || order.name || order.number || "",
        customer_name: order.customer_name || order.customerName || "",
      };
      products = data.order.line_items.map((item) => {
        const normalisedItem = {
          ...item,
          title: item.product_title || item.title || item.name || "",
          variant_title: item.variant_title || item.variant || item.option || "",
          quantity: item.quantity || item.qty || 1,
        };
        if (
          !normalisedItem.product_image &&
          normalisedItem.image &&
          typeof normalisedItem.image === "string"
        ) {
          normalisedItem.product_image = normalisedItem.image;
        } else if (
          !normalisedItem.product_image &&
          normalisedItem.image &&
          typeof normalisedItem.image === "object"
        ) {
          normalisedItem.product_image = normalisedItem.image.src || normalisedItem.image.url || "";
        }
        return normalisedItem;
      });
    }
    return products
      .map((item, index) => {
        let id = item && (item.id || item.variant_id || item.product_id || item.sku || item.code);
        if (id === undefined || id === null) {
          id = `product-${index + 1}`;
        }
        const rawItem = item && item.raw ? item.raw : item;
        const rawWithMeta =
          orderMeta && rawItem && typeof rawItem === "object"
            ? { ...rawItem, order: orderMeta }
            : rawItem;
        return {
          id: String(id),
          label: this.composeProductLabel(item, index),
          image: this.extractProductImage(item),
          raw: rawWithMeta,
        };
      })
      .filter((product) => product.label);
  }

  parseOrderLookupOrders(data) {
    let orders = [];
    if (data) {
      if (Array.isArray(data.orders)) {
        orders = data.orders;
      } else if (data.orders && typeof data.orders === "object") {
        orders = Object.keys(data.orders)
          .sort((a, b) =>
            a
              .toString()
              .localeCompare(b.toString(), undefined, { numeric: true, sensitivity: "base" }),
          )
          .map((key) => data.orders[key]);
      }
    }
    if (!orders.length && data && Array.isArray(data.order_summaries)) {
      orders = data.order_summaries;
    } else if (!orders.length && data && Array.isArray(data.orderSummaries)) {
      orders = data.orderSummaries;
    }

    return orders
      .map((order, index) => {
        const rawNumber =
          order.orderNumber ||
          order.order_number ||
          order.name ||
          order.number ||
          order.id ||
          order.reference ||
          `order-${index + 1}`;
        const orderNumber =
          rawNumber !== null && rawNumber !== undefined ? String(rawNumber) : `order-${index + 1}`;
        const normalisedOrderNumber = this.normaliseOrderNumber(orderNumber);
        const placedAt =
          order.placedAt ||
          order.placed_at ||
          order.created_at ||
          order.createdAt ||
          order.date ||
          "";
        const formattedDate = placedAt ? this.formatOrderLookupDate(placedAt) : "";
        const parts = [orderNumber];
        if (formattedDate) {
          parts.push(formattedDate);
        }
        return {
          id: `order-${index + 1}`,
          orderNumber,
          orderNumberNormalized: normalisedOrderNumber,
          label: parts.join(" • "),
          raw: order,
        };
      })
      .filter((order) => order.orderNumber);
  }

  getOrderLookupSelectedOrderId(data, products = []) {
    if (data && data.order) {
      const order = data.order;
      const directId =
        order.id || order.shopify_id || order.shopifyId || order.order_id || order.orderId;
      if (directId) {
        return String(directId);
      }
    }
    if (data && (data.order_id || data.orderId)) {
      return String(data.order_id || data.orderId);
    }
    if (Array.isArray(products)) {
      const productOrderId = products
        .map((product) => {
          const raw = product && product.raw ? product.raw : null;
          if (!raw || typeof raw !== "object") {
            return null;
          }
          return (
            raw.order_id ||
            raw.orderId ||
            (raw.order &&
              (raw.order.id ||
                raw.order.shopify_id ||
                raw.order.shopifyId ||
                raw.order.order_id ||
                raw.order.orderId))
          );
        })
        .find(Boolean);
      if (productOrderId) {
        return String(productOrderId);
      }
    }
    return "";
  }

  getOrderLookupCustomerName(data, products = []) {
    if (data && data.order) {
      const order = data.order;
      const directName = order.customer_name || order.customerName || order.customer || "";
      if (directName) {
        return String(directName).trim();
      }
    }
    if (Array.isArray(products)) {
      const productOrderCustomerName = products
        .map((product) => {
          const raw = product && product.raw ? product.raw : null;
          if (!raw || typeof raw !== "object") {
            return null;
          }
          return (
            raw.customer_name ||
            raw.customerName ||
            (raw.order && (raw.order.customer_name || raw.order.customerName))
          );
        })
        .find(Boolean);
      if (productOrderCustomerName) {
        return String(productOrderCustomerName).trim();
      }
    }
    return "";
  }

  getOrderLookupProductData(checkbox) {
    if (!checkbox || !checkbox.dataset.productData) {
      return null;
    }
    try {
      const decoded = decodeURIComponent(checkbox.dataset.productData);
      return decoded ? JSON.parse(decoded) : null;
    } catch (error) {
      return null;
    }
  }

  getOrderLookupProductTypes(productData) {
    if (!productData) {
      return [];
    }
    const rawType =
      productData.product_type ||
      productData.productType ||
      productData.product_types ||
      productData.productTypes ||
      "";
    if (Array.isArray(rawType)) {
      return rawType.map((value) => String(value));
    }
    if (typeof rawType === "string") {
      const trimmed = rawType.trim();
      if (!trimmed) {
        return [];
      }
      if (trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map((value) => String(value));
          }
          if (parsed) {
            return [String(parsed)];
          }
        } catch (error) {
          return [trimmed];
        }
      }
      return [trimmed];
    }
    if (rawType) {
      return [String(rawType)];
    }
    return [];
  }

  getOrderLookupSelectedTypeCategories(wrapper) {
    if (!wrapper) {
      return new Set();
    }
    const selectedCheckboxes = Array.from(
      wrapper.querySelectorAll('input[data-order-product-checkbox="true"]:checked'),
    );
    const typeCategories = new Set();

    selectedCheckboxes.forEach((checkbox) => {
      const productData = this.getOrderLookupProductData(checkbox);
      const types = this.getOrderLookupProductTypes(productData);
      let hasWetType = false;
      let hasDryType = false;
      types.forEach((type) => {
        const normalised = String(type).toLowerCase();
        if (normalised.includes("wet food")) {
          hasWetType = true;
        }
        if (normalised.includes("dry food")) {
          hasDryType = true;
        }
      });
      if (hasWetType && hasDryType) {
        typeCategories.add("wet");
        typeCategories.add("dry");
        return;
      }
      if (hasWetType) {
        typeCategories.add("wet");
        return;
      }
      if (hasDryType) {
        typeCategories.add("dry");
        return;
      }
      typeCategories.add("other");
    });

    return typeCategories;
  }

  parseOrderLookupPackSize(text) {
    const value = String(text || "");
    if (!value) {
      return 0;
    }
    // "12 x 395g" / "18x395g"
    let match = value.match(/(\d+)\s*[x×]\s*\d+(?:\.\d+)?\s*(?:g|kg|ml|l)\b/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    // "395g x 12"
    match = value.match(/\d+(?:\.\d+)?\s*(?:g|kg|ml|l)\s*[x×]\s*(\d+)\b/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    // "Tray x 36" / "Bag x 2" / "Pouch x 12"
    match = value.match(/(?:trays?|bags?|packs?|pouch(?:es)?)\s*[x\u00d7]\s*(\d+)\b/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    // "36 x Tray" / "2 x Bag"
    match = value.match(/(\d+)\s*[x\u00d7]\s*(?:trays?|bags?|packs?|pouch(?:es)?)\b/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    // "Pack of 12" / "Multipack of 18"
    match = value.match(/packs?\s+of\s+(\d+)/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    // "12 pack" / "12-pack" / "12 trays" / "12 bags"
    match = value.match(/(\d+)\s*-?\s*(?:packs?|trays?|bags?|pouches?|pouch)\b/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    return 0;
  }

  // Returns the number of trays/bags in a single unit of this line item, or 0 when
  // the pack size cannot be determined from the order lookup data.
  getOrderLookupPackSize(productData) {
    if (!productData || typeof productData !== "object") {
      return 0;
    }

    const packSizeType = productData.packsizetype || productData.packSizeType || null;
    const explicitCandidates = [
      productData.pack_size,
      productData.packSize,
      productData.pack_qty,
      productData.packQty,
      productData.units_per_pack,
      productData.unitsPerPack,
      packSizeType && (packSizeType.qty || packSizeType.quantity),
    ];
    const explicit = explicitCandidates
      .map((candidate) => parseInt(candidate, 10))
      .find((candidate) => Number.isFinite(candidate) && candidate > 0);
    if (explicit) {
      return explicit;
    }

    const textSources = [
      productData.variant_title,
      productData.variantTitle,
      productData.variant,
      productData.option,
      productData.title,
      productData.product_title,
      productData.productTitle,
      productData.name,
    ];
    for (let index = 0; index < textSources.length; index += 1) {
      const parsed = this.parseOrderLookupPackSize(textSources[index]);
      if (parsed > 0) {
        return parsed;
      }
    }

    return 0;
  }

  // Totals the trays/bags across the selected line items. Items whose pack size we cannot
  // read fall back to `fallbackPackSize`, so the cap stays generous rather than absent.
  getOrderLookupSelectedUnitTotal(wrapper, fallbackPackSize = 1) {
    if (!wrapper) {
      return 0;
    }
    const selectedCheckboxes = Array.from(
      wrapper.querySelectorAll('input[data-order-product-checkbox="true"]:checked'),
    );

    return selectedCheckboxes.reduce((total, checkbox) => {
      const productData = this.getOrderLookupProductData(checkbox);
      if (!productData) {
        return total;
      }
      const quantity = parseInt(productData.quantity || productData.qty, 10);
      const orderedPacks = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      const packSize = this.getOrderLookupPackSize(productData);
      return total + (packSize > 0 ? packSize : fallbackPackSize) * orderedPacks;
    }, 0);
  }

  getOrderLookupDamageUnitNoun(input, count) {
    const plural = (input && input.dataset.damageUnitNoun) || "items";
    return count === 1 ? plural.replace(/s$/, "") : plural;
  }

  bindOrderLookupDamageValidation(input) {
    if (!input || input.dataset.damageValidationBound === "true") {
      return;
    }
    input.dataset.damageValidationBound = "true";
    ["input", "change", "blur"].forEach((eventName) => {
      input.addEventListener(eventName, () => {
        this.validateOrderLookupDamageInput(input);
      });
    });
  }

  validateOrderLookupDamageInput(input) {
    if (!input || input.disabled || !input.isConnected) {
      return true;
    }

    const rawValue = (input.value || "").trim();
    if (rawValue === "") {
      // An empty value is handled by the standard required-field validation.
      input.classList.remove("error");
      this.removeErrorMessage(input);
      return true;
    }

    const value = Number(rawValue);
    const maxUnits = parseInt(input.dataset.damageMaxUnits || "", 10);
    const hasMax = Number.isFinite(maxUnits) && maxUnits > 0;
    const unitNoun = this.getOrderLookupDamageUnitNoun(input, maxUnits);
    let message = "";

    if (!Number.isInteger(value) || value < 1) {
      message = this.tPlain(
        "contact_form.errors.damage_quantity_min",
        "Please enter a whole number of 1 or more.",
      );
    } else if (hasMax && value > maxUnits) {
      message = this.tPlain(
        maxUnits === 1
          ? "contact_form.errors.damage_quantity_max_single"
          : "contact_form.errors.damage_quantity_max",
        maxUnits === 1
          ? "You've selected 1 [unit]. Please enter 1."
          : "You've selected [max] [unit]. Please enter a number between 1 and [max].",
      )
        .replace(/\[max\]/g, String(maxUnits))
        .replace(/\[unit\]/g, unitNoun);
    }

    if (message) {
      input.classList.add("error");
      this.addErrorMessage(input, message);
      return false;
    }

    input.classList.remove("error");
    this.removeErrorMessage(input);
    return true;
  }

  updateOrderLookupDamageField(wrapper) {
    if (!wrapper) {
      return;
    }
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.damageContainer || !elements.damageLabel || !elements.damageInput) {
      return;
    }
    const typeCategories = this.getOrderLookupSelectedTypeCategories(wrapper);

    let labelText = "";
    const selectedIssue =
      this.selectedStepOneOptionId ||
      (this.form ? this.form.dataset.selectedIssue : "") ||
      (this.form ? this.form.querySelector('input[name="current_issue"]:checked')?.id : "") ||
      "";
    const isDifferentIssue = selectedIssue === "food-looks-different";
    const isForeignItemIssue = selectedIssue === "foreign-item";
    if (typeCategories.size === 1 && typeCategories.has("wet")) {
      labelText = this.tPlain(
        isDifferentIssue
          ? "contact_form.order_lookup.damage_wet_label_foreign"
          : isForeignItemIssue
            ? "contact_form.order_lookup.damage_wet_label_affected"
            : "contact_form.order_lookup.damage_wet_label",
        isDifferentIssue
          ? "Number of Trays different"
          : isForeignItemIssue
            ? "Number of Trays affected"
            : "Number of Trays damaged",
      );
    } else if (typeCategories.size === 1 && typeCategories.has("dry")) {
      labelText = this.tPlain(
        isDifferentIssue
          ? "contact_form.order_lookup.damage_dry_label_foreign"
          : isForeignItemIssue
            ? "contact_form.order_lookup.damage_dry_label_affected"
            : "contact_form.order_lookup.damage_dry_label",
        isDifferentIssue
          ? "Number of Bags different"
          : isForeignItemIssue
            ? "Number of Bags affected"
            : "Number of Bags Damaged",
      );
    }

    if (!labelText) {
      this.resetOrderLookupDamageField(wrapper);
      return;
    }

    elements.damageLabel.textContent = labelText;
    elements.damageContainer.style.display = "";
    elements.damageContainer.removeAttribute("hidden");
    elements.damageInput.disabled = false;
    elements.damageInput.setAttribute("required", "");

    // One bag per unit is the right fallback for dry food; wet food falls back to the
    // largest tray pack we sell so an unreadable pack size never leaves the field uncapped.
    const isWet = typeCategories.has("wet");
    const maxUnits = this.getOrderLookupSelectedUnitTotal(
      wrapper,
      isWet ? UNKNOWN_WET_PACK_SIZE : 1,
    );
    if (maxUnits > 0) {
      elements.damageInput.setAttribute("max", String(maxUnits));
      elements.damageInput.dataset.damageMaxUnits = String(maxUnits);
    } else {
      elements.damageInput.removeAttribute("max");
      delete elements.damageInput.dataset.damageMaxUnits;
    }
    elements.damageInput.dataset.damageUnitNoun = isWet ? "trays" : "bags";
    this.bindOrderLookupDamageValidation(elements.damageInput);
    this.validateOrderLookupDamageInput(elements.damageInput);

    this.applyOrderAmendAdditionalInfoOverrides();
  }

  resetOrderLookupDamageField(wrapper) {
    if (!wrapper) {
      return;
    }
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.damageContainer || !elements.damageInput) {
      return;
    }
    elements.damageInput.value = "";
    elements.damageInput.disabled = true;
    elements.damageInput.removeAttribute("required");
    elements.damageInput.removeAttribute("max");
    delete elements.damageInput.dataset.damageMaxUnits;
    delete elements.damageInput.dataset.damageUnitNoun;
    elements.damageInput.classList.remove("error");
    this.removeErrorMessage(elements.damageInput);
    elements.damageContainer.style.display = "none";
    elements.damageContainer.setAttribute("hidden", "hidden");
    if (elements.damageLabel) {
      elements.damageLabel.textContent = "";
    }
    this.applyOrderAmendAdditionalInfoOverrides();
  }

  getOrderLookupCreatedAt(data) {
    const order = data && data.order ? data.order : null;
    if (!order) {
      return null;
    }
    const createdAt =
      order.created_at ||
      order.createdAt ||
      order.placed_at ||
      order.placedAt ||
      order.date ||
      order.created ||
      "";
    if (!createdAt) {
      return null;
    }
    const parsed = new Date(createdAt);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed;
  }

  isOrderLookupRecent(createdAt, thresholdHours = 48) {
    if (!createdAt) {
      return false;
    }
    const createdAtMs =
      createdAt instanceof Date ? createdAt.getTime() : new Date(createdAt).getTime();
    if (!createdAtMs || Number.isNaN(createdAtMs)) {
      return false;
    }
    const diffMs = Date.now() - createdAtMs;
    if (diffMs < 0) {
      return false;
    }
    const thresholdMs = thresholdHours * 60 * 60 * 1000;
    return diffMs < thresholdMs;
  }

  updateOrderLookupRecentNotice(wrapper, data) {
    if (!wrapper) {
      return;
    }
    const step = wrapper.closest(".step");
    const route = step && step.dataset.questionRoute ? step.dataset.questionRoute : "";
    if (route !== "order-timing") {
      return;
    }
    const createdAt = this.getOrderLookupCreatedAt(data);
    if (!this.isOrderLookupRecent(createdAt)) {
      this.hideOrderLookupStatus(wrapper);
      return;
    }
    const message = this.t(
      "contact_form.order_lookup.recent_order_notice",
      "This order was placed within the last 48 hours. Please allow at least 48 hours before making an enquiry.",
    );
    this.showOrderLookupStatus(wrapper, message, "info");
  }

  extractDhlTrackingInfo(data) {
    const order = data && data.order ? data.order : null;
    const fulfillments = order && Array.isArray(order.fulfillments) ? order.fulfillments : [];
    const trackingCandidates = [];
    fulfillments.forEach((fulfillment) => {
      const trackingEntries = [];
      if (Array.isArray(fulfillment.trackingInfo)) {
        trackingEntries.push(...fulfillment.trackingInfo);
      } else if (fulfillment.trackingInfo) {
        trackingEntries.push(fulfillment.trackingInfo);
      }
      if (Array.isArray(fulfillment.tracking_info)) {
        trackingEntries.push(...fulfillment.tracking_info);
      } else if (fulfillment.tracking_info) {
        trackingEntries.push(fulfillment.tracking_info);
      }
      if (Array.isArray(fulfillment.tracking_numbers)) {
        fulfillment.tracking_numbers.forEach((number) => {
          trackingEntries.push({
            number,
            company: fulfillment.tracking_company || fulfillment.company || "",
            url: fulfillment.tracking_url || "",
          });
        });
      }
      trackingEntries.forEach((info) => {
        if (!info || !info.number) {
          return;
        }
        const companyValue = (info.company || fulfillment.tracking_company || "")
          .toString()
          .toLowerCase();
        const urlValue = (info.url || info.tracking_url || "").toString().toLowerCase();
        const isEvri = companyValue.includes("evri") || urlValue.includes("evri");
        const isDhl = companyValue.includes("dhl") || urlValue.includes("dhl");
        trackingCandidates.push({
          trackingNumber: info.number,
          carrier: info.company || fulfillment.tracking_company || (isEvri ? "Evri" : "DHL"),
          trackingUrl: info.url || info.tracking_url || "",
          isEvri,
          isDhl: isDhl && !isEvri,
        });
      });
    });
    return trackingCandidates.length ? trackingCandidates[0] : null;
  }

  handleDhlTrackingLookup(data, wrapper) {
    if (!this.shouldFetchDhlTracking(wrapper)) {
      this.clearOrderLookupTracking(wrapper);
      return;
    }
    if (!wrapper) {
      return;
    }
    const trackingInfo = this.extractDhlTrackingInfo(data);
    if (!trackingInfo) {
      this.clearOrderLookupTracking(wrapper);
      return;
    }
    const { trackingNumber, carrier, trackingUrl, isEvri, isDhl } = trackingInfo;
    if (isEvri) {
      this.renderOrderLookupTrackingLink(wrapper, {
        trackingNumber,
        carrier: carrier || "Evri",
        trackingUrl,
      });
      return;
    }
    wrapper.dataset.dhlTrackingNumber = trackingNumber;
    this.showOrderLookupTrackingStatus(
      wrapper,
      this.t("contact_form.order_lookup.tracking_loading", "Fetching tracking updates..."),
      "loading",
    );
    if (!isDhl) {
      this.renderOrderLookupTrackingError(wrapper, trackingNumber, carrier || "DHL");
      return;
    }
    this.fetchDhlTrackingDetails(trackingNumber)
      .then((details) => {
        if (wrapper.dataset.dhlTrackingNumber !== trackingNumber) {
          return;
        }
        this.renderOrderLookupTracking(wrapper, { trackingNumber, carrier, details, trackingUrl });
      })
      .catch(() => {
        if (wrapper.dataset.dhlTrackingNumber !== trackingNumber) {
          return;
        }
        this.renderOrderLookupTrackingError(wrapper, trackingNumber, carrier);
      });
  }

  fetchDhlTrackingDetails(trackingNumber) {
    if (!trackingNumber || !this.dhlTrackingEndpoint || !this.dhlApiKey) {
      return Promise.reject(new Error("missing_tracking_details"));
    }
    if (!this.dhlTrackingCache) {
      this.dhlTrackingCache = new Map();
    }
    if (this.dhlTrackingCache.has(trackingNumber)) {
      return this.dhlTrackingCache.get(trackingNumber);
    }
    const url = `${this.dhlTrackingEndpoint}?trackingNumber=${encodeURIComponent(trackingNumber)}`;
    const request = fetch(url, {
      method: "GET",
      headers: {
        "DHL-API-Key": this.dhlApiKey,
      },
      redirect: "follow",
    }).then((response) => {
      if (!response.ok) {
        throw new Error("dhl_lookup_failed");
      }
      return response.json().catch(() => response.text().then((text) => ({ rawText: text })));
    });
    this.dhlTrackingCache.set(trackingNumber, request);
    request.catch(() => {
      this.dhlTrackingCache.delete(trackingNumber);
    });
    return request;
  }

  parseDhlTrackingDetails(data) {
    if (!data) {
      return null;
    }
    const shipmentsRaw = Array.isArray(data.shipments)
      ? data.shipments
      : data.shipment
        ? [data.shipment]
        : [];
    if (!shipmentsRaw.length) {
      return null;
    }
    const parsedShipments = shipmentsRaw.map((shipment) => {
      const status = shipment.status || {};
      const events = Array.isArray(shipment.events) ? shipment.events : [];
      const latestEvent = events.length ? events[0] : null;
      const description =
        (latestEvent &&
          (latestEvent.description || latestEvent.status || latestEvent.statusCode)) ||
        status.description ||
        "";
      const statusText =
        status.status || status.description || status.statusCode || description || "";
      const timestamp =
        status.timestamp ||
        status.date ||
        (latestEvent && (latestEvent.timestamp || latestEvent.dateTime || latestEvent.date)) ||
        "";
      const location = this.extractTrackingLocation(latestEvent);
      const eta =
        shipment.estimatedTimeOfDelivery ||
        shipment.estimatedDeliveryDate ||
        shipment.estimatedTimeOfArrival ||
        status.estimatedDeliveryDate ||
        status.estimatedTimeOfDelivery ||
        "";
      const statusLower = (statusText || "").toString().toLowerCase();
      const delivered = statusLower.includes("delivered") || statusLower === "delivered";
      const deliveredDate = delivered ? timestamp : "";
      return {
        status: statusText,
        description,
        timestamp,
        location,
        eta,
        delivered,
        deliveredDate,
      };
    });

    const deliveredShipment = parsedShipments.find((shipment) => shipment.delivered);
    const pendingShipments = parsedShipments.filter((shipment) => !shipment.delivered);
    const nextEta = pendingShipments
      .map((shipment) => shipment.eta)
      .filter(Boolean)
      .sort((a, b) => new Date(a) - new Date(b))[0];

    const primary = parsedShipments[0] || {};
    return {
      shipmentsCount: parsedShipments.length,
      status: primary.status || "",
      description: primary.description || "",
      timestamp: primary.timestamp || "",
      location: primary.location || "",
      deliveredDate: deliveredShipment
        ? deliveredShipment.deliveredDate || deliveredShipment.timestamp
        : "",
      estimatedDate: deliveredShipment ? "" : nextEta || primary.eta || "",
    };
  }

  extractTrackingLocation(event) {
    if (!event || !event.location) {
      return "";
    }
    const address = event.location.address || event.location;
    if (!address || typeof address !== "object") {
      return "";
    }
    const locality = address.addressLocality || address.city || "";
    const country = address.countryCode || address.country || "";
    return [locality, country].filter(Boolean).join(", ");
  }

  renderOrderLookupTracking(wrapper, payload) {
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.trackingContainer || !elements.trackingContent) {
      return;
    }
    const { trackingNumber, carrier, details, trackingUrl } = payload;
    const parsed = this.parseDhlTrackingDetails(details);
    const parts = [];
    const carrierLabel = this.decodeHtmlEntities(carrier || "DHL");

    const hasMultipleShipments = parsed && parsed.shipmentsCount && parsed.shipmentsCount > 1;
    if (parsed && parsed.deliveredDate) {
      parts.push(
        `<div class="order-lookup-tracking__status">${this.t(
          "contact_form.order_lookup.tracking_delivered_on",
          `Delivered on ${this.formatTrackingDate(parsed.deliveredDate)}`,
        )}</div>`,
      );
    } else if (parsed && parsed.estimatedDate) {
      const etaText = this.formatTrackingDate(parsed.estimatedDate);
      const key = hasMultipleShipments
        ? "contact_form.order_lookup.tracking_next_parcel"
        : "contact_form.order_lookup.tracking_eta";
      const fallback = hasMultipleShipments
        ? `Next parcel expected ${etaText}`
        : `Estimated delivery ${etaText}`;
      parts.push(`<div class="order-lookup-tracking__status">${this.t(key, fallback)}</div>`);
    } else if (parsed && parsed.status) {
      parts.push(
        `<div class="order-lookup-tracking__status">${this.decodeHtmlEntities(parsed.status)}</div>`,
      );
    }

    if (parsed && parsed.description && (!parsed.deliveredDate || !parsed.estimatedDate)) {
      parts.push(
        `<div class="order-lookup-tracking__description">${this.decodeHtmlEntities(parsed.description)}</div>`,
      );
    }

    if (parsed && (parsed.timestamp || parsed.location)) {
      const timestamp = parsed.timestamp ? this.formatTrackingTimestamp(parsed.timestamp) : "";
      const location = parsed.location || "";
      const metaParts = [timestamp, location].filter(Boolean);
      if (metaParts.length) {
        parts.push(`<div class="order-lookup-tracking__meta">${metaParts.join(" • ")}</div>`);
      }
    }

    if (hasMultipleShipments && parsed && parsed.estimatedDate && !parsed.deliveredDate) {
      parts.push(
        `<div class="order-lookup-tracking__meta">${this.t(
          "contact_form.order_lookup.tracking_multiple",
          `This order has multiple parcels. Next expected: ${this.formatTrackingDate(parsed.estimatedDate)}.`,
        )}</div>`,
      );
    }

    if (!parts.length) {
      parts.push(
        `<div class="order-lookup-tracking__status">${this.t(
          "contact_form.order_lookup.tracking_loaded",
          "Tracking details loaded.",
        )}</div>`,
      );
    }

    const headingContent = trackingUrl
      ? `<a href="${this.escapeAttribute(trackingUrl)}" target="_blank" rel="noopener">${carrierLabel} #${trackingNumber || ""}</a>`
      : `${carrierLabel} • ${trackingNumber || ""}`;
    if (carrier || trackingNumber) {
      parts.push(
        `<div class="order-lookup-tracking__number">For further details please visit: ${headingContent}</div>`,
      );
    }
    elements.trackingContent.innerHTML = parts.join("");
    elements.trackingContainer.style.display = "block";
  }

  renderOrderLookupTrackingError(wrapper, trackingNumber, carrier = "DHL") {
    this.showOrderLookupTrackingStatus(
      wrapper,
      this.t(
        "contact_form.order_lookup.tracking_error",
        `We couldn't load ${carrier} tracking for ${trackingNumber}.`,
      ),
      "error",
    );
  }

  renderOrderLookupTrackingLink(wrapper, payload) {
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.trackingContainer || !elements.trackingContent) {
      return;
    }
    const { trackingNumber, carrier, trackingUrl } = payload || {};
    const carrierLabel = this.decodeHtmlEntities(carrier || "Evri");
    const linkText = trackingUrl
      ? `<a href="${this.escapeAttribute(trackingUrl)}" target="_blank" rel="noopener">${carrierLabel} • ${trackingNumber || ""}</a>`
      : `${carrierLabel} • ${trackingNumber || ""}`;
    const statusText = trackingUrl
      ? this.t(
          "contact_form.order_lookup.tracking_external",
          "View tracking updates on the carrier website.",
        )
      : this.t(
          "contact_form.order_lookup.tracking_number_only",
          "Use this tracking number on the carrier website.",
        );
    elements.trackingContent.innerHTML = `
      <div class="order-lookup-tracking__number">${linkText}</div>
      <div class="order-lookup-tracking__status">${statusText}</div>
    `;
    elements.trackingContainer.style.display = "block";
  }

  markOrderTimingLookupComplete(wrapper, isSuccessful = false) {
    if (!wrapper) {
      return;
    }
    const step = wrapper.closest(".step");
    const route = step && step.dataset.questionRoute ? step.dataset.questionRoute : "";
    if (route !== "order-timing") {
      return;
    }
    const submitCount = parseInt(wrapper.dataset.orderLookupSubmitCount || "0", 10) || 0;
    this.orderTimingHelpButtonReady =
      this.orderTimingHelpButtonReady || isSuccessful || submitCount >= 2;
    if (isSuccessful || submitCount >= 2) {
      this.showOrderTimingHelpPrompt(step);
      return;
    }
    this.updateOrderTimingHelpButtonVisibility();
  }

  showOrderTimingHelpPrompt(step) {
    if (!step || step.dataset.questionRoute !== "order-timing") {
      return;
    }
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    this.orderTimingHelpButtonReady = true;
    this.showFinalStep(step, "order-timing");
    this.orderTimingFallbackActive = false;
    this.orderTimingHelpButtonReady = true;
    this.ensureOrderTimingHelpButton(finalStep);
    this.applyOrderTimingOptionalFields(finalStep, false);
    if (this.orderTimingHelpButton) {
      this.orderTimingHelpButton.style.display = "";
    }
    this.updateOrderTimingHelpButtonVisibility();
    this.updateOrderTimingSubmitVisibility();
  }

  markOrderAmendLookupComplete(wrapper, isSuccessful = false) {
    if (!wrapper) {
      return;
    }
    const step = wrapper.closest(".step");
    const route = step && step.dataset.questionRoute ? step.dataset.questionRoute : "";
    if (route !== "order-amend") {
      return;
    }
    const submitCount = parseInt(wrapper.dataset.orderLookupSubmitCount || "0", 10) || 0;
    if (isSuccessful) {
      this.orderAmendHelpButtonReady = false;
    } else {
      this.orderAmendHelpButtonReady = this.orderAmendHelpButtonReady || submitCount >= 2;
    }
    this.updateOrderTimingHelpButtonVisibility();
  }

  isOrderAmendLookupSuccess(wrapper) {
    if (!wrapper) {
      return false;
    }
    const step = wrapper.closest(".step");
    if (!step || step.dataset.questionRoute !== "order-amend") {
      return false;
    }
    const productsContainer = wrapper.querySelector("[data-order-lookup-products]");
    const hasProducts = !!(productsContainer && productsContainer.children.length);
    return wrapper.dataset.orderLookupSuccessful === "true" && hasProducts;
  }

  updateOrderAmendPostLookupVisibility(target = null) {
    let step = null;
    if (target) {
      if (target.classList && target.classList.contains("step")) {
        step = target;
      } else if (typeof target.closest === "function") {
        step = target.closest(".step");
      }
    }
    if (!step || step.dataset.questionRoute !== "order-amend") {
      step = this.findFirstStepForRoute("order-amend");
    }
    if (!step || step.dataset.questionRoute !== "order-amend") {
      return;
    }

    const lookupWrapper =
      (target && target.matches && target.matches("[data-order-lookup-wrapper]") ? target : null) ||
      step.querySelector("[data-order-lookup-wrapper]");
    if (!lookupWrapper) {
      return;
    }

    const shouldShowPostLookup = this.isOrderAmendLookupSuccess(lookupWrapper);
    const finalStep = this.getFinalStepElement();
    const shouldShowFallbackHelp =
      !shouldShowPostLookup &&
      this.orderAmendHelpButtonReady &&
      finalStep &&
      finalStep.parentElement === step;
    const children = Array.from(step.children);
    const lookupIndex = children.indexOf(lookupWrapper);
    if (lookupIndex === -1) {
      return;
    }

    children.slice(lookupIndex + 1).forEach((element) => {
      const shouldShowElement =
        shouldShowPostLookup || (shouldShowFallbackHelp && element === finalStep);
      if (shouldShowElement) {
        element.style.display = "";
        element.removeAttribute("hidden");
        this.toggleFieldRequiredState(element, true);
      } else {
        element.style.display = "none";
        element.setAttribute("hidden", "hidden");
        this.toggleFieldRequiredState(element, false);
      }
    });

    if (shouldShowPostLookup) {
      this.applyFinalStepCustomizations("order-amend");
      this.updateOrderTimingHelpButtonVisibility();
      return;
    }

    if (shouldShowFallbackHelp && finalStep) {
      this.ensureOrderTimingHelpButton(finalStep);
      this.applyOrderTimingOptionalFields(finalStep, false);
      this.updateOrderTimingHelpButtonVisibility();
      return;
    }

    this.orderTimingOptionalVisible = true;
    this.updateOrderTimingSubmitVisibility();
    this.updateOrderTimingHelpButtonVisibility();
  }

  showOrderLookupTrackingStatus(wrapper, message, type = "info") {
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.trackingContainer || !elements.trackingContent) {
      return;
    }
    const className = type === "error" ? "is-error" : type === "loading" ? "is-loading" : "is-info";
    elements.trackingContainer.style.display = "block";
    elements.trackingContent.innerHTML = `<div class="order-lookup-tracking__status ${className}">${this.decodeHtmlEntities(
      message,
    )}</div>`;
  }

  clearOrderLookupTracking(wrapper) {
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements) {
      return;
    }
    if (elements.trackingContent) {
      elements.trackingContent.innerHTML = "";
    }
    if (elements.trackingContainer) {
      elements.trackingContainer.style.display = "none";
    }
    if (wrapper && wrapper.dataset) {
      delete wrapper.dataset.dhlTrackingNumber;
    }
  }

  shouldFetchDhlTracking(wrapper) {
    if (!wrapper) {
      return false;
    }
    const step = wrapper.closest(".step");
    const route = step && step.dataset.questionRoute ? step.dataset.questionRoute : "";
    return route === "order-timing";
  }

  formatTrackingTimestamp(value) {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    try {
      return date.toLocaleString("en-GB", { hour12: false });
    } catch (error) {
      return date.toISOString();
    }
  }

  formatTrackingDate(value) {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    try {
      return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch (error) {
      return date.toISOString().slice(0, 10);
    }
  }

  scrollIntoViewWithOffset(element, offset = 200) {
    if (!element) {
      return;
    }
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
  }

  formatOrderLookupDate(value) {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
    const match = String(value).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`;
    }
    return String(value);
  }

  ensureOrderTimingHelpButton(finalStep) {
    if (!finalStep || this.orderTimingHelpButton) {
      return;
    }
    const button = document.createElement("button");
    button.type = "button";
    button.className = "button-2 help-button";
    button.textContent = "Need more help?";
    button.dataset.orderTimingHelpButton = "true";
    button.addEventListener("click", () => {
      if (this.finalStepSourceRoute === "order-timing") {
        this.orderTimingFallbackActive = true;
      }
      this.applyOrderTimingOptionalFields(finalStep, true);
      button.style.display = "none";
      // const firstHiddenGroup = finalStep.querySelector('[data-order-timing-optional="true"]');
      // if (firstHiddenGroup) {
      //   firstHiddenGroup.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // }
    });

    const firstGroup = finalStep.querySelector(".form-group");
    if (firstGroup && firstGroup.parentElement) {
      firstGroup.parentElement.insertBefore(button, firstGroup);
    } else {
      finalStep.insertBefore(button, finalStep.firstChild);
    }
    this.orderTimingHelpButton = button;
  }

  applyOrderTimingOptionalFields(finalStep, shouldShow) {
    if (!finalStep) {
      return;
    }
    this.orderTimingOptionalVisible = shouldShow;
    const finalConfig = this.getFinalStepConfig(this.finalStepSourceRoute || "");
    const optionalIds = [
      "customer-phone",
      "additional-information",
      "marketing-opt-in",
      "contact-preferences",
      "product-image-input",
    ];
    if (finalConfig.hideFileUpload) {
      const imageIndex = optionalIds.indexOf("product-image-input");
      if (imageIndex !== -1) {
        optionalIds.splice(imageIndex, 1);
      }
    }
    optionalIds.forEach((id) => {
      const field = finalStep.querySelector(`#${id}`);
      if (!field) {
        return;
      }
      const group = field.closest(".form-group");
      if (!group) {
        return;
      }
      group.dataset.orderTimingOptional = "true";
      if (shouldShow) {
        group.style.display = "";
        group.removeAttribute("hidden");
        this.toggleFieldRequiredState(group, true);
      } else {
        group.style.display = "none";
        group.setAttribute("hidden", "hidden");
        this.toggleFieldRequiredState(group, false);
      }
    });

    if (this.orderTimingHelpButton) {
      this.orderTimingHelpButton.style.display = shouldShow ? "none" : "";
    }
    this.updateOrderTimingSubmitVisibility();
  }

  updateOrderTimingHelpButtonVisibility() {
    if (!this.orderTimingHelpButton) {
      return;
    }
    const sourceRoute = this.finalStepSourceRoute || "";
    const isOrderTiming = sourceRoute === "order-timing";
    const isOrderAmend = sourceRoute === "order-amend";
    if (!isOrderTiming && !isOrderAmend) {
      this.orderTimingHelpButton.style.display = "none";
      return;
    }
    if (this.orderTimingOptionalVisible) {
      this.orderTimingHelpButton.style.display = "none";
      return;
    }
    if (isOrderTiming) {
      this.orderTimingHelpButton.style.display = this.orderTimingHelpButtonReady ? "" : "none";
      return;
    }
    const orderAmendStep = this.findFirstStepForRoute("order-amend");
    const orderAmendWrapper = orderAmendStep
      ? orderAmendStep.querySelector("[data-order-lookup-wrapper]")
      : null;
    if (this.isOrderAmendLookupSuccess(orderAmendWrapper)) {
      this.orderTimingHelpButton.style.display = "none";
      return;
    }
    this.orderTimingHelpButton.style.display = this.orderAmendHelpButtonReady ? "" : "none";
  }

  setOrderTimingSubmitVisibility(container, visible) {
    if (!container) {
      return;
    }
    if (visible) {
      if (container.dataset.orderTimingSubmitDisplay !== undefined) {
        container.style.display = container.dataset.orderTimingSubmitDisplay;
        delete container.dataset.orderTimingSubmitDisplay;
      } else {
        container.style.display = "";
      }
      container.removeAttribute("hidden");
      return;
    }
    if (container.dataset.orderTimingSubmitDisplay === undefined) {
      container.dataset.orderTimingSubmitDisplay = container.style.display || "";
    }
    container.style.display = "none";
    container.setAttribute("hidden", "hidden");
  }

  updateOrderTimingSubmitVisibility() {
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    const buttonContainer = finalStep.querySelector(".button-container");
    if (!buttonContainer) {
      return;
    }
    const isOrderTiming = this.finalStepSourceRoute === "order-timing";
    const isOrderAmend = this.finalStepSourceRoute === "order-amend";
    if (!isOrderTiming && !isOrderAmend) {
      this.setOrderTimingSubmitVisibility(buttonContainer, true);
      return;
    }
    if (isOrderTiming) {
      this.setOrderTimingSubmitVisibility(buttonContainer, this.orderTimingFallbackActive);
      return;
    }
    const orderAmendStep = this.findFirstStepForRoute("order-amend");
    const orderAmendWrapper = orderAmendStep
      ? orderAmendStep.querySelector("[data-order-lookup-wrapper]")
      : null;
    const hasSuccessfulLookup = this.isOrderAmendLookupSuccess(orderAmendWrapper);
    const shouldShow = hasSuccessfulLookup || this.orderTimingOptionalVisible;
    this.setOrderTimingSubmitVisibility(buttonContainer, shouldShow);
  }

  composeProductLabel(item, index) {
    if (!item) {
      return `Product ${index + 1}`;
    }
    const parts = [];
    const title = item.title || item.name || item.product_title;
    if (title) {
      parts.push(title);
    }
    const variant = item.variant_title || item.variant || item.option;
    if (variant) {
      parts.push(variant);
    }
    const quantity = item.quantity || item.qty;
    if (quantity) {
      parts.push(`x${quantity}`);
    }
    return parts.length ? parts.join(" — ") : `Product ${index + 1}`;
  }

  extractProductImage(item) {
    if (!item) {
      return null;
    }
    const candidates = [
      item.image,
      item.image_url,
      item.imageUrl,
      item.product_image,
      item.productImage,
      item.featured_image,
      item.featuredImage,
      item.thumbnail,
      item.thumbnail_url,
      item.thumbnailUrl,
      item.image_src,
    ];
    let src = "";
    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      if (!candidate) {
        continue;
      }
      if (typeof candidate === "string") {
        src = candidate;
        break;
      }
      if (typeof candidate === "object") {
        src =
          candidate.src || candidate.url || candidate.originalSrc || candidate.original_url || "";
        if (src) {
          break;
        }
      }
    }
    if (!src) {
      return null;
    }
    const alt =
      item.title || item.name || item.product_title || item.variant_title || "Product image";
    return { src, alt };
  }

  renderOrderLookupProducts(wrapper, fieldConfig, products) {
    const resultsContainer = wrapper.querySelector("[data-order-lookup-results]");
    const productsContainer = wrapper.querySelector("[data-order-lookup-products]");
    if (!resultsContainer || !productsContainer) {
      return;
    }
    productsContainer.innerHTML = "";
    const disableSelection = fieldConfig.disableProductSelection === true;
    products.forEach((product, index) => {
      const node = this.createOrderLookupProductOption(
        product,
        fieldConfig,
        index,
        disableSelection,
        wrapper,
      );
      productsContainer.appendChild(node);
    });
    resultsContainer.style.display = "block";
    wrapper.dataset.orderLookupSuccessful = "true";
    this.markOrderTimingLookupComplete(wrapper, true);
    this.updateOrderLookupDamageField(wrapper);

    const orderElements = this.getOrderLookupElements(wrapper);
    const hasOrderDropdown = wrapper.dataset.availableOrders === "true";
    if (orderElements) {
      if (hasOrderDropdown) {
        if (orderElements.ordersContainer) {
          orderElements.ordersContainer.style.display = "block";
        }
        if (orderElements.ordersList) {
          const selectEl = orderElements.ordersList.querySelector("select");
          if (selectEl) {
            const activeNumber = this.normaliseOrderNumber(
              wrapper.dataset.selectedOrderNumber ||
                (orderElements.numberInput ? orderElements.numberInput.value : ""),
            );
            if (activeNumber) {
              selectEl.value = activeNumber;
              if (selectEl.selectedIndex === -1) {
                selectEl.selectedIndex = 0;
              }
            } else {
              selectEl.value = "";
            }
          }
        }
        if (orderElements.numberInput) {
          this.toggleOrderNumberFieldVisibility(orderElements.numberInput, false);
        }
      } else {
        if (orderElements.ordersList) {
          orderElements.ordersList.innerHTML = "";
        }
        if (orderElements.ordersContainer) {
          orderElements.ordersContainer.style.display = "none";
        }
        if (orderElements.numberInput) {
          this.toggleOrderNumberFieldVisibility(orderElements.numberInput, true);
        }
        delete wrapper.dataset.availableOrders;
      }
    }
    this.updateOrderAmendPostLookupVisibility(wrapper);
    this.updateSubscriptionSupportFinalLookupVisibility();
    this.updateFoodIssuePostLookupVisibility();
    this.updateDogUnwellPostRetailerVisibility();
  }

  renderOrderLookupOrders(wrapper, fieldConfig, orders) {
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements || !elements.ordersContainer || !elements.ordersList) {
      return;
    }
    const numberInput = elements.numberInput;
    const selectedOrder =
      wrapper.dataset.selectedOrderNumber ||
      (numberInput ? this.normaliseOrderNumber(numberInput.value) : "");

    elements.ordersList.innerHTML = "";

    if (!orders.length) {
      elements.ordersContainer.style.display = "none";
      if (numberInput) {
        this.toggleOrderNumberFieldVisibility(numberInput, true);
      }
      wrapper.dataset.selectedOrderNumber = "";
      wrapper.dataset.selectedOrderId = "";
      wrapper.dataset.orderLookupSuccessful = "false";
      delete wrapper.dataset.availableOrders;
      this.updateOrderAmendPostLookupVisibility(wrapper);
      this.updateSubscriptionSupportFinalLookupVisibility();
      this.updateFoodIssuePostLookupVisibility();
      this.updateDogUnwellPostRetailerVisibility();
      return;
    }

    const select = document.createElement("select");
    select.className = "order-lookup-order-select form-control";
    select.required = true;

    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = this.tPlain(
      "contact_form.order_lookup.orders_placeholder",
      "Choose an order",
    );
    placeholderOption.selected = true;
    select.appendChild(placeholderOption);
    if (selectedOrder) {
      wrapper.dataset.selectedOrderNumber = selectedOrder;
    }

    orders.forEach((order) => {
      const normalisedOrderNumber =
        order.orderNumberNormalized || this.normaliseOrderNumber(order.orderNumber);
      const option = document.createElement("option");
      option.value = normalisedOrderNumber;
      option.textContent = order.label || order.orderNumber || order.id || "";
      option.dataset.originalOrderNumber = order.orderNumber || "";
      option.dataset.orderId = order.raw && order.raw.id ? String(order.raw.id) : "";
      if (normalisedOrderNumber && normalisedOrderNumber === selectedOrder) {
        option.selected = true;
        placeholderOption.selected = false;
        wrapper.dataset.selectedOrderId = option.dataset.orderId || "";
      }
      select.appendChild(option);
    });

    select.addEventListener("change", (event) => {
      const { value } = event.target;
      if (!value) {
        return;
      }
      const finalOrderNumber = value;
      const selectedOption = event.target.selectedOptions[0];
      wrapper.dataset.selectedOrderId = selectedOption ? selectedOption.dataset.orderId || "" : "";
      if (numberInput) {
        numberInput.value = finalOrderNumber;
      }
      wrapper.dataset.selectedOrderNumber = finalOrderNumber;
      this.clearOrderLookupResults(wrapper, { keepOrders: true });
      this.performOrderLookup(wrapper, {
        orderNumber: finalOrderNumber,
        sourceInput: numberInput,
        force: true,
      });
    });

    elements.ordersList.appendChild(select);
    elements.ordersContainer.style.display = "block";
    wrapper.dataset.orderLookupSuccessful = "false";
    wrapper.dataset.availableOrders = "true";
    if (numberInput) {
      this.toggleOrderNumberFieldVisibility(numberInput, false);
    }
    this.updateOrderAmendPostLookupVisibility(wrapper);
    this.updateSubscriptionSupportFinalLookupVisibility();
    this.updateFoodIssuePostLookupVisibility();
    this.updateDogUnwellPostRetailerVisibility();
  }

  clearOrderLookupOrders(wrapper) {
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements) {
      return;
    }
    if (elements.ordersList) {
      elements.ordersList.innerHTML = "";
    }
    if (elements.ordersContainer) {
      elements.ordersContainer.style.display = "none";
    }
    if (elements.numberInput) {
      this.toggleOrderNumberFieldVisibility(elements.numberInput, true);
    }
    wrapper.dataset.selectedOrderNumber = "";
    wrapper.dataset.selectedOrderId = "";
    wrapper.dataset.orderLookupSuccessful = "false";
    this.orderAmendHelpButtonReady = false;
    wrapper.dataset.selectedCustomerName = "";
    delete wrapper.dataset.availableOrders;
    this.orderTimingHelpButtonReady = false;
    this.updateOrderTimingHelpButtonVisibility();
    this.updateOrderAmendPostLookupVisibility(wrapper);
    this.updateSubscriptionSupportFinalLookupVisibility();
    this.updateFoodIssuePostLookupVisibility();
    this.updateDogUnwellPostRetailerVisibility();
  }

  createOrderLookupProductOption(
    product,
    fieldConfig,
    index,
    disableSelection = false,
    wrapper = null,
  ) {
    const baseName = fieldConfig.resultsName || `${fieldConfig.name || fieldConfig.id}-product`;
    const sanitisedId = (product.id || `product-${index + 1}`)
      .toString()
      .replace(/[^a-zA-Z0-9_-]/g, "-");
    const container = document.createElement(disableSelection ? "div" : "label");
    container.className = "order-lookup-product-option";

    if (!disableSelection) {
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.name = baseName;
      checkbox.value = product.id || sanitisedId;
      checkbox.dataset.orderProductCheckbox = "true";
      checkbox.dataset.productLabel = product.label;
      checkbox.dataset.productId = product.id || sanitisedId;
      checkbox.dataset.productKey = sanitisedId;
      if (product.raw) {
        try {
          checkbox.dataset.productData = encodeURIComponent(JSON.stringify(product.raw));
        } catch (error) {
          // Ignore serialization issues
        }
      }
      checkbox.addEventListener("change", () => {
        container.classList.toggle("is-selected", checkbox.checked);
        const targetWrapper = wrapper || checkbox.closest("[data-order-lookup-wrapper]");
        this.updateOrderLookupDamageField(targetWrapper);
        this.applyOrderAmendAdditionalInfoOverrides();
      });
      container.appendChild(checkbox);
    }

    const content = document.createElement("span");
    content.className = "order-lookup-product-option__content";

    if (product.image && product.image.src) {
      const imageWrapper = document.createElement("span");
      imageWrapper.className = "order-lookup-product-option__image";
      const image = document.createElement("img");
      image.src = product.image.src;
      image.alt = product.image.alt || product.label;
      image.loading = "lazy";
      imageWrapper.appendChild(image);
      content.appendChild(imageWrapper);
    }

    const details = document.createElement("span");
    details.className = "order-lookup-product-option__details";

    const raw = product.raw || {};
    const titleText = raw.product_title || raw.title || raw.name || product.label;
    const variantText = raw.variant_title || raw.variant || raw.option || "";
    const quantityValue = raw.quantity || raw.qty || "";

    if (titleText) {
      const titleSpan = document.createElement("span");
      titleSpan.className = "order-lookup-product-option__title";
      titleSpan.textContent = this.decodeHtmlEntities(titleText);
      details.appendChild(titleSpan);
    }

    if (variantText && variantText !== "Default Title") {
      const variantSpan = document.createElement("span");
      variantSpan.className = "order-lookup-product-option__variant";
      variantSpan.textContent = this.decodeHtmlEntities(variantText);
      details.appendChild(variantSpan);
    }

    if (quantityValue) {
      const quantitySpan = document.createElement("span");
      quantitySpan.className = "order-lookup-product-option__quantity";
      quantitySpan.textContent = `Qty: ${quantityValue}`;
      details.appendChild(quantitySpan);
    }

    content.appendChild(details);

    container.appendChild(content);
    return container;
  }

  clearOrderLookupResults(wrapper, options = {}) {
    const { keepOrders = false } = options;
    const elements = this.getOrderLookupElements(wrapper);
    if (!elements) {
      return;
    }
    if (elements.productsContainer) {
      elements.productsContainer.innerHTML = "";
    }
    if (elements.resultsContainer) {
      elements.resultsContainer.style.display = "none";
    }
    wrapper.dataset.orderLookupSuccessful = "false";
    this.orderAmendHelpButtonReady = false;
    this.clearOrderLookupTracking(wrapper);
    if (!keepOrders) {
      this.clearOrderLookupOrders(wrapper);
    }
    this.updateOrderLookupSubmitState(wrapper);
    this.resetOrderLookupDamageField(wrapper);
    this.updateOrderAmendPostLookupVisibility(wrapper);
    this.updateSubscriptionSupportFinalLookupVisibility();
    this.updateFoodIssuePostLookupVisibility();
    this.updateDogUnwellPostRetailerVisibility();
  }

  resetOrderLookupWrapper(wrapper) {
    if (!wrapper) {
      return;
    }
    const input = wrapper.querySelector('[data-order-lookup-input="true"]');
    if (input) {
      input.value = "";
      this.abortOrderLookupRequest(input);
    }
    const elements = this.getOrderLookupElements(wrapper);
    if (elements) {
      if (elements.emailInput) {
        elements.emailInput.value = "";
      }
      if (elements.postcodeInput) {
        elements.postcodeInput.value = "";
      }
    }
    const errorEl = wrapper.querySelector("[data-order-lookup-error]");
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.style.display = "none";
    }
    this.clearOrderLookupTracking(wrapper);
    this.hideOrderLookupStatus(wrapper);
    this.clearOrderLookupResults(wrapper);
    wrapper.dataset.lastLookupKey = "";
    wrapper.dataset.identityKey = "";
    wrapper.dataset.selectedOrderNumber = "";
    wrapper.dataset.selectedOrderId = "";
    wrapper.dataset.selectedCustomerName = "";
    wrapper.dataset.orderLookupSubmitCount = "0";
    wrapper.dataset.orderLookupSuccessful = "false";
    this.orderAmendHelpButtonReady = false;
    this.orderTimingHelpButtonReady = false;
    this.updateOrderTimingHelpButtonVisibility();
    this.updateOrderLookupSubmitState(wrapper);
    this.resetOrderLookupDamageField(wrapper);
    this.updateOrderAmendPostLookupVisibility(wrapper);
    this.updateSubscriptionSupportFinalLookupVisibility();
    this.updateFoodIssuePostLookupVisibility();
    this.updateDogUnwellPostRetailerVisibility();
  }

  resetAllOrderLookupWrappers() {
    this.querySelectorAll("[data-order-lookup-wrapper]").forEach((wrapper) =>
      this.resetOrderLookupWrapper(wrapper),
    );
  }

  showOrderLookupStatus(wrapper, message, type = "info") {
    const statusEl = wrapper.querySelector("[data-order-lookup-status]");
    if (!statusEl) {
      return;
    }
    const resolvedMessage = this.decodeHtmlEntities(message);
    statusEl.textContent = resolvedMessage;
    statusEl.style.display = message ? "block" : "none";
    statusEl.classList.remove("is-loading", "is-error", "is-info");
    if (type === "loading") {
      statusEl.classList.add("is-loading");
    } else if (type === "error") {
      statusEl.classList.add("is-error");
    } else {
      statusEl.classList.add("is-info");
    }
  }

  hideOrderLookupStatus(wrapper) {
    const statusEl = wrapper.querySelector("[data-order-lookup-status]");
    if (!statusEl) {
      return;
    }
    statusEl.textContent = "";
    statusEl.style.display = "none";
    statusEl.classList.remove("is-loading", "is-error", "is-info");
  }

  setupFileUpload() {
    if (!this.addImageBtn || !this.imageInput) {
      return;
    }

    this.addImageBtn.addEventListener("click", () => {
      if (this.productImages.length < this.MAX_IMAGES) {
        this.imageInput.value = "";
        this.imageInput.click();
      }
    });

    this.imageInput.addEventListener("change", (event) => {
      if (!event.target.files) {
        return;
      }
      this.hideImagesError();
      const files = Array.from(event.target.files);
      let valid = true;
      let errorMsg = "";

      files.forEach((file) => {
        if (this.productImages.length >= this.MAX_IMAGES) {
          return;
        }
        const validation = this.isValidImageFile(file);
        if (!validation.valid) {
          valid = false;
          errorMsg = validation.message;
        } else {
          const newTotal = this.getTotalImageSize([...this.productImages, file]);
          if (newTotal > this.TOTAL_MAX_IMAGE_BYTES) {
            valid = false;
            errorMsg = "Adding this image would exceed the total 20MB limit for all images.";
          } else {
            this.productImages.push(file);
          }
        }
      });

      if (!valid) {
        this.showImagesError(errorMsg);
      }

      this.renderProductImages();
      this.updateAddImageBtnState();
    });
  }

  setupFormSubmission() {
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();

      if (!this.validateDirectFinalSubmitFields() || !this.validateStep({ isSubmit: true })) {
        return;
      }

      const resolvedEmail = this.getResolvedCustomerEmail();
      const enquiryReason = this.getSelectedIssueRoute();
      const formData = this.buildFormData();
      const submitButton = this.form.querySelector('button[type="submit"]');
      if (!submitButton) {
        return;
      }

      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = "Submitting...";

      // DEBUG: Simulate async submission
      // Convert FormData to JSON for debugging
      const formDataObj = {};
      for (let [key, value] of formData.entries()) {
        if (formDataObj[key]) {
          // Handle multiple values for the same key
          if (Array.isArray(formDataObj[key])) {
            formDataObj[key].push(value);
          } else {
            formDataObj[key] = [formDataObj[key], value];
          }
        } else {
          formDataObj[key] = value;
        }
      }
      console.log("Form Data Submitted as JSON:", JSON.stringify(formDataObj, null, 2));
      this.submitForm(formData)
        .then(() => {
          const klaviyoClient = window.klaviyo;
          if (klaviyoClient && typeof klaviyoClient.track === "function") {
            if (resolvedEmail && typeof klaviyoClient.identify === "function") {
              klaviyoClient.identify({
                email: resolvedEmail,
                custom_forms: true,
              });
            }
            klaviyoClient.track("Customer Form Submitted");
          }

          document.dispatchEvent(
            new CustomEvent("contact_us", {
              detail: { type: enquiryReason || "contact" },
            }),
          );

          this.displaySuccessMessage();
          this.resetForm();
        })
        .catch((error) => {
          this.displayErrorMessage(error);
        })
        .finally(() => {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        });
    });
  }

  setupAmendSubscriptionShortcut() {
    if (!this.amendSubscriptionButton) {
      return;
    }
    this.amendSubscriptionButton.addEventListener("click", (event) => {
      event.preventDefault();
      this.form.reset();
      if (this.amendReasonSelector) {
        this.amendReasonSelector.value = "add-remove-products";
        this.handleAmendReasonState();
      }
    });
  }

  setupStartOverButton() {
    if (!this.startOverButton) {
      return;
    }
    this.updateStartOverVisibility();
    this.startOverButton.addEventListener("click", () => {
      this.handleStartOver();
    });
  }

  updateStartOverVisibility() {
    if (!this.startOverContainer) {
      return;
    }
    const shouldShow = Boolean(this.activeRoute);
    if (shouldShow) {
      this.startOverContainer.removeAttribute("hidden");
      this.startOverContainer.style.display = "";
    } else {
      this.startOverContainer.setAttribute("hidden", "hidden");
      this.startOverContainer.style.display = "none";
    }
  }

  getActiveStep() {
    return this.querySelector(".step.active:not(.inner-step)");
  }

  updateConditionalInnerSteps(selector) {
    if (!selector) {
      return;
    }
    const currentStep = selector.closest(".step");
    if (!currentStep) {
      return;
    }
    const selectedType = selector.value;
    const innerSteps = currentStep.querySelectorAll(".inner-step");
    innerSteps.forEach((innerStep) => {
      if (innerStep.dataset.alwaysVisible === "true") {
        return;
      }
      if (innerStep.dataset.preserveActive === "true") {
        return;
      }
      if (innerStep.getAttribute("data-inner-step") === selectedType) {
        innerStep.classList.add("active");
        this.toggleFieldRequiredState(innerStep, true);
      } else {
        innerStep.classList.remove("active");
        this.toggleFieldRequiredState(innerStep, false);
        innerStep.querySelectorAll("input, select, textarea").forEach((field) => {
          field.classList.remove("error");
          const errorMessage = field.nextElementSibling;
          if (errorMessage && errorMessage.classList.contains("validation-error")) {
            errorMessage.textContent = "";
          }
        });
      }
    });
  }

  handleAmendReasonState() {
    if (!this.amendReasonSelector) {
      return;
    }
    const step = this.amendReasonSelector.closest(".step");
    if (!step) {
      return;
    }
    const changeAddressStep = step.querySelector('.inner-step[data-inner-step="change-address"]');
    if (changeAddressStep && changeAddressStep.dataset.alwaysVisible !== "true") {
      changeAddressStep.classList.remove("active");
      this.toggleFieldRequiredState(changeAddressStep, false);
      changeAddressStep.style.display = "none";
      changeAddressStep.setAttribute("hidden", "hidden");
      const changeAddressField = changeAddressStep.querySelector("#change-address");
      if (changeAddressField) {
        changeAddressField.setAttribute("autocomplete", "street-address");
      }
      this.clearInputs(changeAddressStep);
    }

    this.applyOrderAmendAdditionalInfoOverrides();
  }

  getOrderAmendAdditionalInfoOverrides(value) {
    const copyMap = {
      "change-address": {
        labelKey: "contact_form.order_amend.route.order_amend.additional_info.change_address.label",
        labelFallback: "What is your new delivery address?",
        placeholderKey:
          "contact_form.order_amend.route.order_amend.additional_info.change_address.placeholder",
        placeholderFallback: "Please include the full delivery address, including postcode.",
      },
      "add-remove-products": {
        labelKey:
          "contact_form.order_amend.route.order_amend.additional_info.add_remove_products.label",
        labelFallback: "What changes would you like to make to your order?",
        placeholderKey:
          "contact_form.order_amend.route.order_amend.additional_info.add_remove_products.placeholder",
        placeholderFallback: "Let us know which items to add or remove and the quantities.",
      },
      "return-products": {
        labelKey: "contact_form.order_amend.route.order_amend.additional_info.returns.label",
        labelFallback: "What would you like to return?",
        placeholderKey:
          "contact_form.order_amend.route.order_amend.additional_info.returns.placeholder",
        placeholderFallback: "Tell us which items you want to return and why.",
      },
      "cancel-order": {
        labelKey: "contact_form.order_amend.route.order_amend.additional_info.cancel_order.label",
        labelFallback: "Why would you like to cancel your order?",
        placeholderKey:
          "contact_form.order_amend.route.order_amend.additional_info.cancel_order.placeholder",
        placeholderFallback: "Let us know the reason for cancellation.",
      },
    };

    const config = copyMap[value];
    if (!config) {
      return null;
    }
    return {
      label: this.t(config.labelKey, config.labelFallback),
      placeholder: this.t(config.placeholderKey, config.placeholderFallback),
    };
  }

  applyOrderAmendAdditionalInfoOverrides() {
    if (this.finalStepSourceRoute !== "order-amend") {
      return;
    }
    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return;
    }
    const labelEl = finalStep.querySelector('label[for="additional-information"]');
    const infoField = finalStep.querySelector("#additional-information");
    if (!labelEl || !infoField) {
      return;
    }

    if (this.orderAmendOriginalInfoLabel === null) {
      this.orderAmendOriginalInfoLabel = labelEl.textContent.trim();
    }
    if (this.orderAmendOriginalInfoPlaceholder === null) {
      this.orderAmendOriginalInfoPlaceholder = infoField.getAttribute("placeholder") || "";
    }

    const selectedValue = this.amendReasonSelector ? this.amendReasonSelector.value : "";
    const overrides = this.getOrderAmendAdditionalInfoOverrides(selectedValue);
    const isReturnProducts = selectedValue === "return-products";
    const returnState = this.getOrderAmendReturnProductsSelectionState(finalStep);
    const shouldHideAdditionalInfo =
      isReturnProducts && returnState.hasResults && returnState.hasSelection;
    if (returnState.wrapper && (!isReturnProducts || returnState.hasSelection)) {
      this.setOrderLookupError(returnState.wrapper, "");
    }
    this.toggleAdditionalInformationVisibility(!shouldHideAdditionalInfo);
    if (shouldHideAdditionalInfo) {
      infoField.value = "";
      infoField.removeAttribute("autocomplete");
    }

    if (!overrides) {
      if (this.orderAmendOriginalInfoLabel) {
        labelEl.textContent = this.orderAmendOriginalInfoLabel;
      }
      if (this.orderAmendOriginalInfoPlaceholder !== null) {
        infoField.setAttribute("placeholder", this.orderAmendOriginalInfoPlaceholder || "");
      }
      infoField.removeAttribute("autocomplete");
      return;
    }

    labelEl.textContent = this.decodeHtmlEntities(overrides.label);
    infoField.setAttribute("placeholder", this.decodeHtmlEntities(overrides.placeholder || ""));
    if (selectedValue === "change-address") {
      infoField.setAttribute("autocomplete", "street-address");
    } else {
      infoField.removeAttribute("autocomplete");
    }
  }

  handleSubscriptionReasonState(options = {}) {
    if (!this.subscriptionReasonSelector) {
      return;
    }

    const step = this.subscriptionReasonSelector.closest(".step");
    if (!step) {
      return;
    }

    const selectedValue = this.subscriptionReasonSelector.value;
    const { skipHelperUpdate = false } = options;

    const cancellationInnerStep = step.querySelector('.inner-step[data-inner-step="cancellation"]');
    if (cancellationInnerStep) {
      if (selectedValue === "cancel") {
        cancellationInnerStep.classList.add("active");
      } else if (cancellationInnerStep.dataset.alwaysVisible !== "true") {
        cancellationInnerStep.classList.remove("active");
        this.clearInputs(cancellationInnerStep);
      }
    }

    const nextButton = step.querySelector(".button-container [data-go-to-step]");
    const cancellationSelector = step.querySelector("#cancellation-reason");
    if (nextButton && cancellationSelector) {
      const finalStepNumber = this.getFinalStepNumber("subscription-support");
      if (["different-pack-size", "too-much-food"].includes(cancellationSelector.value)) {
        nextButton.setAttribute("data-go-to-step", "4");
      } else {
        nextButton.setAttribute("data-go-to-step", String(finalStepNumber));
      }
    }

    if (!skipHelperUpdate) {
      this.updateSubscriptionSupportHelper(step);
    }
  }

  refreshConditionalSelectors(step = null) {
    const selectors = [this.foodIssueTypeSelector, this.dogUnwellTypeSelector].filter(Boolean);
    selectors.forEach((selector) => {
      const selectorStep = selector.closest(".step");
      if (
        selectorStep &&
        (!step || selectorStep === step) &&
        selectorStep.classList.contains("active")
      ) {
        this.updateConditionalInnerSteps(selector);
      }
    });
    this.updateFoodIssueOrderLookupVisibility(step);
    this.updateFoodIssuePostLookupVisibility(step);
    this.updateDogUnwellOrderLookupVisibility(step);
    this.updateDogUnwellPostRetailerVisibility(step);
    this.handleAmendReasonState();
    this.handleSubscriptionReasonState();
    this.updateFoodIssueStepCopy();
    this.updateWrongMissingItemState(step);
    this.updateOrderAmendPostLookupVisibility(step);
    this.updateSubscriptionSupportFinalLookupVisibility();
  }

  updateFoodIssuePostLookupVisibility(step = null, defer = true) {
    let foodIssueStep = null;
    const finalStep = this.getFinalStepElement();
    if (this.form && this.form.dataset.foodIssuePostLookupWatcherAttached !== "true") {
      this.form.dataset.foodIssuePostLookupWatcherAttached = "true";
      const shouldHandleEvent = (target) =>
        !!(
          target &&
          target.matches &&
          target.matches(
            '#food-issue-retailer, [name="food-issue-retailer"], [data-retailer-select="true"], [data-order-lookup-input="true"], [data-order-lookup-email="true"], [data-order-lookup-postcode="true"]',
          )
        );
      this.form.addEventListener("change", (event) => {
        const target = event.target;
        if (!shouldHandleEvent(target)) {
          return;
        }
        this.updateFoodIssuePostLookupVisibility(null, false);
        requestAnimationFrame(() => this.updateFoodIssuePostLookupVisibility(null, false));
      });
      this.form.addEventListener("input", (event) => {
        const target = event.target;
        if (!shouldHandleEvent(target)) {
          return;
        }
        this.updateFoodIssuePostLookupVisibility(null, false);
      });
    }
    const isFoodIssueStep = (candidate) =>
      !!(
        candidate &&
        candidate.getAttribute &&
        (candidate.getAttribute("data-route") === "food-issue" ||
          candidate.getAttribute("data-question-route") === "food-issue")
      );
    const isFinalStepEl = (candidate) =>
      !!(
        candidate &&
        candidate.getAttribute &&
        candidate.getAttribute("data-final-step") === "true"
      );
    const isFoodIssueRouteStep = (candidate) =>
      isFoodIssueStep(candidate) && !isFinalStepEl(candidate);

    if (
      step &&
      (isFoodIssueRouteStep(step) ||
        (finalStep &&
          step === finalStep &&
          (this.finalStepSourceRoute === "food-issue" ||
            finalStep.getAttribute("data-source-route") === "food-issue")))
    ) {
      foodIssueStep = step;
    }

    if (!foodIssueStep && finalStep) {
      const isFinalVisible =
        finalStep.classList.contains("is-visible") || finalStep.classList.contains("active");
      const isFoodIssueFinal =
        this.finalStepSourceRoute === "food-issue" ||
        finalStep.getAttribute("data-source-route") === "food-issue";
      if (isFinalVisible && isFoodIssueFinal) {
        foodIssueStep = finalStep;
      }
    }

    if (!foodIssueStep && this.form) {
      const activeStep = this.form.querySelector(".step.active");
      if (activeStep && isFoodIssueRouteStep(activeStep)) {
        foodIssueStep = activeStep;
      }
    }

    if (!foodIssueStep) {
      const fallbackStep = this.findFirstStepForRoute("food-issue");
      if (fallbackStep) {
        foodIssueStep = fallbackStep;
      }
    }

    if (!foodIssueStep) {
      return;
    }

    let routeStep = null;
    if (isFoodIssueRouteStep(foodIssueStep)) {
      routeStep = foodIssueStep;
    } else if (finalStep && finalStep.parentNode && isFoodIssueRouteStep(finalStep.parentNode)) {
      routeStep = finalStep.parentNode;
    } else if (this.form) {
      const activeStep = this.form.querySelector(
        '.step.active[data-route="food-issue"]:not([data-final-step="true"]), .step.active[data-question-route="food-issue"]:not([data-final-step="true"])',
      );
      if (activeStep) {
        routeStep = activeStep;
      }
    }
    if (!routeStep) {
      routeStep = this.findFirstStepForRoute("food-issue") || foodIssueStep;
    }

    let retailerField =
      routeStep.querySelector("#food-retailer") ||
      routeStep.querySelector("#food-issue-retailer") ||
      routeStep.querySelector('[name="food-retailer"]') ||
      routeStep.querySelector('[name="food-issue-retailer"]') ||
      routeStep.querySelector("[data-food-issue-retailer]") ||
      routeStep.querySelector('[data-retailer-select="true"]');
    let retailerGroup = retailerField ? retailerField.closest(".form-group") : null;

    if (!retailerGroup) {
      retailerGroup = routeStep.querySelector(".form-group");
      retailerField = retailerGroup ? retailerGroup.querySelector("select, input, textarea") : null;
    }

    if (!retailerGroup) {
      return;
    }
    const lookupWrapper = routeStep.querySelector("[data-order-lookup-wrapper]");
    const lookupGroup = lookupWrapper ? lookupWrapper.closest(".form-group") : null;
    const lookupContainer =
      lookupGroup ||
      (lookupWrapper ? lookupWrapper.closest(".inner-step") : null) ||
      lookupWrapper ||
      null;
    const retailerValue = retailerField ? String(retailerField.value || "").trim() : "";
    const retailerValueNormalised = retailerValue.toLowerCase();
    const hasRetailerSelection = !!retailerValue;
    const retailerUsesLookup =
      retailerValueNormalised === "forthglade.com" || retailerValueNormalised === "forthglade";
    const retailerSubtitle = retailerGroup
      ? retailerGroup.querySelector("[data-retailer-subtitle]")
      : null;

    if (retailerSubtitle) {
      const shouldShowRetailerSubtitle = hasRetailerSelection && !retailerUsesLookup;
      if (shouldShowRetailerSubtitle) {
        retailerSubtitle.style.removeProperty("display");
        retailerSubtitle.removeAttribute("hidden");
      } else {
        retailerSubtitle.style.setProperty("display", "none", "important");
        retailerSubtitle.setAttribute("hidden", "hidden");
      }
    }

    const isVisible = (element) =>
      !!element && element.style.display !== "none" && !element.hasAttribute("hidden");
    const lookupVisible =
      !!lookupWrapper &&
      isVisible(lookupWrapper) &&
      (!lookupContainer || isVisible(lookupContainer));
    const productsContainer = lookupWrapper
      ? lookupWrapper.querySelector("[data-order-lookup-products]")
      : null;
    const hasProducts = !!(productsContainer && productsContainer.children.length);
    const hasLookupSuccess =
      lookupVisible &&
      lookupWrapper &&
      lookupWrapper.dataset.orderLookupSuccessful === "true" &&
      hasProducts;

    let mode = "revealed";
    if (!hasRetailerSelection) {
      mode = "retailer-only";
    } else if (retailerUsesLookup && !hasLookupSuccess) {
      mode = "lookup-only";
    }

    const toggleFinalStepVisibility = (shouldShow) => {
      if (
        !finalStep ||
        finalStep === routeStep ||
        finalStep.parentNode !== routeStep ||
        (this.finalStepSourceRoute !== "food-issue" &&
          finalStep.getAttribute("data-source-route") !== "food-issue")
      ) {
        return;
      }

      if (shouldShow) {
        if (finalStep.dataset.foodIssuePostLookupFinalHidden === "true") {
          if (finalStep.dataset.foodIssuePostLookupFinalOriginalDisplay !== undefined) {
            const originalDisplay = finalStep.dataset.foodIssuePostLookupFinalOriginalDisplay;
            if (originalDisplay) {
              finalStep.style.setProperty("display", originalDisplay);
            } else {
              finalStep.style.removeProperty("display");
            }
            delete finalStep.dataset.foodIssuePostLookupFinalOriginalDisplay;
          } else {
            finalStep.style.removeProperty("display");
          }
          if (finalStep.dataset.foodIssuePostLookupFinalOriginalHidden === "true") {
            finalStep.setAttribute("hidden", "hidden");
          } else {
            finalStep.removeAttribute("hidden");
          }
          delete finalStep.dataset.foodIssuePostLookupFinalOriginalHidden;
          this.toggleFieldRequiredState(finalStep, true);
          delete finalStep.dataset.foodIssuePostLookupFinalHidden;
        }
        return;
      }

      if (finalStep.dataset.foodIssuePostLookupFinalHidden !== "true") {
        finalStep.dataset.foodIssuePostLookupFinalOriginalDisplay = finalStep.style.display || "";
        finalStep.dataset.foodIssuePostLookupFinalOriginalHidden = finalStep.hasAttribute("hidden")
          ? "true"
          : "false";
      }
      finalStep.style.setProperty("display", "none", "important");
      finalStep.setAttribute("hidden", "hidden");
      this.toggleFieldRequiredState(finalStep, false);
      finalStep.dataset.foodIssuePostLookupFinalHidden = "true";
    };

    toggleFinalStepVisibility(mode === "revealed");

    const toggleManagedVisibility = (element, shouldShow) => {
      if (!element || !element.classList) {
        return;
      }
      if (shouldShow) {
        if (element.dataset.foodIssuePostLookupHidden === "true") {
          if (element.dataset.foodIssuePostLookupOriginalDisplay !== undefined) {
            const originalDisplay = element.dataset.foodIssuePostLookupOriginalDisplay;
            if (originalDisplay) {
              element.style.setProperty("display", originalDisplay);
            } else {
              element.style.removeProperty("display");
            }
            delete element.dataset.foodIssuePostLookupOriginalDisplay;
          } else {
            element.style.removeProperty("display");
          }
          if (element.dataset.foodIssuePostLookupOriginalHidden === "true") {
            element.setAttribute("hidden", "hidden");
          } else {
            element.removeAttribute("hidden");
          }
          delete element.dataset.foodIssuePostLookupOriginalHidden;
          if (element.style.display !== "none" && !element.hasAttribute("hidden")) {
            this.toggleFieldRequiredState(element, true);
          }
          delete element.dataset.foodIssuePostLookupHidden;
        }
        return;
      }

      if (element.dataset.foodIssuePostLookupHidden !== "true") {
        element.dataset.foodIssuePostLookupOriginalDisplay = element.style.display || "";
        element.dataset.foodIssuePostLookupOriginalHidden = element.hasAttribute("hidden")
          ? "true"
          : "false";
      }
      element.style.setProperty("display", "none", "important");
      element.setAttribute("hidden", "hidden");
      this.toggleFieldRequiredState(element, false);
      element.dataset.foodIssuePostLookupHidden = "true";
    };

    const getManagedElements = (container) => {
      if (!container) {
        return [];
      }
      const isManaged = (element) =>
        !!(
          element &&
          element.classList &&
          (element.classList.contains("form-group") ||
            element.classList.contains("inner-step") ||
            element.classList.contains("button-container"))
        );

      const directChildren = Array.from(container.children).filter(isManaged);
      if (directChildren.length) {
        return directChildren;
      }
      return Array.from(
        container.querySelectorAll(".form-group, .inner-step, .button-container"),
      ).filter(isManaged);
    };

    const managedElements = [];
    const seenElements = new Set();
    const addManaged = (elements) => {
      elements.forEach((element) => {
        if (!element || seenElements.has(element)) {
          return;
        }
        seenElements.add(element);
        managedElements.push(element);
      });
    };

    addManaged(getManagedElements(routeStep));
    if (
      finalStep &&
      finalStep !== routeStep &&
      finalStep.parentNode === routeStep &&
      this.finalStepSourceRoute === "food-issue"
    ) {
      addManaged(getManagedElements(finalStep));
    }

    managedElements.forEach((child) => {
      const isRetailerGroup = !!retailerGroup && child === retailerGroup;
      const isLookupGroup = !!lookupContainer && child === lookupContainer;

      let shouldShow = true;
      if (mode === "retailer-only") {
        shouldShow = isRetailerGroup;
      } else if (mode === "lookup-only") {
        shouldShow = isRetailerGroup || isLookupGroup;
      } else if (isLookupGroup && !lookupVisible) {
        shouldShow = false;
      }
      toggleManagedVisibility(child, shouldShow);
    });

    const toggleLookupOmittedFinalField = (fieldId, shouldShow) => {
      if (
        !finalStep ||
        finalStep === routeStep ||
        finalStep.parentNode !== routeStep ||
        (this.finalStepSourceRoute !== "food-issue" &&
          finalStep.getAttribute("data-source-route") !== "food-issue")
      ) {
        return;
      }

      const field = finalStep.querySelector(`#${fieldId}`);
      const group = field ? field.closest(".form-group") : null;
      if (!group) {
        return;
      }

      if (shouldShow) {
        if (group.dataset.foodIssueLookupOmitHidden === "true") {
          if (group.dataset.foodIssueLookupOmitOriginalDisplay !== undefined) {
            const originalDisplay = group.dataset.foodIssueLookupOmitOriginalDisplay;
            if (originalDisplay) {
              group.style.setProperty("display", originalDisplay);
            } else {
              group.style.removeProperty("display");
            }
            delete group.dataset.foodIssueLookupOmitOriginalDisplay;
          } else {
            group.style.removeProperty("display");
          }
          if (group.dataset.foodIssueLookupOmitOriginalHidden === "true") {
            group.setAttribute("hidden", "hidden");
          } else {
            group.removeAttribute("hidden");
          }
          delete group.dataset.foodIssueLookupOmitOriginalHidden;
          if (group.style.display !== "none" && !group.hasAttribute("hidden")) {
            this.toggleFieldRequiredState(group, true);
          }
          delete group.dataset.foodIssueLookupOmitHidden;
        }
        return;
      }

      if (group.dataset.foodIssueLookupOmitHidden !== "true") {
        group.dataset.foodIssueLookupOmitOriginalDisplay = group.style.display || "";
        group.dataset.foodIssueLookupOmitOriginalHidden = group.hasAttribute("hidden")
          ? "true"
          : "false";
      }
      group.style.setProperty("display", "none", "important");
      group.setAttribute("hidden", "hidden");
      this.toggleFieldRequiredState(group, false);
      group.dataset.foodIssueLookupOmitHidden = "true";
    };

    const shouldOmitLookupIdentityFields = retailerUsesLookup;
    ["customer-name", "customer-email"].forEach((fieldId) => {
      toggleLookupOmittedFinalField(fieldId, !shouldOmitLookupIdentityFields);
    });

    if (defer) {
      requestAnimationFrame(() => {
        this.updateFoodIssuePostLookupVisibility(routeStep, false);
      });
    }
  }

  updateDogUnwellPostRetailerVisibility(step = null, defer = true) {
    let dogUnwellStep = null;
    const finalStep = this.getFinalStepElement();
    if (this.form && this.form.dataset.dogUnwellPostRetailerWatcherAttached !== "true") {
      this.form.dataset.dogUnwellPostRetailerWatcherAttached = "true";
      this.form.addEventListener("change", (event) => {
        const target = event.target;
        if (
          !target ||
          !target.matches ||
          !target.matches('#dog-unwell-retailer, [name="dog-unwell-retailer"]')
        ) {
          return;
        }
        this.updateDogUnwellPostRetailerVisibility(null, false);
        requestAnimationFrame(() => this.updateDogUnwellPostRetailerVisibility(null, false));
      });
    }

    const isDogUnwellStep = (candidate) =>
      !!(
        candidate &&
        candidate.getAttribute &&
        (candidate.getAttribute("data-route") === "dog-unwell" ||
          candidate.getAttribute("data-question-route") === "dog-unwell")
      );
    const isFinalStepEl = (candidate) =>
      !!(
        candidate &&
        candidate.getAttribute &&
        candidate.getAttribute("data-final-step") === "true"
      );
    const isDogUnwellRouteStep = (candidate) =>
      isDogUnwellStep(candidate) && !isFinalStepEl(candidate);

    if (
      step &&
      (isDogUnwellRouteStep(step) ||
        (finalStep &&
          step === finalStep &&
          (this.finalStepSourceRoute === "dog-unwell" ||
            finalStep.getAttribute("data-source-route") === "dog-unwell")))
    ) {
      dogUnwellStep = step;
    }

    if (!dogUnwellStep && finalStep) {
      const isFinalVisible =
        finalStep.classList.contains("is-visible") || finalStep.classList.contains("active");
      const isDogUnwellFinal =
        this.finalStepSourceRoute === "dog-unwell" ||
        finalStep.getAttribute("data-source-route") === "dog-unwell";
      if (isFinalVisible && isDogUnwellFinal) {
        dogUnwellStep = finalStep;
      }
    }

    if (!dogUnwellStep && this.form) {
      const activeStep = this.form.querySelector(
        '.step.active[data-route="dog-unwell"]:not([data-final-step="true"]), .step.active[data-question-route="dog-unwell"]:not([data-final-step="true"])',
      );
      if (activeStep) {
        dogUnwellStep = activeStep;
      }
    }

    if (!dogUnwellStep) {
      const fallbackStep = this.findFirstStepForRoute("dog-unwell");
      if (fallbackStep) {
        dogUnwellStep = fallbackStep;
      }
    }

    if (!dogUnwellStep) {
      return;
    }

    let routeStep = null;
    if (isDogUnwellRouteStep(dogUnwellStep)) {
      routeStep = dogUnwellStep;
    } else if (finalStep && finalStep.parentNode && isDogUnwellRouteStep(finalStep.parentNode)) {
      routeStep = finalStep.parentNode;
    } else if (this.form) {
      const activeStep = this.form.querySelector(
        '.step.active[data-route="dog-unwell"]:not([data-final-step="true"]), .step.active[data-question-route="dog-unwell"]:not([data-final-step="true"])',
      );
      if (activeStep) {
        routeStep = activeStep;
      }
    }
    if (!routeStep) {
      routeStep = this.findFirstStepForRoute("dog-unwell") || dogUnwellStep;
    }

    let retailerField =
      routeStep.querySelector("#dog-unwell-retailer") ||
      routeStep.querySelector('[name="dog-unwell-retailer"]');
    let retailerGroup = retailerField ? retailerField.closest(".form-group") : null;
    if (!retailerGroup) {
      retailerGroup = routeStep.querySelector(".form-group");
      retailerField = retailerGroup ? retailerGroup.querySelector("select, input, textarea") : null;
    }
    if (!retailerGroup) {
      return;
    }

    const retailerValue = retailerField ? String(retailerField.value || "").trim() : "";
    const retailerValueNormalised = retailerValue.toLowerCase();
    const hasRetailerSelection = !!retailerValue;
    const retailerUsesLookup =
      retailerValueNormalised === "forthglade.com" || retailerValueNormalised === "forthglade";

    const lookupWrapper = routeStep.querySelector("[data-order-lookup-wrapper]");
    const lookupGroup = lookupWrapper ? lookupWrapper.closest(".form-group") : null;
    const lookupContainer =
      lookupGroup ||
      (lookupWrapper ? lookupWrapper.closest(".inner-step") : null) ||
      lookupWrapper ||
      null;
    const isVisible = (element) =>
      !!element && element.style.display !== "none" && !element.hasAttribute("hidden");
    const lookupVisible =
      !!lookupWrapper &&
      isVisible(lookupWrapper) &&
      (!lookupContainer || isVisible(lookupContainer));
    const productsContainer = lookupWrapper
      ? lookupWrapper.querySelector("[data-order-lookup-products]")
      : null;
    const hasProducts = !!(productsContainer && productsContainer.children.length);
    const hasLookupSuccess =
      lookupVisible &&
      lookupWrapper &&
      lookupWrapper.dataset.orderLookupSuccessful === "true" &&
      hasProducts;

    let mode = "revealed";
    if (!hasRetailerSelection) {
      mode = "retailer-only";
    } else if (retailerUsesLookup && !hasLookupSuccess) {
      mode = "lookup-only";
    }

    const toggleManagedVisibility = (element, shouldShow) => {
      if (!element || !element.classList) {
        return;
      }
      if (shouldShow) {
        if (element.dataset.dogUnwellPostRetailerHidden === "true") {
          if (element.dataset.dogUnwellPostRetailerOriginalDisplay !== undefined) {
            const originalDisplay = element.dataset.dogUnwellPostRetailerOriginalDisplay;
            if (originalDisplay) {
              element.style.setProperty("display", originalDisplay);
            } else {
              element.style.removeProperty("display");
            }
            delete element.dataset.dogUnwellPostRetailerOriginalDisplay;
          } else {
            element.style.removeProperty("display");
          }
          if (element.dataset.dogUnwellPostRetailerOriginalHidden === "true") {
            element.setAttribute("hidden", "hidden");
          } else {
            element.removeAttribute("hidden");
          }
          delete element.dataset.dogUnwellPostRetailerOriginalHidden;
          if (element.style.display !== "none" && !element.hasAttribute("hidden")) {
            this.toggleFieldRequiredState(element, true);
          }
          delete element.dataset.dogUnwellPostRetailerHidden;
        }
        return;
      }

      if (element.dataset.dogUnwellPostRetailerHidden !== "true") {
        element.dataset.dogUnwellPostRetailerOriginalDisplay = element.style.display || "";
        element.dataset.dogUnwellPostRetailerOriginalHidden = element.hasAttribute("hidden")
          ? "true"
          : "false";
      }
      element.style.setProperty("display", "none", "important");
      element.setAttribute("hidden", "hidden");
      this.toggleFieldRequiredState(element, false);
      element.dataset.dogUnwellPostRetailerHidden = "true";
    };

    const getManagedElements = (container) => {
      if (!container) {
        return [];
      }
      const isManaged = (element) =>
        !!(
          element &&
          element.classList &&
          (element.classList.contains("form-group") ||
            element.classList.contains("inner-step") ||
            element.classList.contains("button-container"))
        );

      const directChildren = Array.from(container.children).filter(isManaged);
      if (directChildren.length) {
        return directChildren;
      }
      return Array.from(
        container.querySelectorAll(".form-group, .inner-step, .button-container"),
      ).filter(isManaged);
    };

    const managedElements = [];
    const seenElements = new Set();
    const addManaged = (elements) => {
      elements.forEach((element) => {
        if (!element || seenElements.has(element)) {
          return;
        }
        seenElements.add(element);
        managedElements.push(element);
      });
    };

    addManaged(getManagedElements(routeStep));
    if (
      finalStep &&
      finalStep !== routeStep &&
      finalStep.parentNode === routeStep &&
      (this.finalStepSourceRoute === "dog-unwell" ||
        finalStep.getAttribute("data-source-route") === "dog-unwell")
    ) {
      addManaged(getManagedElements(finalStep));
    }

    managedElements.forEach((child) => {
      const isRetailerGroup = child === retailerGroup;
      const isLookupGroup = !!lookupContainer && child === lookupContainer;
      let shouldShow = true;
      if (mode === "retailer-only") {
        shouldShow = isRetailerGroup;
      } else if (mode === "lookup-only") {
        shouldShow = isRetailerGroup || isLookupGroup;
      } else if (isLookupGroup && !lookupVisible) {
        shouldShow = false;
      }
      toggleManagedVisibility(child, shouldShow);
    });

    if (
      finalStep &&
      finalStep !== routeStep &&
      finalStep.parentNode === routeStep &&
      (this.finalStepSourceRoute === "dog-unwell" ||
        finalStep.getAttribute("data-source-route") === "dog-unwell")
    ) {
      const shouldShowFinal = mode === "revealed";
      if (shouldShowFinal) {
        if (finalStep.dataset.dogUnwellPostRetailerFinalHidden === "true") {
          if (finalStep.dataset.dogUnwellPostRetailerFinalOriginalDisplay !== undefined) {
            const originalDisplay = finalStep.dataset.dogUnwellPostRetailerFinalOriginalDisplay;
            if (originalDisplay) {
              finalStep.style.setProperty("display", originalDisplay);
            } else {
              finalStep.style.removeProperty("display");
            }
            delete finalStep.dataset.dogUnwellPostRetailerFinalOriginalDisplay;
          } else {
            finalStep.style.removeProperty("display");
          }
          if (finalStep.dataset.dogUnwellPostRetailerFinalOriginalHidden === "true") {
            finalStep.setAttribute("hidden", "hidden");
          } else {
            finalStep.removeAttribute("hidden");
          }
          delete finalStep.dataset.dogUnwellPostRetailerFinalOriginalHidden;
          this.toggleFieldRequiredState(finalStep, true);
          delete finalStep.dataset.dogUnwellPostRetailerFinalHidden;
        }
      } else {
        if (finalStep.dataset.dogUnwellPostRetailerFinalHidden !== "true") {
          finalStep.dataset.dogUnwellPostRetailerFinalOriginalDisplay =
            finalStep.style.display || "";
          finalStep.dataset.dogUnwellPostRetailerFinalOriginalHidden = finalStep.hasAttribute(
            "hidden",
          )
            ? "true"
            : "false";
        }
        finalStep.style.setProperty("display", "none", "important");
        finalStep.setAttribute("hidden", "hidden");
        this.toggleFieldRequiredState(finalStep, false);
        finalStep.dataset.dogUnwellPostRetailerFinalHidden = "true";
      }
    }

    if (defer) {
      requestAnimationFrame(() => {
        this.updateDogUnwellPostRetailerVisibility(routeStep, false);
      });
    }
  }

  resetRetailerSubtitles() {
    if (!this.form) {
      return;
    }
    this.form.querySelectorAll("[data-retailer-subtitle]").forEach((subtitle) => {
      subtitle.style.setProperty("display", "none", "important");
      subtitle.setAttribute("hidden", "hidden");
    });
  }

  loadRoute(route) {
    this.clearAllFormFields();
    this.activeRoute = route || "";
    this.showFinalStep(null);
    this.resetRetailerSubtitles();
    this.updateFoodIssuePostLookupVisibility();
    this.updateDogUnwellPostRetailerVisibility();
    const targetStep = this.findFirstStepForRoute(route);

    if (this.stepOne) {
      this.markStepCompleted(this.stepOne);
    }

    this.mainSteps.forEach((step) => {
      if (step === this.stepOne) {
        step.classList.add("is-visible", "completed");
        step.classList.remove("active");
        return;
      }

      if (step === targetStep) {
        return;
      }

      step.classList.remove("active", "is-visible", "completed");
      this.clearStepFields(step);
    });

    const stepToShow = targetStep || this.stepOne;
    if (stepToShow) {
      this.setActiveStep(stepToShow);
      if (stepToShow === this.stepOne) {
        this.activeRoute = "";
        this.showFinalStep(null);
      } else {
        this.showFinalStep(stepToShow, this.activeRoute);
      }
      this.scrollToStep(stepToShow);
      this.refreshConditionalSelectors(stepToShow);
      this.prefillCustomerDetails();
      this.updateStartOverVisibility();
    }
  }

  loadStep(stepNumber, routeName, returnRoute) {
    const currentStep = this.getActiveStep();
    if (!currentStep) {
      return false;
    }

    const currentStepNum = parseInt(currentStep.getAttribute("data-step"), 10);
    const targetStepNum = parseInt(stepNumber, 10);
    const targetRoute = returnRoute || routeName || null;
    const routeForFinal = targetRoute || routeName || this.activeRoute || "";
    const targetStep = this.findStepElement(targetStepNum, routeName, returnRoute);
    const isFinalTarget = this.isFinalStepNumber(targetStepNum, routeForFinal);

    if (!isFinalTarget && !targetStep) {
      return false;
    }

    if (targetStepNum === 1) {
      this.clearAllFormFields();
    }

    const shouldBypassStepValidation = isFinalTarget && this.isDirectFinalStepRoute(routeForFinal);

    if (targetStepNum > currentStepNum && !shouldBypassStepValidation) {
      if (!this.validateStep()) {
        return false;
      }
      this.markStepCompleted(currentStep);
    } else if (targetStepNum < currentStepNum || returnRoute) {
      this.clearStepFields(currentStep);
      this.resetStepsAfter(targetStepNum, targetRoute);
    }

    if (isFinalTarget) {
      const routeStep = currentStep || this.findFirstStepForRoute(routeForFinal);
      if (!routeStep) {
        return false;
      }
      this.setActiveStep(routeStep);
      this.scrollToStep(routeStep);
      this.activeRoute = routeForFinal;
      this.showFinalStep(routeStep, routeForFinal);
      this.refreshConditionalSelectors(routeStep);
      this.prefillCustomerDetails();
      this.updateStartOverVisibility();
      return true;
    }

    if (!targetStep) {
      return false;
    }

    this.setActiveStep(targetStep);
    this.scrollToStep(targetStep);
    const targetStepRoute = targetStep.getAttribute("data-question-route") || routeForFinal;
    if (targetStep === this.stepOne) {
      this.activeRoute = "";
      this.showFinalStep(null);
    } else {
      this.activeRoute = targetStepRoute || this.activeRoute;
      const routeToShow = this.activeRoute || "";
      this.showFinalStep(targetStep, routeToShow);
    }
    this.refreshConditionalSelectors(targetStep);
    this.prefillCustomerDetails();
    this.updateStartOverVisibility();

    return true;
  }

  findFirstStepForRoute(route) {
    if (!route) {
      return null;
    }
    return (
      this.mainSteps.find((step) => step.getAttribute("data-question-route") === route) || null
    );
  }

  findStepElement(stepNumber, routeName, returnRoute) {
    const stepNum = parseInt(stepNumber, 10);
    if (stepNum === 1) {
      return this.stepOne;
    }

    const finalRoute = returnRoute || routeName || this.finalStepSourceRoute || "";
    const finalStep = this.getFinalStepElement();
    if (finalStep && this.isFinalStepNumber(stepNum, finalRoute)) {
      return finalStep;
    }

    if (returnRoute) {
      const routeMatch = this.mainSteps.find((step) => {
        const matchesStep = parseInt(step.getAttribute("data-step"), 10) === stepNum;
        const matchesRoute = step.getAttribute("data-question-route") === returnRoute;
        return matchesStep && matchesRoute;
      });
      if (routeMatch) {
        return routeMatch;
      }
    }

    if (routeName) {
      const routeMatch = this.mainSteps.find((step) => {
        const matchesStep = parseInt(step.getAttribute("data-step"), 10) === stepNum;
        const matchesRoute = step.getAttribute("data-question-route") === routeName;
        return matchesStep && matchesRoute;
      });
      if (routeMatch) {
        return routeMatch;
      }
    }

    return (
      this.mainSteps.find((step) => parseInt(step.getAttribute("data-step"), 10) === stepNum) ||
      null
    );
  }

  markStepCompleted(step) {
    if (!step || step.classList.contains("inner-step")) {
      return;
    }
    step.classList.add("is-visible", "completed");
    step.classList.remove("active");
  }

  resetStepsAfter(targetStepNum, targetRoute) {
    this.mainSteps.forEach((step) => {
      const stepNum = parseInt(step.getAttribute("data-step"), 10);
      const stepRoute = step.getAttribute("data-question-route");
      const matchesTargetRoute =
        stepNum === targetStepNum && (!targetRoute || !stepRoute || stepRoute === targetRoute);
      const shouldHide =
        stepNum > targetStepNum ||
        (stepNum === targetStepNum && targetRoute && stepRoute && stepRoute !== targetRoute);

      if (shouldHide) {
        step.classList.remove("active", "is-visible", "completed");
        this.clearStepFields(step);
      } else if (matchesTargetRoute) {
        step.classList.add("is-visible");
        step.classList.remove("completed");
      }
    });
  }

  setActiveStep(step) {
    if (!step) {
      return;
    }
    this.querySelectorAll(".step.active").forEach((activeStep) => {
      if (activeStep !== step) {
        activeStep.classList.remove("active");
      }
    });
    step.classList.add("active", "is-visible");
    step.classList.remove("completed");
  }

  scrollToStep(step) {
    if (!step) {
      return;
    }
    const stepTop = step.getBoundingClientRect().top + window.scrollY;
    const offset = Math.max(stepTop - 175, 0);
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  isElementVisibleForValidation(element) {
    if (!element) {
      return false;
    }

    let current = element;
    while (current && current !== this.form) {
      if (current.hasAttribute && current.hasAttribute("hidden")) {
        return false;
      }
      if (current.classList && current.classList.contains("is-hidden")) {
        return false;
      }
      if (typeof window !== "undefined" && typeof window.getComputedStyle === "function") {
        const styles = window.getComputedStyle(current);
        if (styles.display === "none" || styles.visibility === "hidden") {
          return false;
        }
      }
      current = current.parentElement;
    }

    return true;
  }

  validateFinalField(field) {
    if (!field) {
      return false;
    }

    field.classList.remove("error");
    const fieldType = field.type;
    const value = (field.value || "").trim();

    if (value === "") {
      field.classList.add("error");
      this.addErrorMessage(field, "This field is required");
      return false;
    }

    if (fieldType === "email" && !this.isValidEmail(value)) {
      field.classList.add("error");
      this.addErrorMessage(field, "Please enter a valid email address");
      return false;
    }

    if (field.id === "customer-phone" && !this.isValidPhone(value)) {
      field.classList.add("error");
      this.addErrorMessage(field, "Please enter a valid phone number");
      return false;
    }

    return true;
  }

  validateDirectFinalSubmitFields() {
    const route = this.getSelectedIssueRoute();
    if (!this.isDirectFinalStepRoute(route)) {
      return true;
    }

    const finalStep = this.getFinalStepElement();
    if (!finalStep) {
      return false;
    }

    const requiredFieldIds = [
      "customer-name",
      "customer-email",
      "customer-phone",
      "additional-information",
    ];
    const isValid = requiredFieldIds
      .map((fieldId) => this.validateFinalField(finalStep.querySelector(`#${fieldId}`)))
      .every(Boolean);

    if (!isValid) {
      const firstErrorField = requiredFieldIds
        .map((fieldId) => finalStep.querySelector(`#${fieldId}.error`))
        .find(Boolean);
      if (firstErrorField) {
        this.scrollIntoViewWithOffset(firstErrorField);
        firstErrorField.focus();
      }
    }

    return isValid;
  }

  validateStep(options = {}) {
    const currentStep = this.querySelector(".step.active");
    if (!currentStep) {
      return true;
    }
    const { isSubmit = false } = options;
    let isValid = true;

    const requiredFields = Array.from(currentStep.querySelectorAll("[required]")).filter(
      (field) => {
        if (field.disabled || !this.isElementVisibleForValidation(field)) {
          return false;
        }
        const innerStep = field.closest(".inner-step");
        if (
          innerStep &&
          !innerStep.classList.contains("active") &&
          innerStep.dataset.alwaysVisible !== "true"
        ) {
          return false;
        }
        return true;
      },
    );

    currentStep.querySelectorAll(".validation-error").forEach((error) => {
      if (error.id !== "product-images-error" && !error.hasAttribute("data-order-lookup-error")) {
        error.remove();
      }
    });

    requiredFields.forEach((field) => {
      field.classList.remove("error");
      const fieldType = field.type;
      const value = field.value.trim();
      let fieldValid = true;

      if (value === "") {
        fieldValid = false;
        this.addErrorMessage(field, "This field is required");
      } else if (fieldType === "email" && !this.isValidEmail(value)) {
        fieldValid = false;
        this.addErrorMessage(field, "Please enter a valid email address");
      } else if (field.id === "customer-phone" && !this.isValidPhone(value)) {
        fieldValid = false;
        this.addErrorMessage(field, "Please enter a valid phone number");
      } else if (field.name === "number-items-damaged") {
        fieldValid = this.validateOrderLookupDamageInput(field);
      } else if (fieldType === "number" && field.min === "100000000" && field.max === "999999999") {
        if (!/^[0-9]{9}$/.test(value)) {
          fieldValid = false;
          this.addErrorMessage(field, "Please enter your 9 digit order number");
        }
      }

      if (!fieldValid) {
        field.classList.add("error");
        isValid = false;
      }
    });

    if (!this.validateOrderLookupProductSelections(currentStep)) {
      isValid = false;
    }

    if (isSubmit && !this.validateOrderLookupForSubmit()) {
      isValid = false;
    }

    if (isSubmit && this.finalStepSourceRoute === "order-amend") {
      const selectedValue = this.amendReasonSelector ? this.amendReasonSelector.value : "";
      if (selectedValue === "return-products") {
        const returnState = this.getOrderAmendReturnProductsSelectionState(currentStep);
        const hasLookupResults = returnState.hasResults;
        const hasSelection = returnState.hasSelection;
        if (!hasLookupResults) {
          const shouldShowPrompt = this.areOrderLookupStatusAndResultsHidden(returnState.wrapper);
          this.setOrderLookupError(
            returnState.wrapper,
            shouldShowPrompt
              ? this.getOrderLookupPromptMessage()
              : this.getOrderLookupRequiredMessage(),
          );
          isValid = false;
        } else if (!hasSelection) {
          this.setOrderLookupError(
            returnState.wrapper,
            this.tPlain(
              "contact_form.errors.return_products_select",
              "Please select at least one product you would like to return.",
            ),
          );
          isValid = false;
        } else {
          this.setOrderLookupError(returnState.wrapper, "");
        }
      } else {
        const returnState = this.getOrderAmendReturnProductsSelectionState(currentStep);
        const returnProductsMessage = this.tPlain(
          "contact_form.errors.return_products_select",
          "Please select at least one product you would like to return.",
        );
        const wrapper = returnState.wrapper;
        const errorEl = wrapper ? wrapper.querySelector("[data-order-lookup-error]") : null;
        const currentMessage = errorEl ? (errorEl.textContent || "").trim() : "";
        if (currentMessage === returnProductsMessage) {
          this.setOrderLookupError(wrapper, "");
        }
      }
    }

    // if (currentStep.getAttribute('data-step') === '1') {
    //   const radios = currentStep.querySelectorAll('input[type="radio"][name="current_issue"]');
    //   const isAnyChecked = Array.from(radios).some((radio) => radio.checked);
    //   if (!isAnyChecked) {
    //     isValid = false;
    //     if (!currentStep.querySelector('.radio-group-error')) {
    //       const errorDiv = document.createElement('div');
    //       errorDiv.className = 'validation-error radio-group-error';
    //       errorDiv.textContent = 'Please select an option to continue';
    //       currentStep.insertBefore(errorDiv, currentStep.firstChild);
    //     }
    //     currentStep.querySelectorAll('.radio-wrapper').forEach((wrapper) => {
    //       wrapper.classList.add('error');
    //     });
    //   } else {
    //     const radioError = currentStep.querySelector('.radio-group-error');
    //     if (radioError) {
    //       radioError.remove();
    //     }
    //     currentStep.querySelectorAll('.radio-wrapper').forEach((wrapper) => {
    //       wrapper.classList.remove('error');
    //     });
    //   }
    // }

    if (this.isFinalStepElement(currentStep)) {
      this.hideImagesError();
      if (this.productImages.length > this.MAX_IMAGES) {
        this.showImagesError(`You can upload up to ${this.MAX_IMAGES} images.`);
        isValid = false;
      }
      const totalSize = this.getTotalImageSize(this.productImages);
      if (totalSize > this.TOTAL_MAX_IMAGE_BYTES) {
        this.showImagesError("Total image size must not exceed 20MB.");
        isValid = false;
      }
      for (let i = 0; i < this.productImages.length; i += 1) {
        const validation = this.isValidImageFile(this.productImages[i]);
        if (!validation.valid) {
          this.showImagesError(validation.message);
          isValid = false;
          break;
        }
      }
    }

    if (!isValid) {
      const firstErrorField = Array.from(currentStep.querySelectorAll(".error")).find((field) =>
        this.isElementVisibleForValidation(field),
      );
      const firstErrorMessage = Array.from(
        currentStep.querySelectorAll(".validation-error:not(#product-images-error)"),
      ).find(
        (error) =>
          this.isElementVisibleForValidation(error) &&
          Boolean(error.textContent && error.textContent.trim()),
      );
      const firstError = firstErrorField || firstErrorMessage;
      if (firstError) {
        this.scrollIntoViewWithOffset(firstError);
        if (
          firstError.tagName === "INPUT" ||
          firstError.tagName === "SELECT" ||
          firstError.tagName === "TEXTAREA"
        ) {
          firstError.focus();
        }
      }
    }

    return isValid;
  }

  addErrorMessage(field, message) {
    let errorSpan = field.nextElementSibling;
    if (!errorSpan || !errorSpan.classList.contains("validation-error")) {
      errorSpan = document.createElement("span");
      errorSpan.className = "validation-error";
      field.parentNode.insertBefore(errorSpan, field.nextSibling);
    }
    errorSpan.textContent = message;
  }

  removeErrorMessage(field) {
    if (!field) {
      return;
    }
    const errorSpan = field.nextElementSibling;
    if (errorSpan && errorSpan.classList.contains("validation-error")) {
      errorSpan.remove();
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone) {
    const trimmed = (phone || "").trim();
    const phoneRegex = /^(?:0\d{10}|\+44\d{10})$/;
    const repeatedMobileDigitsRegex = /^(?:07|\+447)(\d)\1{8}$/;
    return phoneRegex.test(trimmed) && !repeatedMobileDigitsRegex.test(trimmed);
  }

  hideImagesError() {
    if (this.imagesError) {
      this.imagesError.style.display = "none";
      this.imagesError.textContent = "";
    }
  }

  showImagesError(message) {
    if (this.imagesError) {
      this.imagesError.style.display = "block";
      this.imagesError.textContent = message;
    }
  }

  handleStartOver() {
    if (!this.form) {
      return;
    }

    this.form.reset();
    const innerContainer = this.form.querySelector(".inner");
    if (innerContainer) {
      innerContainer.style.display = "";
    }
    this.clearAllFormFields();
    this.clearProductImagesState();
    this.applySubscriptionSupportOtherFinalState(false);
    this.subscriptionSupportSelectedResources = null;
    this.activeRoute = "";
    this.selectedStepOneOptionId = "";
    if (this.form) {
      delete this.form.dataset.selectedIssue;
    }

    if (this.successMessage) {
      this.successMessage.style.display = "none";
      this.successMessage.textContent = "";
    }
    if (this.errorMessage) {
      this.errorMessage.style.display = "none";
      this.errorMessage.textContent = "";
    }

    if (this.routeSelectors && this.routeSelectors.length) {
      this.routeSelectors.forEach((selector) => {
        selector.checked = false;
        const wrapper = selector.closest(".radio-wrapper");
        if (wrapper) {
          wrapper.classList.remove("active", "completed", "error");
        }
      });
    }

    this.mainSteps.forEach((step) => {
      if (step !== this.stepOne) {
        step.classList.remove("active", "is-visible", "completed");
      }
    });

    if (this.stepOne) {
      this.setActiveStep(this.stepOne);
      this.stepOne.classList.remove("completed");
      this.stepOne.querySelectorAll(".validation-error").forEach((error) => {
        if (error.id !== "product-images-error") {
          error.textContent = "";
        }
      });
      this.stepOne.querySelectorAll("input, select, textarea").forEach((field) => {
        field.classList.remove("error");
      });
    }

    this.showFinalStep(null);
    this.applyFinalStepCustomizations("");
    this.refreshConditionalSelectors();
    this.scrollToStep(this.stepOne || this.form);
    this.updateStartOverVisibility();
  }

  clearAllFormFields() {
    const fieldsToReset = this.form.querySelectorAll(
      '.step:not([data-step="1"]) input, .step:not([data-step="1"]) select, .step:not([data-step="1"]) textarea',
    );
    fieldsToReset.forEach((field) => {
      if (field.type === "checkbox" || field.type === "radio") {
        field.checked = false;
      } else if (field.tagName === "SELECT") {
        field.selectedIndex = 0;
      } else {
        field.value = "";
      }
      field.classList.remove("error");
      const errorMessage = field.nextElementSibling;
      if (errorMessage && errorMessage.classList.contains("validation-error")) {
        errorMessage.textContent = "";
      }
    });

    this.querySelectorAll(".inner-step.active").forEach((innerStep) => {
      if (innerStep.dataset.alwaysVisible !== "true") {
        innerStep.classList.remove("active");
      }
    });

    this.resetAllOrderLookupWrappers();

    if (this.subscriptionReasonSelector) {
      const subscriptionStep = this.subscriptionReasonSelector.closest(".step");
      if (subscriptionStep) {
        this.removeSubscriptionSupportHelper(subscriptionStep);
      }
    }
    this.wrongMissingItemSelection = "";
  }

  clearStepFields(step) {
    step.querySelectorAll("input, select, textarea").forEach((field) => {
      switch (field.type) {
        case "checkbox":
        case "radio":
          field.checked = false;
          break;
        default:
          field.value = "";
          break;
      }
      if (field.tagName === "SELECT") {
        field.selectedIndex = 0;
      }
      field.classList.remove("error");
      const errorMessage = field.nextElementSibling;
      if (errorMessage && errorMessage.classList.contains("validation-error")) {
        errorMessage.textContent = "";
      }
    });
    this.syncOrderLookupProductOptionStates(step);

    step.querySelectorAll(".inner-step").forEach((innerStep) => {
      if (innerStep.dataset.alwaysVisible !== "true") {
        innerStep.classList.remove("active");
      }
    });

    step
      .querySelectorAll("[data-order-lookup-wrapper]")
      .forEach((wrapper) => this.resetOrderLookupWrapper(wrapper));

    if (this.subscriptionReasonSelector && step.contains(this.subscriptionReasonSelector)) {
      this.removeSubscriptionSupportHelper(step);
    }
  }

  clearInputs(container) {
    container.querySelectorAll("input, select, textarea").forEach((field) => {
      if (field.tagName === "SELECT") {
        field.selectedIndex = 0;
      } else {
        field.value = "";
      }
      field.classList.remove("error");
      const errorMessage = field.nextElementSibling;
      if (errorMessage && errorMessage.classList.contains("validation-error")) {
        errorMessage.textContent = "";
      }
    });

    container
      .querySelectorAll("[data-order-lookup-wrapper]")
      .forEach((wrapper) => this.resetOrderLookupWrapper(wrapper));
  }

  clearProductImagesState() {
    this.productImages = [];
    if (this.imageInput) {
      this.imageInput.value = "";
    }
    this.renderProductImages();
    this.updateAddImageBtnState();
    this.hideImagesError();
  }

  renderProductImages() {
    if (!this.productImagesContainer) {
      return;
    }
    this.productImagesContainer.innerHTML = "";
    this.productImages.forEach((file, idx) => {
      const imgDiv = document.createElement("div");
      imgDiv.className = "product-image-preview";

      const img = document.createElement("img");
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
      imgDiv.appendChild(img);

      const sizeDiv = document.createElement("div");
      sizeDiv.className = "product-image-size";
      sizeDiv.textContent = this.formatFileSize(file.size);
      imgDiv.appendChild(sizeDiv);

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.textContent = "×";
      removeBtn.title = "Remove image";
      removeBtn.className = "button button--remove-image";
      removeBtn.addEventListener("click", () => {
        this.productImages.splice(idx, 1);
        this.renderProductImages();
        this.updateAddImageBtnState();
      });
      imgDiv.appendChild(removeBtn);

      this.productImagesContainer.appendChild(imgDiv);
    });
  }

  updateAddImageBtnState() {
    if (!this.addImageBtn) {
      return;
    }
    if (this.productImages.length >= this.MAX_IMAGES) {
      this.addImageBtn.disabled = true;
      this.addImageBtn.classList.add("disabled");
      this.addImageBtn.textContent = `Max ${this.MAX_IMAGES} images`;
    } else {
      this.addImageBtn.disabled = false;
      this.addImageBtn.classList.remove("disabled");
      this.addImageBtn.textContent = "Add Image";
    }
  }

  isValidImageFile(file) {
    if (!file) {
      return { valid: true };
    }
    const acceptedImageTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/heic",
      "image/heif",
    ];
    const maxSizeInBytes = 8 * 1024 * 1024;
    if (!acceptedImageTypes.includes(file.type)) {
      return {
        valid: false,
        message: "Please upload a valid image file (JPEG, PNG, GIF, WEBP, HEIC, HEIF)",
      };
    }
    if (file.size > maxSizeInBytes) {
      return { valid: false, message: "Image file is too large. Maximum size is 8MB." };
    }
    return { valid: true };
  }

  getTotalImageSize(files) {
    return files.reduce((sum, file) => sum + file.size, 0);
  }

  formatFileSize(bytes) {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${bytes} bytes`;
  }

  buildFormData() {
    const formData = new FormData();
    const emailField = this.form.querySelector('input[name="customer-email"]');
    const orderEmailField =
      Array.from(this.form.querySelectorAll('input[name$="-email"]')).find(
        (field) => field.value.trim() !== "",
      ) || this.form.querySelector('input[name="order-number-email"]');
    const resolvedEmail =
      (orderEmailField && orderEmailField.value) || (emailField && emailField.value) || "";
    if (resolvedEmail) {
      formData.append("email", resolvedEmail);
    }
    formData.append("type", "contact_form");
    formData.append("page", "/contact-us");

    const customerId = window.QF && window.QF.customer ? window.QF.customer.id : "";

    const orderLookupWrappers = Array.from(
      this.form.querySelectorAll("[data-order-lookup-wrapper]"),
    );
    const orderIdFromSelect = orderLookupWrappers
      .map((wrapper) => {
        const select = wrapper.querySelector("[data-order-lookup-orders-list] select");
        const selectedOption = select ? select.selectedOptions[0] : null;
        const numberInput = wrapper.querySelector('[data-order-lookup-input="true"]');
        const selectedOrderNumber =
          (selectedOption &&
            (selectedOption.dataset.originalOrderNumber || selectedOption.value)) ||
          wrapper.dataset.selectedOrderNumber ||
          "";
        const normalisedOrderNumber = this.normaliseOrderNumber(selectedOrderNumber);
        if (numberInput && !numberInput.value.trim() && normalisedOrderNumber) {
          numberInput.value = normalisedOrderNumber;
        }
        return selectedOption && selectedOption.dataset.orderId
          ? selectedOption.dataset.orderId
          : null;
      })
      .find(Boolean);
    const orderIdFromLookup = orderLookupWrappers
      .map((wrapper) => (wrapper.dataset.selectedOrderId ? wrapper.dataset.selectedOrderId : null))
      .find(Boolean);

    const orderProductCheckboxes = Array.from(
      this.form.querySelectorAll('input[data-order-product-checkbox="true"]:checked'),
    );
    const productVariantIds = [];
    let orderIdFromProducts = null;
    let customerIdFromOrderLookup = null;
    let customerNameFromOrderLookup =
      orderLookupWrappers
        .map((wrapper) => (wrapper.dataset.selectedCustomerName || "").trim())
        .find(Boolean) || "";

    orderProductCheckboxes.forEach((checkbox) => {
      let productData = null;
      if (checkbox.dataset.productData) {
        try {
          const decoded = decodeURIComponent(checkbox.dataset.productData);
          productData = decoded ? JSON.parse(decoded) : null;
        } catch (error) {
          productData = null;
        }
      }
      const variantId =
        (productData && (productData.variant_id || productData.variantId)) ||
        (productData && productData.variant && productData.variant.id) ||
        (productData && productData.id) ||
        checkbox.dataset.productId ||
        checkbox.value;
      if (variantId) {
        productVariantIds.push(String(variantId));
      }
      if (productData && productData.order) {
        if (!orderIdFromProducts && productData.order.id) {
          orderIdFromProducts = String(productData.order.id);
        }
        if (!customerIdFromOrderLookup && productData.order.customer_id) {
          customerIdFromOrderLookup = String(productData.order.customer_id);
        }
        if (
          !customerNameFromOrderLookup &&
          (productData.order.customer_name || productData.order.customerName)
        ) {
          customerNameFromOrderLookup = String(
            productData.order.customer_name || productData.order.customerName,
          ).trim();
        }
      }
    });

    const orderId = orderIdFromSelect || orderIdFromLookup;
    if (orderId) {
      formData.append("order[shopify_id]", String(orderId));
    }
    const resolvedCustomerId = customerId || customerIdFromOrderLookup;
    if (resolvedCustomerId) {
      formData.append("customer[shopify_id]", String(resolvedCustomerId));
    }
    productVariantIds.forEach((variantId, index) => {
      formData.append(`product_variants[${index}][shopify_id]`, variantId);
    });

    formData.append("fields[username][type]", "hidden");
    formData.append("fields[username][value]", "null");
    let order = 1;
    formData.append("fields[username][order]", order);
    const orderProductGroups = {};

    const fields = Array.from(this.form.querySelectorAll("input, select, textarea"));
    fields.forEach((field) => {
      const name = field.name || field.id;
      if (!name) {
        return;
      }

      const innerStep = field.closest(".inner-step");
      if (
        innerStep &&
        !innerStep.classList.contains("active") &&
        innerStep.dataset.alwaysVisible !== "true"
      ) {
        return;
      }

      if (field.type === "file") {
        return;
      }

      const isOrderProduct = field.dataset.orderProductCheckbox === "true";

      if (field.type === "checkbox") {
        if (!field.checked) {
          return;
        }

        if (isOrderProduct) {
          const productLabel = field.dataset.productLabel || field.value || "";
          const productId = field.dataset.productId || field.value || "";
          const combinedValue =
            productLabel && productId
              ? `${productLabel} (${productId})`
              : productLabel || productId || field.value || "";
          if (!combinedValue) {
            return;
          }
          const productEntry = { value: combinedValue };

          if (field.dataset.productData) {
            try {
              const decoded = decodeURIComponent(field.dataset.productData);
              if (decoded) {
                try {
                  productEntry.data = JSON.parse(decoded);
                } catch (error) {
                  productEntry.data = decoded;
                }
              }
            } catch (error) {
              // Ignore serialization issues
            }
          }

          if (!orderProductGroups[name]) {
            orderProductGroups[name] = [];
          }
          orderProductGroups[name].push(productEntry);
        } else {
          order += 1;
          formData.append(`fields[${name}][type]`, "checkbox");
          formData.append(`fields[${name}][value]`, field.value || "true");
          formData.append(`fields[${name}][order]`, order);
        }
        return;
      }

      if (field.type === "radio" && !field.checked) {
        return;
      }

      let value;
      if (field.type === "radio") {
        if (name === "current_issue") {
          value = this.getCurrentIssueLabel(field);
        } else {
          const label = field.closest("label");
          if (label) {
            const labelTitle = label.querySelector(".label-title");
            const labelValue = labelTitle ? labelTitle.textContent.trim() : "";
            value = labelValue || field.value;
          } else {
            value = field.value;
          }
        }
      } else if (field.tagName === "SELECT") {
        const selectedOption = field.options[field.selectedIndex];
        value = selectedOption ? selectedOption.text.trim() : "";
      } else {
        value = field.value.trim();
      }

      if (!value || value === "Select an option") {
        return;
      }

      order += 1;
      const type = field.tagName === "SELECT" ? "select" : field.type;
      formData.append(`fields[${name}][type]`, type);
      formData.append(`fields[${name}][value]`, value);
      formData.append(`fields[${name}][order]`, order);
    });

    if (customerNameFromOrderLookup && !formData.has("fields[customer-name][value]")) {
      order += 1;
      formData.append("fields[customer-name][type]", "text");
      formData.append("fields[customer-name][value]", customerNameFromOrderLookup);
      formData.append("fields[customer-name][order]", order);
    }

    // Ensure the selected route is always sent, even if radio labels resolve to empty text.
    if (!formData.has("fields[current_issue][value]")) {
      const checkedIssue = this.form
        ? this.form.querySelector('input[name="current_issue"]:checked')
        : null;
      const fallbackIssueValue = this.getCurrentIssueLabel(checkedIssue);
      if (fallbackIssueValue) {
        order += 1;
        formData.append("fields[current_issue][type]", "radio");
        formData.append("fields[current_issue][value]", fallbackIssueValue);
        formData.append("fields[current_issue][order]", order);
      }
    }

    Object.entries(orderProductGroups).forEach(([groupName, entries]) => {
      if (!entries.length) {
        return;
      }

      const summaryValues = entries
        .map((entry) => (entry && entry.value ? entry.value : null))
        .filter(Boolean);

      if (summaryValues.length) {
        order += 1;
        formData.append(`fields[${groupName}][type]`, "text");
        formData.append(`fields[${groupName}][value]`, JSON.stringify(summaryValues));
        formData.append(`fields[${groupName}][order]`, order);
      }
    });

    this.productImages.forEach((file, index) => {
      formData.append(`fields[product-image-${index + 1}][type]`, "file");
      formData.append(`fields[product-image-${index + 1}][value]`, file);
      order += 1;
      formData.append(`fields[product-image-${index + 1}][order]`, order);
    });

    return formData;
  }

  submitForm(formData) {
    const endpoint = this.formSubmitEndpoint;
    return fetch(endpoint, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Access-Control-Allow-Origin": "https://forthglade-store.myshopify.com",
        "Access-Control-Allow-Credentials": "true",
      },
      body: formData,
    })
      .then((response) => {
        if (this.dataset.testMode && this.dataset.testMode === "true") {
          const error = new Error(this.tPlain("contact_form.errors.invalid_form"));
          throw error;
        }

        if (!response.ok) {
          if (response.status === 422) {
            return response.json().catch(() => {
              const error = new Error(this.tPlain("contact_form.errors.invalid_form"));
              error.status = response.status;
              throw error;
            });
          }
          let errorMessage;
          if (response.status >= 400 && response.status < 500) {
            errorMessage = this.tPlain("contact_form.errors.invalid_form");
          } else if (response.status >= 500) {
            errorMessage = this.tPlain("contact_form.errors.server");
          } else {
            errorMessage = this.tPlain("contact_form.errors.unexpected");
          }
          const error = new Error(errorMessage);
          error.status = response.status;
          throw error;
        }
        return response.json();
      })
      .then((data) => {
        if (data.error || data.success === false) {
          const invalidOrderIdError =
            data &&
            data.error &&
            (data.error["order.shopify_id"] || (data.error.order && data.error.order.shopify_id));
          const errorMessage = invalidOrderIdError
            ? this.tPlain("contact_form.errors.invalid_order_id")
            : data.message || this.tPlain("contact_form.errors.submission");
          const error = new Error(errorMessage);
          error.isInvalidOrderId = Boolean(invalidOrderIdError);
          error.apiError = true;
          throw error;
        }
        return data;
      })
      .catch((error) => {
        if (error.name === "TypeError" && error.message.includes("fetch")) {
          const networkError = new Error(this.tPlain("contact_form.errors.network"));
          throw networkError;
        }
        if (error.name === "SyntaxError") {
          const parseError = new Error(this.tPlain("contact_form.errors.parse_response"));
          throw parseError;
        }
        throw error;
      });
  }

  displaySuccessMessage() {
    if (!this.successMessage) {
      return;
    }
    if (this.errorMessage) {
      this.errorMessage.style.display = "none";
      this.errorMessage.textContent = "";
    }
    this.successMessage.style.display = "block";
    this.successMessage.innerHTML = `<h2>${this.t(this.schema.successMessage.headingKey)}</h2><p>${this.t(
      this.schema.successMessage.contentKey,
    )}</p>`;
    window.scrollTo({
      top: this.form.getBoundingClientRect().top + window.scrollY - 175,
      behavior: "smooth",
    });
  }

  displayErrorMessage(error) {
    const message = typeof error === "string" ? error : error.message;
    if (!this.errorMessage) {
      return;
    }
    if (this.successMessage) {
      this.successMessage.style.display = "none";
      this.successMessage.textContent = "";
    }
    this.errorMessage.style.display = "block";
    this.errorMessage.innerHTML = `<p>${message}</p>`;
    this.scrollIntoViewWithOffset(this.errorMessage);
    if (error && error.isInvalidOrderId) {
      this.hideFallbackShopifyForm();
    } else {
      this.showFallbackShopifyForm();
    }
  }

  getFallbackShopifyForm() {
    if (this.fallbackShopifyForm && document.contains(this.fallbackShopifyForm)) {
      return this.fallbackShopifyForm;
    }
    const fallbackForm = document.querySelector('[data-form-root="true"][data-forms-id]');
    this.fallbackShopifyForm = fallbackForm;
    return fallbackForm;
  }

  hideFallbackShopifyForm() {
    const fallbackForm = this.getFallbackShopifyForm();
    if (!fallbackForm) {
      return;
    }
    if (fallbackForm.dataset.originalDisplay === undefined) {
      fallbackForm.dataset.originalDisplay = fallbackForm.style.display || "";
    }
    fallbackForm.style.display = "none";
    fallbackForm.setAttribute("hidden", "hidden");
  }

  showFallbackShopifyForm() {
    const fallbackForm = this.getFallbackShopifyForm();
    if (!fallbackForm) {
      return;
    }
    if (fallbackForm.dataset.originalDisplay !== undefined) {
      fallbackForm.style.display = fallbackForm.dataset.originalDisplay;
      delete fallbackForm.dataset.originalDisplay;
    } else {
      fallbackForm.style.display = "";
    }
    fallbackForm.removeAttribute("hidden");
  }

  resetForm() {
    this.form.reset();
    const innerContainer = this.form.querySelector(".inner");
    if (innerContainer) {
      innerContainer.style.display = "none";
    }
    if (this.errorMessage) {
      this.errorMessage.style.display = "none";
      this.errorMessage.textContent = "";
    }
    this.resetAllOrderLookupWrappers();
    this.productImages = [];
    this.renderProductImages();
    this.updateAddImageBtnState();
    if (this.subscriptionReasonSelector) {
      const subscriptionStep = this.subscriptionReasonSelector.closest(".step");
      if (subscriptionStep) {
        this.removeSubscriptionSupportHelper(subscriptionStep);
      }
    }
    this.selectedStepOneOptionId = "";
    this.updateFoodIssueStepCopy();
    this.showFinalStep(null);
    this.applyFinalStepCustomizations("");
  }
}

if (!customElements.get("fg-contact-form")) {
  customElements.define("fg-contact-form", ContactForm);
}
